import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Stethoscope, Calendar, Users, LogOut, Search, X, Plus, AlertCircle, Baby, FileText, Syringe, TrendingUp, Heart, Edit2, Trash2, Clock, CheckCircle, ArrowLeft, ShieldAlert, BarChart3, Settings, Pill, Activity, ChevronDown, ChevronRight, Lock, Upload, UserCog, Sparkles, Eye, Check, RefreshCw, Zap } from 'lucide-react';
import { useSmartRefresh } from '../hooks/useSmartRefresh';
import DoctorBabyCard from './DoctorDashboard_BabyCard';
import ConfigurationTab from './ConfigurationTab';
import SettingsTab from './SettingsTab';
import SearchableSelect from './SearchableSelect';
import MedicationSearchInput from './MedicationSearchInput';
import MedicationIcon from './MedicationIcon';
import { 
  MedicalConditionsSection, 
  AllergiesSection, 
  VaccinationsSection, 
  GrowthTrackerSection, 
  BirthRecordSection, 
  MedicalSessionsSection 
} from './DoctorDashboard_Sections';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { DateInput } from './ui/date-input';
import axios from 'axios';
import { toast } from 'sonner';
import { ensureDrPrefix } from '../lib/utils';
import { calculateBMI, getBMIBadgeClasses } from '../utils/bmiCalculator';
import { formatDateToDDMMYYYY } from '../utils/dateFormatter';
import { assessGrowthRecord } from '../utils/whoGrowthData';
import AIClinicalAssistant from './AIClinicalAssistant';
import GlobalVoiceDictation from './GlobalVoiceDictation';
import BirthRecordVoiceRx from './BirthRecordVoiceRx';
import GrowthRecordVoiceRx from './GrowthRecordVoiceRx';

// Vitals Status Helper Functions
// Age-based vital signs ranges
const getAgeInMonths = (birthDate) => {
  if (!birthDate) return null;
  let birth;
  if (birthDate.includes('/')) {
    const [day, month, year] = birthDate.split('/');
    birth = new Date(year, month - 1, day);
  } else {
    birth = new Date(birthDate);
  }
  const today = new Date();
  const months = (today.getFullYear() - birth.getFullYear()) * 12 + (today.getMonth() - birth.getMonth());
  return months;
};

// Get normal ranges for Pulse Rate based on age (pediatric)
const getPulseRangeByAge = (ageInMonths) => {
  if (ageInMonths === null) return { min: 60, max: 100, range: '60-100' }; // Default adult range
  if (ageInMonths < 1) return { min: 100, max: 160, range: '100-160' }; // Newborn (0-1 month)
  if (ageInMonths < 12) return { min: 80, max: 140, range: '80-140' }; // Infant (1-12 months)
  if (ageInMonths < 36) return { min: 80, max: 130, range: '80-130' }; // Toddler (1-3 years)
  if (ageInMonths < 72) return { min: 80, max: 120, range: '80-120' }; // Preschool (3-6 years)
  if (ageInMonths < 144) return { min: 70, max: 110, range: '70-110' }; // School age (6-12 years)
  return { min: 60, max: 100, range: '60-100' }; // Adolescent/Adult (12+ years)
};

// Get normal ranges for Respiratory Rate based on age (pediatric)
const getRRRangeByAge = (ageInMonths) => {
  if (ageInMonths === null) return { min: 12, max: 20, range: '12-20' }; // Default adult range
  if (ageInMonths < 1) return { min: 30, max: 60, range: '30-60' }; // Newborn (0-1 month)
  if (ageInMonths < 12) return { min: 25, max: 50, range: '25-50' }; // Infant (1-12 months)
  if (ageInMonths < 36) return { min: 20, max: 40, range: '20-40' }; // Toddler (1-3 years)
  if (ageInMonths < 72) return { min: 20, max: 30, range: '20-30' }; // Preschool (3-6 years)
  if (ageInMonths < 144) return { min: 15, max: 25, range: '15-25' }; // School age (6-12 years)
  return { min: 12, max: 20, range: '12-20' }; // Adolescent/Adult (12+ years)
};

const getVitalStatus = (type, value, ageInMonths = null) => {
  if (!value || value === '') return null;
  
  const numValue = parseFloat(value);
  if (isNaN(numValue)) return null;
  
  switch(type) {
    case 'temp':
      if (numValue < 97) return { status: 'Low', color: 'bg-blue-100 text-blue-800' };
      if (numValue >= 97 && numValue <= 99) return { status: 'Normal', color: 'bg-green-100 text-green-800' };
      if (numValue > 99) return { status: 'High', color: 'bg-red-100 text-red-800' };
      break;
    case 'bp_systolic':
      if (numValue < 90) return { status: 'Low', color: 'bg-blue-100 text-blue-800' };
      if (numValue >= 90 && numValue <= 120) return { status: 'Normal', color: 'bg-green-100 text-green-800' };
      if (numValue > 120) return { status: 'High', color: 'bg-red-100 text-red-800' };
      break;
    case 'bp_diastolic':
      if (numValue < 60) return { status: 'Low', color: 'bg-blue-100 text-blue-800' };
      if (numValue >= 60 && numValue <= 80) return { status: 'Normal', color: 'bg-green-100 text-green-800' };
      if (numValue > 80) return { status: 'High', color: 'bg-red-100 text-red-800' };
      break;
    case 'pulse': {
      const pulseRange = getPulseRangeByAge(ageInMonths);
      if (numValue < pulseRange.min) return { status: 'Low', color: 'bg-blue-100 text-blue-800', range: pulseRange.range };
      if (numValue >= pulseRange.min && numValue <= pulseRange.max) return { status: 'Normal', color: 'bg-green-100 text-green-800', range: pulseRange.range };
      if (numValue > pulseRange.max) return { status: 'High', color: 'bg-red-100 text-red-800', range: pulseRange.range };
      break;
    }
    case 'respiratory_rate': {
      const rrRange = getRRRangeByAge(ageInMonths);
      if (numValue < rrRange.min) return { status: 'Low', color: 'bg-blue-100 text-blue-800', range: rrRange.range };
      if (numValue >= rrRange.min && numValue <= rrRange.max) return { status: 'Normal', color: 'bg-green-100 text-green-800', range: rrRange.range };
      if (numValue > rrRange.max) return { status: 'High', color: 'bg-red-100 text-red-800', range: rrRange.range };
      break;
    }
    case 'spo2':
      if (numValue < 95) return { status: 'Low', color: 'bg-orange-100 text-orange-800' };
      if (numValue >= 95) return { status: 'Normal', color: 'bg-green-100 text-green-800' };
      break;
    default:
      return null;
  }
  return null;
};

const DoctorDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [hospital, setHospital] = useState(null);
  const [activeTab, setActiveTab] = useState('appointments');
  
  const [loading, setLoading] = useState(true);
  
  // Helper function to open S3 or local files
  const openReportFile = async (fileUrl, fileName) => {
    if (!fileUrl) {
      console.error('No file URL provided');
      return;
    }
    
    console.log('Opening file:', fileUrl);
    
    // Check if it's an S3 URL
    if (fileUrl.includes('s3.') && fileUrl.includes('amazonaws.com')) {
      try {
        // Extract S3 key from URL
        const urlParts = fileUrl.split('.amazonaws.com/');
        if (urlParts.length > 1) {
          const s3Key = urlParts[1].split('?')[0]; // Remove any query params
          console.log('Fetching presigned URL for S3 key:', s3Key);
          // Get presigned URL from backend
          const response = await axios.get(`${API}/api/s3-file/${s3Key}`);
          if (response.data && response.data.url) {
            window.open(response.data.url, '_blank');
            return;
          }
        }
      } catch (error) {
        console.error('Error getting presigned URL:', error);
        toast.error('Failed to open file. Please try again.');
        return;
      }
    }
    
    // For local files or fallback, open directly
    let url;
    if (fileUrl.startsWith('http')) {
      url = fileUrl;
    } else if (fileUrl.startsWith('/api/')) {
      // File path includes /api/ prefix
      url = `${API}${fileUrl}`;
    } else if (fileUrl.startsWith('/uploads/')) {
      // File path without /api/ prefix - just /uploads/
      url = `${API}${fileUrl}`;
    } else if (fileUrl.startsWith('/')) {
      // Other absolute path
      url = `${API}${fileUrl}`;
    } else {
      // Relative path
      url = `${API}/${fileUrl}`;
    }
    
    console.log('Opening URL:', url);
    window.open(url, '_blank');
  };
  
  // Data states
  const [appointments, setAppointments] = useState([]);
  const [babies, setBabies] = useState([]);
  const [selectedDate, setSelectedDate] = useState(() => {
    // Get current date in local timezone
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const currentDate = `${year}-${month}-${day}`;
    console.log(`🕐 Doctor Dashboard initialized with date: ${currentDate} (${now.toLocaleString()})`);
    return currentDate;
  });
  
  // Baby profile states - Always start with appointments view
  const [selectedBaby, setSelectedBaby] = useState(null);
  const [showBabyProfile, setShowBabyProfile] = useState(false);
  const [babyDetails, setBabyDetails] = useState(null);
  const [profileDisplayMode, setProfileDisplayMode] = useState('full'); // 'full' or 'panel'
  const [activePanelSection, setActivePanelSection] = useState(null); // Track active section in panel mode
  
  // Helper function to convert time string to minutes for proper sorting
  const timeToMinutes = (timeStr) => {
    if (!timeStr) return 0;
    const [time, period] = timeStr.split(' ');
    let [hours, minutes] = time.split(':').map(Number);
    
    // Handle 24-hour format (no AM/PM)
    if (!period) {
      return hours * 60 + (minutes || 0);
    }
    
    // Handle 12-hour format with AM/PM
    if (period === 'PM' && hours !== 12) hours += 12;
    if (period === 'AM' && hours === 12) hours = 0;
    return hours * 60 + (minutes || 0);
  };
  
  // Quick lookup states
  const [showQuickLookup, setShowQuickLookup] = useState(false);
  const [lookupQuery, setLookupQuery] = useState({ id: '', name: '', dob: '' });
  
  // Quick Appointment states
  const [showQuickAppointment, setShowQuickAppointment] = useState(false);
  const [quickAppointmentForm, setQuickAppointmentForm] = useState({
    baby_id: '',
    baby_name: '',
    baby_dob: '',
    baby_gender: 'Male',
    baby_phone: '',
    baby_country_code: '+91',
    notes: '',
    appointment_type: 'new',
    category: 'Regular',
    category_ref: '',
    payment_method: 'Cash'
  });
  const [useExistingBabyQuick, setUseExistingBabyQuick] = useState(true);
  const [foundBabyQuick, setFoundBabyQuick] = useState(null);
  const [quickAppointmentSubmitting, setQuickAppointmentSubmitting] = useState(false);
  
  // Template states
  const [medicationTemplates, setMedicationTemplates] = useState([]);
  const [chiefComplaintTemplates, setChiefComplaintTemplates] = useState([]);
  const [testTemplates, setTestTemplates] = useState([]);
  const [diagnosisTemplates, setDiagnosisTemplates] = useState([]);
  const [additionalNotesTemplates, setAdditionalNotesTemplates] = useState([]);
  const [commonMedications, setCommonMedications] = useState([]);
  const [doctorSettings, setDoctorSettings] = useState({ show_common_medications: false, quick_appointment_enabled: false });
  
  // All Patients tab state
  const [allPatients, setAllPatients] = useState([]);
  const [allPatientsLoading, setAllPatientsLoading] = useState(false);
  const [allPatientsSearch, setAllPatientsSearch] = useState('');
  
  // Sub-chief complaints state
  const [selectedChiefComplaint, setSelectedChiefComplaint] = useState(null);
  const [showSubComplaints, setShowSubComplaints] = useState(false);
  const [selectedSubComplaint, setSelectedSubComplaint] = useState(null);
  
  // Diagnosis selection states (similar to chief complaints)
  const [selectedDiagnosis, setSelectedDiagnosis] = useState(null);
  const [showLevel2Diagnosis, setShowLevel2Diagnosis] = useState(false);
  const [selectedLevel2Diagnosis, setSelectedLevel2Diagnosis] = useState(null);
  const [showLevel3Diagnosis, setShowLevel3Diagnosis] = useState(false);
  const [selectedLevel3Diagnosis, setSelectedLevel3Diagnosis] = useState(null);
  const [showSubSubComplaints, setShowSubSubComplaints] = useState(false);
  const [selectedSubSubComplaint, setSelectedSubSubComplaint] = useState(null);
  const [showLevel4Complaints, setShowLevel4Complaints] = useState(false);
  const [selectedLevel4Complaint, setSelectedLevel4Complaint] = useState(null);
  const [showLevel5Complaints, setShowLevel5Complaints] = useState(false);
  const [selectedLevel5Complaint, setSelectedLevel5Complaint] = useState(null);
  const [showLevel6Complaints, setShowLevel6Complaints] = useState(false);
  
  // Dialog states for baby profile sections
  const [showMedicalConditionsDialog, setShowMedicalConditionsDialog] = useState(false);
  const [showAllergiesDialog, setShowAllergiesDialog] = useState(false);
  const [showVaccinationsDialog, setShowVaccinationsDialog] = useState(false);
  const [showGrowthDialog, setShowGrowthDialog] = useState(false);
  const [showBirthRecordDialog, setShowBirthRecordDialog] = useState(false);
  const [showMedicalSessionsDialog, setShowMedicalSessionsDialog] = useState(false);
  
  // Form states
  const [showAddCondition, setShowAddCondition] = useState(false);
  const [editingCondition, setEditingCondition] = useState(null);
  const [conditionForm, setConditionForm] = useState({
    condition_name: '',
    description: '',
    diagnosed_date: '',
    medication: '',
    status: 'Active'
  });
  
  const [showAddAllergy, setShowAddAllergy] = useState(false);
  const [editingAllergy, setEditingAllergy] = useState(null);
  const [allergyForm, setAllergyForm] = useState({
    allergen: '',
    reaction: '',
    severity: 'Mild',
    discovered_date: '',
    notes: ''
  });
  
  const [showAddVaccination, setShowAddVaccination] = useState(false);
  const [editingVaccination, setEditingVaccination] = useState(null);
  const [vaccinationForm, setVaccinationForm] = useState({
    vaccine_name: '',
    given_date: '',
    trade_name: '',
    batch_number: '',
    expiry_date: ''
  });
  
  // Separate form for adding NEW vaccinations (matches Baby App)
  const [showAddNewVaccination, setShowAddNewVaccination] = useState(false);
  const [addVaccinationForm, setAddVaccinationForm] = useState({
    vaccine_name: '',
    trade_name: '',
    given_date: ''
  });
  
  const [showAddGrowth, setShowAddGrowth] = useState(false);
  const [editingGrowth, setEditingGrowth] = useState(null);
  const [growthForm, setGrowthForm] = useState({
    date: '',
    weight: '',
    height: '',
    head_circumference: '',
    notes: ''
  });
  
  const [showEditBirthRecord, setShowEditBirthRecord] = useState(false);
  const [birthRecordForm, setBirthRecordForm] = useState({
    sex: '',
    mode_of_delivery: '',
    birth_weight: '',
    birth_length: '',
    birth_head_circumference: '',
    blood_group: '',
    mother_name: '',
    father_name: '',
    family_history: '',
    allergies: '',
    remarks: '',
    antenatal_history: '',
    birth_history: ''
  });
  
  // Temperature states
  const [showTempDialog, setShowTempDialog] = useState(false);
  const [showEditTempDialog, setShowEditTempDialog] = useState(false);
  const [editingTemperature, setEditingTemperature] = useState(null);
  const [tempFormData, setTempFormData] = useState({
    temperature: '',
    unit: 'C',
    method: 'Oral',
    date_recorded: '',
    time_recorded: new Date().toTimeString().slice(0, 5),
    notes: ''
  });
  
  // Blood Pressure states
  const [showBpDialog, setShowBpDialog] = useState(false);
  const [showEditBpDialog, setShowEditBpDialog] = useState(false);
  const [editingBloodPressure, setEditingBloodPressure] = useState(null);
  const [bpFormData, setBpFormData] = useState({
    systolic: '',
    diastolic: '',
    pulse: '',
    method: 'Manual',
    date_recorded: '',
    time_recorded: new Date().toTimeString().slice(0, 5),
    notes: ''
  });
  
  const [showAddSession, setShowAddSession] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [followUpFromSession, setFollowUpFromSession] = useState(null);
  const [currentAppointmentId, setCurrentAppointmentId] = useState(null); // Track appointment for MA vitals
  const [currentAppointment, setCurrentAppointment] = useState(null); // Track full appointment including follow-up info
  const [showPreviousSessionDialog, setShowPreviousSessionDialog] = useState(false);
  const [previousSessionData, setPreviousSessionData] = useState(null);
  const [showReuseMedicationsDialog, setShowReuseMedicationsDialog] = useState(false); // Reuse medications modal
  const [reusableMedications, setReusableMedications] = useState([]); // Medications from previous session
  const [selectedReuseMedications, setSelectedReuseMedications] = useState([]); // Selected medications to reuse
  const [showTestSelectionDialog, setShowTestSelectionDialog] = useState(false);
  const [selectedTests, setSelectedTests] = useState([]);
  const [testFieldTarget, setTestFieldTarget] = useState('tests'); // 'tests' or 'additional_tests'
  const [testSearchQuery, setTestSearchQuery] = useState('');
  const [expandedTestTypes, setExpandedTestTypes] = useState({});
  const [expandedCategories, setExpandedCategories] = useState({});
  const [sessionForm, setSessionForm] = useState({
    session_date: '',
    doctor_name: '',
    chief_complaints: '',
    diagnosis: '',
    assessment: '',
    medications: [{ name: '', timing: 'Before Food', duration: '', time_of_day: [], frequency: '', medication_type: '', treatment_for: '' }],
    temperature: '',
    notes: '',
    follow_up_of: '',
    next_followup: '', // Next follow-up date
    // Vitals
    pulse: '',
    respiratory_rate: '', // Respiratory Rate (breaths per minute)
    spo2: '',
    blood_pressure: '', // Combined BP field like "120/80"
    height: '',
    weight: '',
    // Tests
    tests: '',
    additional_tests: ''
  });
  const [selectedFiles, setSelectedFiles] = useState([]);
  
  // File upload state
  const [uploadedReports, setUploadedReports] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [reanalyzingReports, setReanalyzingReports] = useState({});
  const [analyzingReports, setAnalyzingReports] = useState({}); // Track which reports are being analyzed
  
  const [submitting, setSubmitting] = useState(false);
  
  // AI Clinical Assistant state
  const [showAIClinicalAssistant, setShowAIClinicalAssistant] = useState(false);
  
  // Differential Diagnosis state
  const [differentialDiagnosis, setDifferentialDiagnosis] = useState(null);
  const [isGeneratingDifferential, setIsGeneratingDifferential] = useState(false);
  
  // SOAP Notes Dialog state
  const [showSOAPDialog, setShowSOAPDialog] = useState(false);
  const [viewingSOAPNotes, setViewingSOAPNotes] = useState(null);
  
  // Previous Sessions Panel state (for session form left panel)
  const [previousDoctorSessions, setPreviousDoctorSessions] = useState([]);
  const [lastVisitInfo, setLastVisitInfo] = useState(null);
  const [selectedPreviousSession, setSelectedPreviousSession] = useState(null);
  
  // AI Features Settings state (controlled by Central Admin)
  // Default to false - features hidden until settings are loaded from API
  const [aiFeatureSettings, setAiFeatureSettings] = useState({
    ai_clinical_assistant_enabled: false,
    differential_diagnosis_enabled: false,
    voice_dictation_enabled: false
  });
  const [aiSettingsLoaded, setAiSettingsLoaded] = useState(false);
  const [hospitalAiFeaturesEnabled, setHospitalAiFeaturesEnabled] = useState(true);
  
  // BMI calculation state
  const [calculatedBMI, setCalculatedBMI] = useState(null);
  
  // Medication timings array
  const medicationTimings = [
    'Before Food',
    'After Food'
  ];

  // Medication handlers
  const handleAddMedication = () => {
    setSessionForm(prev => ({
      ...prev,
      medications: [...prev.medications, { name: '', timing: 'Before Food', duration: '', time_of_day: [], frequency: '', medication_type: '', treatment_for: '' }]
    }));
  };

  const handleRemoveMedication = (index) => {
    setSessionForm(prev => ({
      ...prev,
      medications: prev.medications.filter((_, i) => i !== index)
    }));
  };

  // Helper function to detect medication type from name
  const detectMedicationType = (medicationName) => {
    if (!medicationName) return '';
    const nameLower = medicationName.toLowerCase();
    
    // Check for medication types in order of specificity
    if (nameLower.includes('injection') || nameLower.includes('inj.') || nameLower.includes('inj ')) {
      return 'injection';
    }
    if (nameLower.includes('syrup') || nameLower.includes('suspension') || nameLower.includes('liquid')) {
      return 'syrup';
    }
    if (nameLower.includes('capsule') || nameLower.includes('cap.') || nameLower.includes('cap ')) {
      return 'capsule';
    }
    if (nameLower.includes('tablet') || nameLower.includes('tab.') || nameLower.includes('tab ')) {
      return 'tablet';
    }
    if (nameLower.includes('pill')) {
      return 'pill';
    }
    
    return ''; // Return empty string if no type detected
  };
  
  // Refs to store timeout IDs for debounced medication name updates
  const medicationDebounceRefs = useRef({});

  const handleMedicationChange = (index, field, value) => {
    // For medication name field, update immediately but debounce type detection
    if (field === 'name') {
      // Update the name immediately for responsive typing
      setSessionForm(prev => ({
        ...prev,
        medications: prev.medications.map((med, i) => 
          i === index ? { ...med, name: value } : med
        )
      }));
      
      // Clear any pending debounce for this index
      if (medicationDebounceRefs.current[index]) {
        clearTimeout(medicationDebounceRefs.current[index]);
      }
      
      // Debounce the medication type detection (only after user stops typing)
      if (value) {
        medicationDebounceRefs.current[index] = setTimeout(() => {
          const detectedType = detectMedicationType(value);
          if (detectedType) {
            setSessionForm(prev => ({
              ...prev,
              medications: prev.medications.map((med, i) => 
                i === index && !med.medication_type ? { ...med, medication_type: detectedType } : med
              )
            }));
          }
        }, 300); // 300ms debounce
      }
    } else {
      // For other fields, update immediately as before
      setSessionForm(prev => ({
        ...prev,
        medications: prev.medications.map((med, i) => 
          i === index ? { ...med, [field]: value } : med
        )
      }));
    }
  };

  // File handlers
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(files);
  };

  // Handle file upload
  const handleFileUpload = async (files) => {
    if (!files || files.length === 0) return;
    
    setIsUploading(true);
    const newReports = [];
    
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await fetch(`${API}/api/report-uploads/upload-file`, {
          method: 'POST',
          body: formData,
        });
        
        if (response.ok) {
          const result = await response.json();
          const reportData = {
            id: result.id || Date.now() + Math.random(),
            filename: result.filename || file.name,
            originalName: result.originalName || file.name,
            fileSize: result.file_size || file.size,
            uploadDate: result.uploadDate || new Date().toISOString(),
            fileUrl: result.file_path || result.fileUrl,
            analysis_file_url: result.analysis_file_url || null,
            analysis_text: result.analysis_text || null,
            abnormal_count: result.abnormal_count || 0,
            borderline_count: result.borderline_count || 0,
            reportDate: result.report_date || null,
            report_type: result.report_type || 'Blood Test'
          };
          newReports.push(reportData);
          
          // Mark as analyzing
          setAnalyzingReports(prev => ({...prev, [reportData.id]: true}));
          
          // Poll for analysis results after 3 seconds
          setTimeout(() => pollForAnalysis(reportData.id), 3000);
        } else {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.detail || `Failed to upload ${file.name}`);
        }
      }
      
      setUploadedReports(prev => [...prev, ...newReports]);
      toast.success(`Successfully uploaded ${newReports.length} report(s)`, {
        description: 'Analysis will appear in a few seconds...'
      });
    } catch (error) {
      console.error('File upload error:', error);
      toast.error(error.message || 'Failed to upload reports');
    } finally {
      setIsUploading(false);
    }
  };
  
  const pollForAnalysis = async (reportId, attempts = 0) => {
    if (attempts >= 15) {
      console.log(`Analysis polling stopped for ${reportId} after 15 attempts`);
      return; // Stop after 15 attempts (45 seconds)
    }
    
    try {
      const response = await axios.get(`${API}/api/report-uploads/check-analysis/${reportId}`);
      const result = response.data;
      
      if (result.status === 'completed') {
        // Clear analyzing state
        setAnalyzingReports(prev => {
          const newState = {...prev};
          delete newState[reportId];
          return newState;
        });
        
        // Update the report with analysis results
        setUploadedReports(prev => prev.map(report => 
          report.id === reportId 
            ? {
                ...report,
                analysis_file_url: result.analysis_file_url,
                abnormal_count: result.abnormal_count || 0,
                borderline_count: result.borderline_count || 0
              }
            : report
        ));
        
        // Show toast if analysis found abnormal/borderline values
        if (result.abnormal_count > 0 || result.borderline_count > 0) {
          toast.success('Analysis complete!', {
            description: `Found ${result.abnormal_count} abnormal and ${result.borderline_count} borderline values`
          });
        }
        
        console.log(`Analysis completed for ${reportId}:`, result);
        return; // Stop polling
      } else if (result.status === 'error') {
        console.error(`Analysis failed for ${reportId}:`, result.error);
        toast.error('Analysis failed. You can try re-analyzing the report.');
        return; // Stop polling
      } else {
        // Still processing, poll again after 3 seconds
        setTimeout(() => pollForAnalysis(reportId, attempts + 1), 3000);
      }
    } catch (error) {
      console.error('Error polling for analysis:', error);
      // Retry after 3 seconds
      setTimeout(() => pollForAnalysis(reportId, attempts + 1), 3000);
    }
  };

  // Auto-fill diagnosis from uploaded reports
  const handleAutofillFromUploadedReports = async () => {
    if (uploadedReports.length === 0) {
      toast.error('No reports uploaded to analyze');
      return;
    }

    try {
      // Filter reports that have analysis results
      const analyzedReports = uploadedReports.filter(r => r.analysis_file_url && (r.abnormal_count > 0 || r.borderline_count > 0));
      
      if (analyzedReports.length === 0) {
        toast.error('No analysis results available yet. Please wait for analysis to complete.');
        return;
      }
      
      // Call the backend to generate AI diagnosis from uploaded reports
      const reportIds = analyzedReports.map(r => r.id);
      
      const response = await axios.post(`${API}/api/report-uploads/generate-diagnosis`, {
        report_ids: reportIds
      });
      
      if (response.data && response.data.suggestions) {
        const suggestions = response.data.suggestions;
        
        setSessionForm(prev => ({ 
          ...prev, 
          diagnosis: prev.diagnosis ? `${prev.diagnosis}\n${suggestions}` : suggestions
        }));
        
        toast.success('AI diagnosis suggestions added from reports');
      } else {
        toast.info('No significant findings to add');
      }
    } catch (error) {
      console.error('Error generating diagnosis from uploaded reports:', error);
      toast.error('Failed to generate diagnosis suggestions');
    }
  };

  // Remove uploaded report
  const removeUploadedReport = (reportId) => {
    setUploadedReports(prev => prev.filter(report => report.id !== reportId));
    toast.success('Report removed');
  };

  // Generate Differential Diagnosis using AI
  const generateDifferentialDiagnosis = async () => {
    const chiefComplaints = sessionForm.chief_complaints?.trim();
    const diagnosis = sessionForm.diagnosis?.trim();
    
    if (!chiefComplaints && !diagnosis) {
      toast.error('Please enter chief complaints or diagnosis first');
      return;
    }
    
    setIsGeneratingDifferential(true);
    try {
      const response = await axios.post(`${API}/api/ai-clinical/suggest-diagnosis`, {
        chief_complaints: chiefComplaints || '',
        symptoms: '', // Can be populated from extracted entities if available
        vitals: {
          temperature: sessionForm.temperature,
          pulse: sessionForm.pulse,
          respiratory_rate: sessionForm.respiratory_rate,
          blood_pressure: sessionForm.blood_pressure,
          spo2: sessionForm.spo2
        },
        patient_context: selectedBaby ? `Patient: ${selectedBaby.name}, Age: ${calculateAge(selectedBaby.date_of_birth)}, Gender: ${selectedBaby.gender}` : ''
      });
      
      if (response.data) {
        setDifferentialDiagnosis(response.data);
        toast.success('Differential diagnosis generated');
      }
    } catch (error) {
      console.error('Error generating differential diagnosis:', error);
      toast.error('Failed to generate differential diagnosis');
    } finally {
      setIsGeneratingDifferential(false);
    }
  };

  // View SOAP Notes Dialog
  const handleViewSOAPNotes = (soapNotes) => {
    setViewingSOAPNotes(soapNotes);
    setShowSOAPDialog(true);
  };

  const handleReanalyzeUploadedReport = async (reportId) => {
    setReanalyzingReports(prev => ({...prev, [reportId]: true}));
    try {
      // Re-analyze the live session report (uses different endpoint than saved reports)
      const response = await axios.post(`${API}/api/report-uploads/reanalyze-live/${reportId}`);
      
      // Update the specific report in uploadedReports with new analysis data
      setUploadedReports(prev => prev.map(report => 
        report.id === reportId 
          ? {
              ...report,
              analysis_file_url: response.data.analysis_file_url,
              abnormal_count: response.data.abnormal_count || 0,
              borderline_count: response.data.borderline_count || 0
            }
          : report
      ));
      
      toast.success('Report re-analyzed successfully');
    } catch (error) {
      console.error('Re-analyze error:', error);
      toast.error('Failed to re-analyze report');
    } finally {
      setReanalyzingReports(prev => ({...prev, [reportId]: false}));
    }
  };

  const API = process.env.REACT_APP_BACKEND_URL;
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  // Load previous sessions for this doctor when session form opens
  const loadPreviousDoctorSessions = async (babyId, doctorId) => {
    console.log('Loading previous sessions for baby:', babyId, 'doctor:', doctorId);
    try {
      const response = await axios.get(`${API}/api/medical-sessions/${babyId}`);
      const allSessions = response.data || [];
      console.log('All sessions for baby:', allSessions.length);
      
      // Filter sessions by this doctor and sort by date
      const doctorSessions = allSessions
        .filter(s => s.updated_by === doctorId)
        .sort((a, b) => new Date(b.session_date) - new Date(a.session_date));
      
      console.log('Doctor sessions:', doctorSessions.length);
      setPreviousDoctorSessions(doctorSessions);
      
      // Calculate last visit info
      if (doctorSessions.length > 0) {
        const lastSession = doctorSessions[0];
        const lastVisitDate = new Date(lastSession.session_date);
        const today = new Date();
        const diffDays = Math.floor((today - lastVisitDate) / (1000 * 60 * 60 * 24));
        console.log('Last visit:', diffDays, 'days ago', lastSession.session_date);
        setLastVisitInfo({
          date: lastVisitDate,
          daysAgo: diffDays,
          session: lastSession
        });
      } else {
        console.log('No previous sessions by this doctor');
        setLastVisitInfo(null);
      }
    } catch (error) {
      console.error('Error loading previous sessions:', error);
      setPreviousDoctorSessions([]);
      setLastVisitInfo(null);
    }
  };

  // Generate AI Visit Summary (5 bullets)
  const generateVisitSummary = (session) => {
    const bullets = [];
    if (session.chief_complaints) bullets.push(`Chief: ${session.chief_complaints.substring(0, 50)}...`);
    if (session.diagnosis) bullets.push(`Dx: ${session.diagnosis.substring(0, 50)}...`);
    if (session.medications?.length > 0) bullets.push(`Rx: ${session.medications.length} medications`);
    if (session.tests) bullets.push(`Tests: ${session.tests.substring(0, 40)}...`);
    if (session.next_followup) bullets.push(`F/U: ${formatDate(session.next_followup)}`);
    return bullets.slice(0, 5);
  };

  // Fetch vitals recorded by Medical Assistant for an appointment
  const fetchMAVitals = async (appointmentId) => {
    try {
      const response = await axios.get(`${API}/api/vitals-records/appointment/${appointmentId}`);
      if (response.data) {
        // Lock the vitals so MA can no longer edit
        await axios.put(`${API}/api/vitals-records/lock/${appointmentId}`);
        return response.data;
      }
      return null;
    } catch (error) {
      console.log('No MA vitals found for appointment:', appointmentId);
      return null;
    }
  };

  // Helper function to format doctor name with "Dr." prefix and specialization
  const getFormattedDoctorName = (doctorUser) => {
    if (!doctorUser) return undefined;
    const name = ensureDrPrefix(doctorUser.name);
    // Show only super_specialization if set, otherwise show main specialization
    const displaySpecialization = doctorUser.super_specialization || doctorUser.specialization || null;
    return displaySpecialization ? `${name} (${displaySpecialization})` : name;
  };

  useEffect(() => {
    // Check if user is logged in
    const userData = localStorage.getItem('healthcareUser');
    if (!userData) {
      navigate('/doctor-login');
      return;
    }
    
    const parsedUser = JSON.parse(userData);
    if (parsedUser.role !== 'doctor') {
      toast.error('Access denied. Doctor only.');
      navigate('/doctor-login');
      return;
    }
    
    setUser(parsedUser);
    
    // Load hospital data from localStorage
    const hospitalData = localStorage.getItem('currentHospital');
    if (hospitalData) {
      setHospital(JSON.parse(hospitalData));
    }
    
    loadData(parsedUser.id);
    
    // Check if there's a saved baby view state (from page refresh)
    const savedBabyView = localStorage.getItem('doctorViewingBaby');
    if (savedBabyView) {
      try {
        const { babyId, mode, section } = JSON.parse(savedBabyView);
        // Restore the baby profile view
        restoreBabyProfileView(babyId, mode, section);
      } catch (error) {
        console.error('Error parsing saved baby view:', error);
        // If there's an error, clear the invalid state and show appointments
        localStorage.removeItem('doctorViewingBaby');
        setShowBabyProfile(false);
        setActiveTab('appointments');
      }
    } else {
      // No saved view, start with appointments
      setShowBabyProfile(false);
      setActiveTab('appointments');
    }
    
    // Function to refresh doctor info from database
    const refreshDoctorInfo = async () => {
      try {
        const response = await axios.get(`${API}/api/doctors/${parsedUser.id}`);
        if (response.data) {
          const updatedUser = {
            ...parsedUser,
            name: response.data.name,
            specialization: response.data.specialization,
            email: response.data.email,
            phone: response.data.phone,
            registered_number: response.data.registered_number,
            photo_url: response.data.photo_url
          };
          
          // Update localStorage and state if data has changed
          const currentUser = JSON.parse(localStorage.getItem('healthcareUser'));
          if (JSON.stringify(currentUser) !== JSON.stringify(updatedUser)) {
            localStorage.setItem('healthcareUser', JSON.stringify(updatedUser));
            setUser(updatedUser);
          }
        }
      } catch (error) {
        console.error('Error refreshing doctor info:', error);
      }
    };
    
    // Also refresh doctor info on initial load
    refreshDoctorInfo();
    
    // Load templates
    loadTemplates(parsedUser.id);
  }, [navigate]);

  // Smart auto-refresh that pauses during user interactions
  useSmartRefresh(
    useCallback(() => {
      const userData = localStorage.getItem('healthcareUser');
      if (userData) {
        const parsedUser = JSON.parse(userData);
        loadData(parsedUser.id);
        refreshDoctorInfo();
      }
    }, []),
    120000, // 2 minutes
    5000    // Wait 5 seconds after user activity before resuming
  );

  // Update localStorage when panel section changes
  useEffect(() => {
    if (selectedBaby && showBabyProfile && activePanelSection) {
      saveBabyViewState(selectedBaby.id, profileDisplayMode, activePanelSection);
    }
  }, [activePanelSection, selectedBaby, showBabyProfile, profileDisplayMode]);

  // Reload doctor settings when switching away from configuration tab
  useEffect(() => {
    const reloadSettings = async () => {
      if (user?.id && activeTab !== 'configuration') {
        try {
          const response = await axios.get(`${API}/api/doctor-settings/${user.id}`);
          setDoctorSettings(response.data || { show_common_medications: false });
        } catch (error) {
          console.error('Error reloading doctor settings:', error);
        }
      }
    };
    
    reloadSettings();
  }, [activeTab, user?.id]);

  // Load AI features settings (master and hospital-level)
  useEffect(() => {
    const loadAiFeatureSettings = async () => {
      try {
        // Load master AI settings from Central Admin
        const masterResponse = await axios.get(`${API}/api/ai-features-settings`);
        // Only enable features that are explicitly set to true
        const settings = {
          ai_clinical_assistant_enabled: masterResponse.data.ai_clinical_assistant_enabled === true,
          differential_diagnosis_enabled: masterResponse.data.differential_diagnosis_enabled === true,
          voice_dictation_enabled: masterResponse.data.voice_dictation_enabled === true
        };
        setAiFeatureSettings(settings);
        setAiSettingsLoaded(true);
        
        // Load hospital-specific AI settings
        const hospital = JSON.parse(localStorage.getItem('currentHospital') || '{}');
        if (hospital?.id) {
          const hospitalResponse = await axios.get(`${API}/api/hospitals/${hospital.id}/ai-features`);
          setHospitalAiFeaturesEnabled(hospitalResponse.data.ai_features_enabled === true);
        }
      } catch (error) {
        console.error('Error loading AI features settings:', error);
        // Default to disabled if error - fail safe
        setAiFeatureSettings({ 
          ai_clinical_assistant_enabled: false, 
          differential_diagnosis_enabled: false,
          voice_dictation_enabled: false 
        });
        setHospitalAiFeaturesEnabled(false);
        setAiSettingsLoaded(true);
      }
    };
    
    loadAiFeatureSettings();
  }, []);

  // Calculate BMI when height, weight changes
  useEffect(() => {
    if (sessionForm.height && sessionForm.weight && selectedBaby?.date_of_birth && selectedBaby?.gender) {
      const bmiResult = calculateBMI(
        parseFloat(sessionForm.weight),
        parseFloat(sessionForm.height),
        selectedBaby.date_of_birth,
        selectedBaby.gender,
        user?.specialization || ''
      );
      setCalculatedBMI(bmiResult);
    } else {
      setCalculatedBMI(null);
    }
  }, [sessionForm.height, sessionForm.weight, selectedBaby?.date_of_birth, selectedBaby?.gender, user?.specialization]);

  // Auto-update to current date after midnight and reload appointments
  useEffect(() => {
    const checkDateChange = async () => {
      // Get current date in local timezone
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const currentDate = `${year}-${month}-${day}`;
      
      if (selectedDate !== currentDate) {
        console.log(`🕐 Date changed from ${selectedDate} to ${currentDate} (${now.toLocaleString()})`);
        console.log(`📅 Updating dashboard to show ${currentDate} appointments...`);
        
        // First update the date
        setSelectedDate(currentDate);
        
        // Then reload appointments to show new date's appointments
        if (user?.id) {
          await loadData(user.id);
          console.log(`✅ Dashboard updated - now showing ${currentDate} appointments`);
          toast.success(`Dashboard updated to ${formatDateToDDMMYYYY(currentDate)}`);
        }
      }
    };

    // Check every minute if date has changed
    const interval = setInterval(checkDateChange, 60000); // Check every 60 seconds

    return () => clearInterval(interval);
  }, [selectedDate, user]);

  // Refetch appointments when date changes manually
  useEffect(() => {
    if (user?.id && selectedDate) {
      // Silently refetch appointments when date changes
      const fetchAppointmentsForDate = async () => {
        try {
          const currentHospitalData = localStorage.getItem('currentHospital');
          const currentHospital = currentHospitalData ? JSON.parse(currentHospitalData) : null;
          const hospitalId = currentHospital?.id;
          
          const response = await axios.get(
            `${API}/api/appointments-detailed?doctor_id=${user.id}${hospitalId ? `&hospital_id=${hospitalId}` : ''}`,
            { timeout: 30000 }
          );
          setAppointments(response.data);
          console.log(`📅 Loaded ${response.data.length} appointments for date filter`);
        } catch (error) {
          console.error('Error fetching appointments for date:', error);
          // Don't show error toast for background refresh - only log it
        }
      };
      
      fetchAppointmentsForDate();
    }
  }, [selectedDate, user?.id]);

  // Fetch all patients when "All Patients" tab is selected
  const fetchAllPatients = useCallback(async () => {
    if (!user?.id) return;
    
    setAllPatientsLoading(true);
    try {
      const currentHospitalData = localStorage.getItem('currentHospital');
      const currentHospital = currentHospitalData ? JSON.parse(currentHospitalData) : null;
      const hospitalId = currentHospital?.id;
      
      const response = await axios.get(
        `${API}/api/doctor/${user.id}/all-patients${hospitalId ? `?hospital_id=${hospitalId}` : ''}`,
        { timeout: 30000 }
      );
      setAllPatients(response.data);
      console.log(`👥 Loaded ${response.data.length} patients for All Patients tab`);
    } catch (error) {
      console.error('Error fetching all patients:', error);
      toast.error('Failed to load patients');
    } finally {
      setAllPatientsLoading(false);
    }
  }, [user?.id]);

  // Load all patients when tab is switched to 'all-patients'
  useEffect(() => {
    if (activeTab === 'all-patients' && user?.id) {
      fetchAllPatients();
    }
  }, [activeTab, user?.id, fetchAllPatients]);

  // Real-time data synchronization - Poll for updates every 30 seconds
  useEffect(() => {
    const syncData = async () => {
      if (user?.id) {
        // Check if user is actively working (dialog open, input focused, or typing)
        const activeElement = document.activeElement;
        const isInputActive = activeElement && (
          activeElement.tagName === 'INPUT' || 
          activeElement.tagName === 'TEXTAREA' ||
          activeElement.isContentEditable
        );
        
        // Check if any dialog/modal is open
        const isDialogOpen = document.querySelector('[role="dialog"]') !== null;
        
        // Skip sync if user is actively working
        if (isInputActive || isDialogOpen) {
          console.log('⏸️ Skipping sync - user is actively working');
          return;
        }
        
        try {
          // Silently reload data in background
          const [appointmentsRes, babiesRes] = await Promise.all([
            axios.get(`${API}/appointments`),
            axios.get(`${API}/babies`)
          ]);

          // Filter appointments for this doctor
          const doctorAppointments = appointmentsRes.data.filter(apt => apt.doctor_id === user.id);
          
          // Only update if data has actually changed
          if (JSON.stringify(doctorAppointments) !== JSON.stringify(appointments)) {
            setAppointments(doctorAppointments);
            console.log('🔄 Appointments updated in real-time');
          }

          if (JSON.stringify(babiesRes.data) !== JSON.stringify(babies)) {
            setBabies(babiesRes.data);
            console.log('🔄 Babies data updated in real-time');
          }
        } catch (error) {
          console.error('Error syncing data:', error);
          // Silently fail - don't show error to user for background sync
        }
      }
    };

    // Sync data every 30 seconds
    const syncInterval = setInterval(syncData, 30000); // 30 seconds

    return () => clearInterval(syncInterval);
  }, [user, appointments, babies]);

  const loadData = async (doctorId) => {
    try {
      setLoading(true);
      // Get current hospital from localStorage
      const currentHospitalData = localStorage.getItem('currentHospital');
      const currentHospital = currentHospitalData ? JSON.parse(currentHospitalData) : null;
      const hospitalId = currentHospital?.id;
      
      // Retry logic for API calls
      const fetchWithRetry = async (url, retries = 3, delay = 1000) => {
        for (let i = 0; i < retries; i++) {
          try {
            const response = await axios.get(url, { timeout: 30000 });
            return response;
          } catch (error) {
            if (i === retries - 1) throw error;
            console.warn(`Retry ${i + 1}/${retries} for ${url}`);
            await new Promise(r => setTimeout(r, delay));
          }
        }
      };
      
      const [appointmentsRes, babiesRes] = await Promise.all([
        fetchWithRetry(`${API}/api/appointments-detailed?doctor_id=${doctorId}${hospitalId ? `&hospital_id=${hospitalId}` : ''}`),
        fetchWithRetry(`${API}/api/babies`)
      ]);
      
      // Appointments are already filtered by doctor_id and hospital_id from backend
      setAppointments(appointmentsRes.data);
      setBabies(babiesRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  // Save baby view state to localStorage
  const saveBabyViewState = (babyId, mode, section = null) => {
    localStorage.setItem('doctorViewingBaby', JSON.stringify({
      babyId,
      mode,
      section
    }));
  };

  // Restore baby profile view from saved state
  const restoreBabyProfileView = async (babyId, mode, section) => {
    try {
      await loadBabyProfile(babyId, mode);
      if (section) {
        setActivePanelSection(section);
      }
    } catch (error) {
      console.error('Error restoring baby profile view:', error);
      // Clear invalid saved state and return to appointments
      localStorage.removeItem('doctorViewingBaby');
      setShowBabyProfile(false);
      setBabyDetails(null);
      setSelectedBaby(null);
      setActiveTab('appointments');
      toast.error('Could not restore previous view. Showing appointments.');
    }
  };

  // Clear baby view state
  const clearBabyViewState = () => {
    localStorage.removeItem('doctorViewingBaby');
  };

  // Load templates
  const loadTemplates = async (doctorId) => {
    try {
      const [medResponse, chiefComplaintResponse, testResponse, diagnosisResponse, notesResponse, commonMedResponse, settingsResponse] = await Promise.all([
        axios.get(`${API}/api/medication-templates/${doctorId}`),
        axios.get(`${API}/api/chief-complaint-templates/${doctorId}`),
        axios.get(`${API}/api/test-templates/${doctorId}`),
        axios.get(`${API}/api/diagnosis-templates/${doctorId}`),
        axios.get(`${API}/api/additional-notes-templates/${doctorId}`),
        axios.get(`${API}/api/common-medications`),
        axios.get(`${API}/api/doctor-settings/${doctorId}`)
      ]);
      setMedicationTemplates(medResponse.data);
      setChiefComplaintTemplates(chiefComplaintResponse.data);
      setTestTemplates(testResponse.data);
      setDiagnosisTemplates(diagnosisResponse.data);
      setAdditionalNotesTemplates(notesResponse.data);
      console.log('Chief Complaint templates loaded:', chiefComplaintResponse.data);
      console.log('Diagnosis templates loaded:', diagnosisResponse.data);
      console.log('Additional Notes templates loaded:', notesResponse.data);
      setCommonMedications(commonMedResponse.data);
      setDoctorSettings(settingsResponse.data || { show_common_medications: false });
    } catch (error) {
      console.error('Error loading templates:', error);
      // Set defaults on error
      setChiefComplaintTemplates([]);
      setTestTemplates([]);
      setDiagnosisTemplates([]);
      setAdditionalNotesTemplates([]);
      setCommonMedications([]);
      setDoctorSettings({ show_common_medications: false });
    }
  };


  const handleLogout = () => {
    // Clear all doctor-related state
    localStorage.removeItem('healthcareUser');
    localStorage.removeItem('doctorViewingBaby'); // Clear saved baby profile state
    sessionStorage.removeItem('doctorActiveTab');
    
    // Reset state before navigation
    setShowBabyProfile(false);
    setSelectedBaby(null);
    setBabyDetails(null);
    setActiveTab('appointments');
    
    toast.success('Logged out successfully');
    navigate('/doctor-login');
  };

  // Format date helper
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    
    // If already in DD/MM/YYYY format, return as is
    if (typeof dateStr === 'string' && dateStr.includes('/')) {
      return dateStr;
    }
    
    // Otherwise convert from ISO format
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return 'Invalid Date';
    
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Convert DD/MM/YYYY to YYYY-MM-DD for backend
  const convertToISODate = (ddmmyyyy) => {
    if (!ddmmyyyy || ddmmyyyy.length !== 10) return '';
    const [day, month, year] = ddmmyyyy.split('/');
    return `${year}-${month}-${day}`;
  };

  // Generic date input handler (DD/MM/YYYY format)
  const handleDateInput = (setter, field) => (e) => {
    let value = e.target.value;
    
    // Remove all non-numeric characters except /
    value = value.replace(/[^\d/]/g, '');
    
    // Auto-add slashes and format
    if (value.length === 2 && !value.includes('/')) {
      value = value + '/';
    } else if (value.length === 5 && value.split('/').length === 2) {
      value = value + '/';
    }
    
    // Limit to dd/mm/yyyy format (10 characters)
    if (value.length > 10) {
      value = value.substring(0, 10);
    }
    
    setter(prev => ({ ...prev, [field]: value }));
  };

  // Generic date backspace handler
  const handleDateKeyDown = (setter, field) => (e) => {
    if (e.key === 'Backspace') {
      const input = e.target;
      const value = input.value;
      const cursorPosition = input.selectionStart;
      
      // If cursor is right after a slash, delete the slash and the character before it
      if (cursorPosition > 0 && value[cursorPosition - 1] === '/') {
        e.preventDefault();
        const newValue = value.substring(0, cursorPosition - 2) + value.substring(cursorPosition);
        setter(prev => ({ ...prev, [field]: newValue }));
        // Set cursor position after the state updates
        setTimeout(() => {
          input.selectionStart = input.selectionEnd = cursorPosition - 2;
        }, 0);
      }
    }
  };

  // Helper to normalize date for comparison (handles both YYYY-MM-DD and DD/MM/YYYY)
  const normalizeDateForComparison = (dateStr) => {
    if (!dateStr) return '';
    // If already in YYYY-MM-DD format, return as-is
    if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) return dateStr;
    // Convert DD/MM/YYYY to YYYY-MM-DD
    if (dateStr.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
      const [day, month, year] = dateStr.split('/');
      return `${year}-${month}-${day}`;
    }
    return dateStr;
  };

  // Calculate age helper (matching baby app)
  const calculateAge = (birthDate) => {
    if (!birthDate) return 'N/A';
    
    let birth;
    // Handle DD/MM/YYYY format
    if (birthDate.includes('/')) {
      const [day, month, year] = birthDate.split('/');
      birth = new Date(year, month - 1, day);
    } else {
      // Handle ISO format (YYYY-MM-DD)
      birth = new Date(birthDate);
    }
    
    const today = new Date();
    let years = today.getFullYear() - birth.getFullYear();
    let months = today.getMonth() - birth.getMonth();
    let days = today.getDate() - birth.getDate();
    
    if (days < 0) {
      months--;
      const prevMonth = new Date(today.getFullYear(), today.getMonth(), 0);
      days += prevMonth.getDate();
    }
    
    if (months < 0) {
      years--;
      months += 12;
    }
    
    const parts = [];
    if (years > 0) {
      parts.push(`${years} year${years > 1 ? 's' : ''}`);
    }
    if (months > 0) {
      parts.push(`${months} month${months > 1 ? 's' : ''}`);
    }
    if (days > 0 || parts.length === 0) {
      parts.push(`${days} day${days !== 1 ? 's' : ''}`);
    }
    
    return parts.join(', ');
  };

  // Calculate statistics
  const calculateStats = () => {
    const today = new Date().toISOString().split('T')[0];
    const todayAppointments = appointments.filter(apt => normalizeDateForComparison(apt.appointment_date) === today);
    
    // Weekly calculation (last 7 days)
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    const weekAgoStr = weekAgo.toISOString().split('T')[0];
    
    // Use normalizeDateForComparison for consistent date comparison
    const weeklyAppointments = appointments.filter(apt => {
      const normalizedDate = normalizeDateForComparison(apt.appointment_date);
      return normalizedDate >= weekAgoStr && normalizedDate <= today;
    });
    
    // Filter out cancelled appointments from totals
    const todayActive = todayAppointments.filter(apt => apt.status !== 'cancelled');
    const weeklyActive = weeklyAppointments.filter(apt => apt.status !== 'cancelled');
    
    return {
      today: todayActive.length,
      todayCompleted: todayActive.filter(apt => apt.status === 'completed').length,
      weekly: weeklyActive.length,
      weeklyCompleted: weeklyActive.filter(apt => apt.status === 'completed').length
    };
  };

  // Quick lookup baby
  const handleQuickLookup = async () => {
    try {
      let baby = null;
      
      if (lookupQuery.id) {
        // Search by ID (support short_id, full UUID, or UUID prefix)
        const searchId = lookupQuery.id.trim();
        baby = babies.find(b => 
          b.id === searchId || 
          (b.short_id && b.short_id.toUpperCase() === searchId.toUpperCase()) ||
          b.id.toLowerCase().startsWith(searchId.toLowerCase())
        );
      } else if (lookupQuery.name && lookupQuery.dob) {
        // Try to match DOB in multiple formats
        const inputDob = lookupQuery.dob; // e.g., "19/10/2018"
        const isoDob = lookupQuery.dob.includes('/') 
          ? convertToISODate(lookupQuery.dob) // Convert to "2018-10-19"
          : lookupQuery.dob;
        
        console.log('Searching for baby:', {
          searchName: lookupQuery.name,
          searchDobOriginal: inputDob,
          searchDobISO: isoDob,
          babies: babies.map(b => ({ name: b.name, dob: b.date_of_birth }))
        });
        
        // Search by name and DOB (try both DD/MM/YYYY and YYYY-MM-DD formats)
        baby = babies.find(b => {
          const nameMatch = b.name.trim().toLowerCase() === lookupQuery.name.trim().toLowerCase();
          const dobMatch = b.date_of_birth === isoDob || b.date_of_birth === inputDob;
          
          console.log('Checking baby:', {
            babyName: b.name,
            babyDob: b.date_of_birth,
            nameMatch,
            dobMatch,
            comparing: `${b.date_of_birth} === ${isoDob} || ${b.date_of_birth} === ${inputDob}`
          });
          
          return nameMatch && dobMatch;
        });
      }
      
      if (baby) {
        toast.success(`Baby found: ${baby.name}`);
        await loadBabyProfile(baby.id, 'panel'); // Use panel mode for quick lookup
        setShowQuickLookup(false);
        setLookupQuery({ id: '', name: '', dob: '' });
      } else {
        toast.error('Baby not found. Please check the details and try again.');
      }
    } catch (error) {
      console.error('Error in quick lookup:', error);
      toast.error('Failed to find baby');
    }
  };

  // Quick Appointment - Search for baby
  const handleQuickAppointmentSearch = async () => {
    try {
      let baby = null;
      
      if (quickAppointmentForm.baby_id) {
        const searchId = quickAppointmentForm.baby_id.trim();
        baby = babies.find(b => 
          b.id === searchId || 
          (b.short_id && b.short_id.toUpperCase() === searchId.toUpperCase()) ||
          b.id.toLowerCase().startsWith(searchId.toLowerCase())
        );
      } else if (quickAppointmentForm.baby_name && quickAppointmentForm.baby_dob) {
        const inputDob = quickAppointmentForm.baby_dob;
        const isoDob = quickAppointmentForm.baby_dob.includes('/') 
          ? convertToISODate(quickAppointmentForm.baby_dob)
          : quickAppointmentForm.baby_dob;
        
        baby = babies.find(b => {
          const nameMatch = b.name.trim().toLowerCase() === quickAppointmentForm.baby_name.trim().toLowerCase();
          const dobMatch = b.date_of_birth === isoDob || b.date_of_birth === inputDob;
          return nameMatch && dobMatch;
        });
      }
      
      if (baby) {
        setFoundBabyQuick(baby);
        setQuickAppointmentForm(prev => ({ ...prev, baby_id: baby.short_id || baby.id.substring(0, 8).toUpperCase() }));
        toast.success(`Found: ${baby.name}`);
      } else {
        setFoundBabyQuick(null);
        toast.error('Baby not found. Please check the details and try again.');
      }
    } catch (error) {
      console.error('Error in quick appointment search:', error);
      toast.error('Failed to find baby');
    }
  };

  // Quick Appointment - Create appointment
  const handleCreateQuickAppointment = async () => {
    try {
      if (!quickAppointmentForm.baby_phone) {
        toast.error('Please provide phone number');
        return;
      }

      // Validate category_ref for Known category
      if (quickAppointmentForm.category === 'Known' && !quickAppointmentForm.category_ref.trim()) {
        toast.error('Please provide a reference for Known category');
        return;
      }

      // Validate payment method for Regular category
      if (quickAppointmentForm.category === 'Regular' && !quickAppointmentForm.payment_method) {
        toast.error('Please select a payment method');
        return;
      }

      let finalBabyId = null;

      if (useExistingBabyQuick) {
        if (foundBabyQuick) {
          finalBabyId = foundBabyQuick.id;
        } else if (quickAppointmentForm.baby_id) {
          const searchId = quickAppointmentForm.baby_id.toLowerCase();
          const baby = babies.find(b => 
            b.id === quickAppointmentForm.baby_id || 
            (b.short_id && b.short_id.toUpperCase() === quickAppointmentForm.baby_id.toUpperCase()) ||
            b.id.toLowerCase().startsWith(searchId)
          );
          if (!baby) {
            toast.error('Baby not found. Please search first.');
            return;
          }
          finalBabyId = baby.id;
        } else {
          toast.error('Please search for a baby first');
          return;
        }
      } else {
        // Create new baby
        if (!quickAppointmentForm.baby_name || !quickAppointmentForm.baby_dob || !quickAppointmentForm.baby_gender) {
          toast.error('Please provide all baby details');
          return;
        }

        // Check for duplicate baby
        const duplicateCheck = await axios.post(`${API}/api/babies/check-duplicate`, {
          name: quickAppointmentForm.baby_name,
          date_of_birth: quickAppointmentForm.baby_dob
        });

        if (duplicateCheck.data.exists) {
          const existingBaby = duplicateCheck.data.baby;
          toast.error(`Already exists: ${existingBaby.name}. Please use existing option.`);
          return;
        }

        // Create baby
        const newBabyData = {
          name: quickAppointmentForm.baby_name,
          date_of_birth: quickAppointmentForm.baby_dob,
          gender: quickAppointmentForm.baby_gender,
          height: 0.0,
          weight: 0.0,
          created_via: "quick_appointment"
        };

        const babyResponse = await axios.post(`${API}/api/babies`, newBabyData);
        finalBabyId = babyResponse.data.id;
        
        // Initialize vaccination schedule for new baby (non-blocking)
        axios.post(`${API}/api/vaccinations/initialize/${finalBabyId}`)
          .catch(vaccError => console.error('Error initializing vaccinations:', vaccError));
      }

      setQuickAppointmentSubmitting(true);

      // Get today's date in YYYY-MM-DD format
      const now = new Date();
      const todayDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      
      // Get current time for display
      const currentTime = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });

      // Determine payment method based on category
      const paymentMethod = quickAppointmentForm.category === 'Regular' 
        ? quickAppointmentForm.payment_method 
        : 'N/A';

      // Create quick appointment
      const appointmentData = {
        baby_id: finalBabyId,
        doctor_id: user.id,
        hospital_id: hospital?.id || user.hospital_id,
        appointment_date: todayDate,
        appointment_time: `Quick - ${currentTime}`,
        notes: quickAppointmentForm.baby_phone,
        payment_method: paymentMethod,
        booking_source: 'quick_appointment',
        created_by: user.id,
        appointment_type: useExistingBabyQuick ? quickAppointmentForm.appointment_type : 'new',
        category: quickAppointmentForm.category,
        category_ref: quickAppointmentForm.category_ref || null,
        is_quick_appointment: true
      };

      const response = await axios.post(`${API}/api/appointments`, appointmentData);
      
      toast.success('Quick Appointment created successfully!');
      
      // Reload appointments
      await loadData(user.id);
      
      // Close dialog and reset form
      setShowQuickAppointment(false);
      setQuickAppointmentForm({
        baby_id: '',
        baby_name: '',
        baby_dob: '',
        baby_gender: 'Male',
        baby_phone: '',
        baby_country_code: '+91',
        notes: '',
        appointment_type: 'new',
        category: 'Regular',
        category_ref: '',
        payment_method: 'Cash'
      });
      setFoundBabyQuick(null);
      setUseExistingBabyQuick(true);
    } catch (error) {
      console.error('Error creating quick appointment:', error);
      toast.error(error.response?.data?.detail || 'Failed to create appointment');
    } finally {
      setQuickAppointmentSubmitting(false);
    }
  };

  // Handle DOB input with auto-advance for Quick Appointment
  const handleQuickAppointmentDobInput = (value) => {
    let cleaned = value.replace(/\D/g, '');
    
    if (cleaned.length >= 2) {
      let day = cleaned.substring(0, 2);
      if (parseInt(day) > 31) day = '31';
      cleaned = day + '/' + (cleaned.length > 2 ? cleaned.substring(2) : '');
    }
    
    if (cleaned.length >= 5) {
      let month = cleaned.substring(3, 5);
      if (parseInt(month) > 12) month = '12';
      cleaned = cleaned.substring(0, 3) + month + '/' + (cleaned.length > 5 ? cleaned.substring(5, 9) : '');
    }
    
    if (cleaned.length > 10) {
      cleaned = cleaned.substring(0, 10);
    }
    
    setQuickAppointmentForm(prev => ({ ...prev, baby_dob: cleaned }));
  };

  // Load baby profile with all details
  const loadBabyProfile = async (babyId, displayMode = 'full') => {
    try {
      setLoading(true); // Show loading state
      
      const [babyRes, birthRecordRes, sessionsRes, conditionsRes, allergiesRes, vaccinationsRes, growthRes, temperatureRes, bloodPressureRes] = await Promise.all([
        axios.get(`${API}/api/babies/${babyId}`),
        axios.get(`${API}/api/birth-record/${babyId}`).catch(() => ({ data: null })),
        axios.get(`${API}/api/medical-sessions/${babyId}`),
        axios.get(`${API}/api/medical-conditions/${babyId}`),
        axios.get(`${API}/api/allergies/${babyId}`),
        axios.get(`${API}/api/vaccinations/${babyId}`),
        axios.get(`${API}/api/growth-records/${babyId}`),
        axios.get(`${API}/api/temperature/${babyId}`).catch(() => ({ data: [] })),
        axios.get(`${API}/api/blood-pressure/${babyId}`).catch(() => ({ data: [] }))
      ]);
      
      // Sort temperature readings by created_at timestamp, most recent first
      const sortedTemperature = temperatureRes.data.sort((a, b) => {
        const timeA = new Date(a.created_at || a.date_recorded).getTime();
        const timeB = new Date(b.created_at || b.date_recorded).getTime();
        return timeB - timeA; // Descending order (latest first)
      });
      
      // Sort blood pressure readings by created_at timestamp, most recent first
      const sortedBloodPressure = bloodPressureRes.data.sort((a, b) => {
        const timeA = new Date(a.created_at || a.date_recorded).getTime();
        const timeB = new Date(b.created_at || b.date_recorded).getTime();
        return timeB - timeA; // Descending order (latest first)
      });
      
      // Sort growth records by created_at or date_recorded, most recent first (matching baby app)
      const sortedGrowth = growthRes.data.sort((a, b) => {
        if (a.created_at && b.created_at) {
          return new Date(b.created_at) - new Date(a.created_at);
        }
        return new Date(b.date_recorded) - new Date(a.date_recorded);
      });
      
      // Sort medical sessions by session_date, most recent first, then by created_at
      const sortedSessions = sessionsRes.data.sort((a, b) => {
        let dateA, dateB;
        // Handle both DD/MM/YYYY and YYYY-MM-DD formats
        if (a.session_date && a.session_date.includes('/')) {
          const [dayA, monthA, yearA] = a.session_date.split('/');
          dateA = new Date(yearA, monthA - 1, dayA);
        } else {
          dateA = new Date(a.session_date);
        }
        if (b.session_date && b.session_date.includes('/')) {
          const [dayB, monthB, yearB] = b.session_date.split('/');
          dateB = new Date(yearB, monthB - 1, dayB);
        } else {
          dateB = new Date(b.session_date);
        }
        
        // First sort by date
        const dateDiff = dateB - dateA;
        if (dateDiff !== 0) {
          return dateDiff; // Descending order (newest first)
        }
        
        // If same date, sort by created_at (most recently created first)
        if (a.created_at && b.created_at) {
          return new Date(b.created_at) - new Date(a.created_at);
        }
        
        return 0;
      });
      
      // Merge birth record data into baby object if it exists
      const babyData = babyRes.data;
      if (birthRecordRes.data) {
        babyData.birth_weight = birthRecordRes.data.birth_weight;
        babyData.birth_length = birthRecordRes.data.length;  // Backend uses 'length'
        babyData.birth_head_circumference = birthRecordRes.data.head_circumference;  // Backend uses 'head_circumference'
        babyData.length = birthRecordRes.data.length;  // Also store as 'length' for compatibility
        babyData.head_circumference = birthRecordRes.data.head_circumference;  // Also store as 'head_circumference'
        babyData.mode_of_delivery = birthRecordRes.data.mode_of_delivery;
        babyData.hospital_name = birthRecordRes.data.hospital_name;
        babyData.doctor_name = birthRecordRes.data.doctor_name;
        babyData.antenatal_history = birthRecordRes.data.antenatal_history;
        babyData.birth_history = birthRecordRes.data.birth_history;
      }
      
      setBabyDetails({
        baby: babyData,
        birthRecord: birthRecordRes.data,
        sessions: sortedSessions,
        conditions: conditionsRes.data,
        allergies: allergiesRes.data,
        vaccinations: vaccinationsRes.data,
        growth: sortedGrowth,
        temperature: sortedTemperature,
        bloodPressure: sortedBloodPressure
      });
      setSelectedBaby(babyData);
      setProfileDisplayMode(displayMode); // Set display mode
      setShowBabyProfile(true);
      
      // Save view state to localStorage for persistence across refreshes
      saveBabyViewState(babyId, displayMode, activePanelSection);
      
      setLoading(false);
    } catch (error) {
      console.error('Error loading baby profile:', error);
      toast.error('Failed to load baby profile');
      setLoading(false);
    }
  };

  // Handle appointment click
  const handleAppointmentClick = async (appointment) => {
    if (appointment.baby) {
      setCurrentAppointmentId(appointment.id); // Track appointment for MA vitals
      setCurrentAppointment(appointment); // Store full appointment including follow-up info
      await loadBabyProfile(appointment.baby.id, 'full'); // Use full mode for appointment clicks
    }
  };

  // Handle session done (mark appointment as completed)
  const handleCaseDone = async (appointmentId) => {
    try {
      await axios.put(`${API}/api/appointments/${appointmentId}/complete`);
      toast.success('Session marked as completed');
      // Reload appointments to reflect the change
      await loadData(user.id);
    } catch (error) {
      console.error('Error marking session as done:', error);
      toast.error('Failed to mark session as completed');
    }
  };

  // Reload baby profile data (for real-time sync)
  const reloadBabyProfile = async () => {
    if (selectedBaby) {
      await loadBabyProfile(selectedBaby.id);
    }
  };

  // Medical Conditions CRUD
  const handleAddCondition = async () => {
    if (!conditionForm.condition_name) {
      toast.error('Please enter condition name');
      return;
    }
    
    try {
      setSubmitting(true);
      
      // Convert date if in DD/MM/YYYY format
      const isoDate = conditionForm.diagnosed_date && conditionForm.diagnosed_date.includes('/') 
        ? convertToISODate(conditionForm.diagnosed_date)
        : conditionForm.diagnosed_date;
      
      await axios.post(`${API}/api/medical-conditions`, {
        baby_id: selectedBaby.id,
        ...conditionForm,
        diagnosed_date: isoDate,
        updated_by: user?.id,  // Track who created it
        updated_by_doctor: getFormattedDoctorName(user),
        updated_by_hospital: hospital?.name || undefined
      });
      toast.success('Medical condition added successfully');
      setShowAddCondition(false);
      setConditionForm({ condition_name: '', description: '', diagnosed_date: '', medication: '', status: 'active' });
      await reloadBabyProfile();
    } catch (error) {
      console.error('Error adding condition:', error);
      toast.error('Failed to add medical condition');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditCondition = async () => {
    if (!conditionForm.condition_name) {
      toast.error('Please enter condition name');
      return;
    }
    
    try {
      setSubmitting(true);
      const isoDate = conditionForm.diagnosed_date && conditionForm.diagnosed_date.includes('/') 
        ? convertToISODate(conditionForm.diagnosed_date)
        : conditionForm.diagnosed_date;
      
      await axios.put(`${API}/api/medical-conditions/${editingCondition.id}`, {
        baby_id: selectedBaby.id,
        condition_name: conditionForm.condition_name,
        description: conditionForm.description,
        diagnosed_date: isoDate,
        status: conditionForm.status,
        medication: conditionForm.medication,
        updated_by: user?.id,
        updated_by_doctor: getFormattedDoctorName(user),
        updated_by_hospital: hospital?.name || undefined
      });
      toast.success('Medical condition updated successfully');
      setShowAddCondition(false);
      setEditingCondition(null);
      setConditionForm({ condition_name: '', description: '', diagnosed_date: '', medication: '', status: 'active' });
      await reloadBabyProfile();
    } catch (error) {
      console.error('Error updating condition:', error);
      toast.error('Failed to update medical condition');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteCondition = async (conditionId) => {
    if (!window.confirm('Are you sure you want to delete this medical condition?')) return;
    
    try {
      await axios.delete(`${API}/api/medical-conditions/${conditionId}`);
      toast.success('Medical condition deleted successfully');
      await reloadBabyProfile();
    } catch (error) {
      console.error('Error deleting condition:', error);
      toast.error('Failed to delete medical condition');
    }
  };

  const openEditCondition = (condition) => {
    setEditingCondition(condition);
    setConditionForm({
      condition_name: condition.condition_name,
      description: condition.description || '',
      diagnosed_date: condition.diagnosed_date || '',
      medication: condition.medication || '',
      status: condition.status || 'Active'
    });
    setShowAddCondition(true);
  };

  // Temperature CRUD handlers
  const handleAddTemperature = async (e) => {
    e.preventDefault();
    if (!selectedBaby || !selectedBaby.id) {
      toast.error('Baby data not loaded. Please try again.');
      return;
    }

    try {
      const doctorInfo = {
        updated_by_doctor: getFormattedDoctorName(user),
        updated_by_hospital: hospital?.name || undefined
      };
      
      await axios.post(`${API}/api/temperature`, {
        baby_id: selectedBaby.id,
        ...tempFormData,
        ...doctorInfo
      });
      toast.success('Temperature reading added successfully');
      setShowTempDialog(false);
      setTempFormData({
        temperature: '',
        unit: 'C',
        method: 'Oral',
        date_recorded: '',
        time_recorded: new Date().toTimeString().slice(0, 5),
        notes: ''
      });
      await reloadBabyProfile();
    } catch (error) {
      console.error('Error adding temperature:', error);
      toast.error('Failed to add temperature reading');
    }
  };

  const handleEditTemperature = async (e) => {
    e.preventDefault();
    if (!editingTemperature) return;

    try {
      await axios.put(`${API}/api/temperature/${editingTemperature.id}`, tempFormData);
      toast.success('Temperature reading updated successfully');
      setShowEditTempDialog(false);
      setEditingTemperature(null);
      await reloadBabyProfile();
    } catch (error) {
      console.error('Error updating temperature:', error);
      toast.error('Failed to update temperature reading');
    }
  };

  const handleDeleteTemperature = async (tempId) => {
    if (!window.confirm('Are you sure you want to delete this temperature reading?')) return;

    try {
      await axios.delete(`${API}/api/temperature/${tempId}`);
      toast.success('Temperature reading deleted successfully');
      await reloadBabyProfile();
    } catch (error) {
      console.error('Error deleting temperature:', error);
      toast.error('Failed to delete temperature reading');
    }
  };

  const openEditTemperature = (temp) => {
    setEditingTemperature(temp);
    setTempFormData({
      temperature: temp.temperature,
      unit: temp.unit,
      method: temp.method,
      date_recorded: temp.date_recorded,
      time_recorded: temp.time_recorded,
      notes: temp.notes || ''
    });
    setShowEditTempDialog(true);
  };

  // Blood Pressure CRUD handlers
  const handleAddBloodPressure = async (e) => {
    e.preventDefault();
    if (!selectedBaby || !selectedBaby.id) {
      toast.error('Baby data not loaded. Please try again.');
      return;
    }

    try {
      const doctorInfo = {
        updated_by_doctor: getFormattedDoctorName(user),
        updated_by_hospital: hospital?.name || undefined
      };
      
      await axios.post(`${API}/api/blood-pressure`, {
        baby_id: selectedBaby.id,
        ...bpFormData,
        ...doctorInfo
      });
      toast.success('Blood pressure reading added successfully');
      setShowBpDialog(false);
      setBpFormData({
        systolic: '',
        diastolic: '',
        pulse: '',
        method: 'Manual',
        date_recorded: '',
        time_recorded: new Date().toTimeString().slice(0, 5),
        notes: ''
      });
      await reloadBabyProfile();
    } catch (error) {
      console.error('Error adding blood pressure:', error);
      toast.error('Failed to add blood pressure reading');
    }
  };

  const handleEditBloodPressure = async (e) => {
    e.preventDefault();
    if (!editingBloodPressure) return;

    try {
      await axios.put(`${API}/api/blood-pressure/${editingBloodPressure.id}`, bpFormData);
      toast.success('Blood pressure reading updated successfully');
      setShowEditBpDialog(false);
      setEditingBloodPressure(null);
      await reloadBabyProfile();
    } catch (error) {
      console.error('Error updating blood pressure:', error);
      toast.error('Failed to update blood pressure reading');
    }
  };

  const handleDeleteBloodPressure = async (bpId) => {
    if (!window.confirm('Are you sure you want to delete this blood pressure reading?')) return;

    try {
      await axios.delete(`${API}/api/blood-pressure/${bpId}`);
      toast.success('Blood pressure reading deleted successfully');
      await reloadBabyProfile();
    } catch (error) {
      console.error('Error deleting blood pressure:', error);
      toast.error('Failed to delete blood pressure reading');
    }
  };

  const openEditBloodPressure = (bp) => {
    setEditingBloodPressure(bp);
    setBpFormData({
      systolic: bp.systolic,
      diastolic: bp.diastolic,
      pulse: bp.pulse || '',
      method: bp.method,
      date_recorded: bp.date_recorded,
      time_recorded: bp.time_recorded,
      notes: bp.notes || ''
    });
    setShowEditBpDialog(true);
  };

  // Date input handlers for temperature
  const handleTempDateInput = (e) => {
    let value = e.target.value;
    value = value.replace(/[^\d/]/g, '');
    if (value.length === 2 && !value.includes('/')) {
      value = value + '/';
    } else if (value.length === 5 && value.split('/').length === 2) {
      value = value + '/';
    }
    if (value.length > 10) {
      value = value.substring(0, 10);
    }
    setTempFormData(prev => ({ ...prev, date_recorded: value }));
  };

  const handleTempDateKeyDown = (e) => {
    if (e.key === 'Backspace') {
      const value = tempFormData.date_recorded;
      if (value.length === 3 || value.length === 6) {
        e.preventDefault();
        setTempFormData(prev => ({ ...prev, date_recorded: value.slice(0, -1) }));
      }
    }
  };

  // Date input handlers for blood pressure
  const handleBpDateInput = (e) => {
    let value = e.target.value;
    value = value.replace(/[^\d/]/g, '');
    if (value.length === 2 && !value.includes('/')) {
      value = value + '/';
    } else if (value.length === 5 && value.split('/').length === 2) {
      value = value + '/';
    }
    if (value.length > 10) {
      value = value.substring(0, 10);
    }
    setBpFormData(prev => ({ ...prev, date_recorded: value }));
  };

  const handleBpDateKeyDown = (e) => {
    if (e.key === 'Backspace') {
      const value = bpFormData.date_recorded;
      if (value.length === 3 || value.length === 6) {
        e.preventDefault();
        setBpFormData(prev => ({ ...prev, date_recorded: value.slice(0, -1) }));
      }
    }
  };

  // Blood Pressure input handler with automatic "/" insertion
  const handleBPInput = (e) => {
    let value = e.target.value.replace(/[^\d/]/g, ''); // Remove non-digits and non-slash
    
    // Auto-insert "/" after 3 digits (systolic)
    if (value.length === 3 && !value.includes('/')) {
      value = value + '/';
    }
    
    // Limit to format: XXX/XXX (max 7 characters)
    if (value.length > 7) {
      value = value.substring(0, 7);
    }
    
    setSessionForm(prev => ({ ...prev, blood_pressure: value }));
  };

  // Blood Pressure keydown handler for backspace on "/"
  const handleBPKeyDown = (e) => {
    if (e.key === 'Backspace') {
      const value = sessionForm.blood_pressure;
      const cursorPosition = e.target.selectionStart;
      
      // If cursor is right after "/" (e.g., "120/|"), delete the "/" and the digit before it
      if (cursorPosition > 0 && value[cursorPosition - 1] === '/') {
        e.preventDefault();
        // Remove the "/" and the digit before it
        const newValue = value.substring(0, cursorPosition - 2) + value.substring(cursorPosition);
        setSessionForm(prev => ({ ...prev, blood_pressure: newValue }));
      }
    }
  };

  // Allergies CRUD
  const handleAddAllergy = async () => {
    if (!selectedBaby || !selectedBaby.id) {
      console.error('selectedBaby is missing:', selectedBaby);
      toast.error('Baby data not loaded. Please try again.');
      return;
    }
    
    if (!allergyForm.allergen || !allergyForm.reaction || !allergyForm.severity || !allergyForm.discovered_date) {
      const missing = [];
      if (!allergyForm.allergen) missing.push('allergen');
      if (!allergyForm.reaction) missing.push('reaction');
      if (!allergyForm.severity) missing.push('severity');
      if (!allergyForm.discovered_date) missing.push('discovered_date');
      console.error('Missing required fields:', missing);
      toast.error('Please fill in all required fields');
      return;
    }
    
    try {
      setSubmitting(true);
      
      // Send date in DD/MM/YYYY format as-is (matching baby app behavior)
      const allergyData = {
        baby_id: selectedBaby.id,
        allergen: allergyForm.allergen,
        reaction: allergyForm.reaction,
        severity: allergyForm.severity,
        discovered_date: allergyForm.discovered_date,  // Send as DD/MM/YYYY without conversion
        notes: allergyForm.notes || null,
        updated_by: user?.id,
        updated_by_doctor: getFormattedDoctorName(user),
        updated_by_hospital: hospital?.name || undefined
      };
      
      console.log('=== ALLERGY SUBMISSION DEBUG ===');
      console.log('selectedBaby.id:', selectedBaby.id);
      console.log('allergyForm:', allergyForm);
      console.log('allergyData being sent:', allergyData);
      console.log('API endpoint:', `${API}/api/allergies`);
      console.log('================================');
      
      await axios.post(`${API}/api/allergies`, allergyData);
      toast.success('Allergy added successfully');
      setShowAddAllergy(false);
      setAllergyForm({ allergen: '', reaction: '', severity: 'Mild', discovered_date: '' });
      await reloadBabyProfile();
    } catch (error) {
      console.error('Error adding allergy:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      toast.error(error.response?.data?.detail?.[0]?.msg || error.response?.data?.detail || 'Failed to add allergy');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditAllergy = async () => {
    if (!allergyForm.allergen) {
      toast.error('Please enter allergen');
      return;
    }
    
    try {
      setSubmitting(true);
      
      await axios.put(`${API}/api/allergies/${editingAllergy.id}`, {
        baby_id: selectedBaby.id,
        allergen: allergyForm.allergen,
        reaction: allergyForm.reaction,
        severity: allergyForm.severity,
        discovered_date: allergyForm.discovered_date,
        notes: allergyForm.notes || null,
        updated_by: user?.id,
        updated_by_doctor: getFormattedDoctorName(user),
        updated_by_hospital: hospital?.name || undefined
      });
      toast.success('Allergy updated successfully');
      setShowAddAllergy(false);
      setEditingAllergy(null);
      setAllergyForm({ allergen: '', reaction: '', severity: 'Mild', discovered_date: '', notes: '' });
      await reloadBabyProfile();
    } catch (error) {
      console.error('Error updating allergy:', error);
      toast.error('Failed to update allergy');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteAllergy = async (allergyId) => {
    if (!window.confirm('Are you sure you want to delete this allergy?')) return;
    
    try {
      await axios.delete(`${API}/api/allergies/${allergyId}`);
      toast.success('Allergy deleted successfully');
      await reloadBabyProfile();
    } catch (error) {
      console.error('Error deleting allergy:', error);
      toast.error('Failed to delete allergy');
    }
  };

  const openEditAllergy = (allergy) => {
    setEditingAllergy(allergy);
    setAllergyForm({
      allergen: allergy.allergen,
      reaction: allergy.reaction || '',
      severity: allergy.severity || 'Mild',
      discovered_date: allergy.discovered_date || '',
      notes: allergy.notes || ''
    });
    setShowAddAllergy(true);
  };

  // Vaccinations CRUD
  const handleAddVaccination = async () => {
    if (!vaccinationForm.vaccine_name) {
      toast.error('Please enter vaccine name');
      return;
    }
    
    try {
      setSubmitting(true);
      await axios.post(`${API}/api/vaccinations`, {
        baby_id: selectedBaby.id,
        ...vaccinationForm,
        updated_by: user?.id,
        updated_by_doctor: getFormattedDoctorName(user),
        updated_by_hospital: hospital?.name || undefined
      });
      toast.success('Vaccination added successfully');
      setShowAddVaccination(false);
      setVaccinationForm({ vaccine_name: '', date_administered: '', batch_number: '', administered_by: '' });
      await reloadBabyProfile();
    } catch (error) {
      console.error('Error adding vaccination:', error);
      toast.error('Failed to add vaccination');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditVaccination = async () => {
    if (!vaccinationForm.vaccine_name || !vaccinationForm.given_date) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    // Convert date from native input (YYYY-MM-DD) to DD/MM/YYYY if needed
    let givenDate = vaccinationForm.given_date;
    const isoDateRegex = /^(\d{4})-(\d{2})-(\d{2})$/;
    const ddmmyyyyRegex = /^(\d{2})\/(\d{2})\/(\d{4})$/;
    
    if (isoDateRegex.test(givenDate)) {
      // Convert YYYY-MM-DD to DD/MM/YYYY
      const [year, month, day] = givenDate.split('-');
      givenDate = `${day}/${month}/${year}`;
    } else if (!ddmmyyyyRegex.test(givenDate)) {
      toast.error('Please select a valid date');
      return;
    }
    
    try {
      setSubmitting(true);
      
      // Add doctor information to the vaccination record
      const updateData = {
        ...vaccinationForm,
        given_date: givenDate,
        status: 'given',
        administered_by: user ? getFormattedDoctorName(user) : 'Doctor',
        doctor_id: user.id,
        hospital_id: user.hospital_id,
        baby_id: editingVaccination.baby_id // Include baby_id for subsequent vaccine update
      };
      
      await axios.put(`${API}/api/vaccinations/${editingVaccination.id}`, updateData);
      toast.success('Vaccination marked as given successfully');
      setShowAddVaccination(false);
      setEditingVaccination(null);
      setVaccinationForm({ vaccine_name: '', given_date: '', trade_name: '', batch_number: '', expiry_date: '' });
      await reloadBabyProfile();
    } catch (error) {
      console.error('Error updating vaccination:', error);
      toast.error('Failed to update vaccination');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteVaccination = async (vaccinationId) => {
    if (!window.confirm('Are you sure you want to delete this vaccination?')) return;
    
    try {
      await axios.delete(`${API}/api/vaccinations/${vaccinationId}`);
      toast.success('Vaccination deleted successfully');
      await reloadBabyProfile();
    } catch (error) {
      console.error('Error deleting vaccination:', error);
      toast.error('Failed to delete vaccination');
    }
  };

  const openEditVaccination = (vaccination) => {
    setEditingVaccination(vaccination);
    setVaccinationForm({
      vaccine_name: vaccination.vaccine_name,
      date_administered: vaccination.date_administered || '',
      batch_number: vaccination.batch_number || '',
      administered_by: vaccination.administered_by || ''
    });
  };

  // Growth Tracker CRUD
  const handleAddGrowth = async () => {
    if (!selectedBaby || !selectedBaby.id) {
      console.error('selectedBaby is missing:', selectedBaby);
      toast.error('Baby data not loaded. Please try again.');
      return;
    }
    
    if (!growthForm.date || !growthForm.weight || !growthForm.height) {
      const missing = [];
      if (!growthForm.date) missing.push('date');
      if (!growthForm.weight) missing.push('weight');
      if (!growthForm.height) missing.push('height');
      console.error('Missing required fields:', missing);
      toast.error('Please fill in all required fields');
      return;
    }
    
    try {
      setSubmitting(true);
      
      // Ensure we have user data
      if (!user) {
        toast.error('User information not loaded. Please refresh and try again.');
        return;
      }
      
      // Send date in DD/MM/YYYY format as-is (matching baby app date input behavior)
      const doctorInfo = getFormattedDoctorName(user);
      const hospitalInfo = hospital?.name || undefined;
      
      const growthData = {
        baby_id: selectedBaby.id,
        date_recorded: growthForm.date,  // Send as DD/MM/YYYY without conversion
        height: parseFloat(growthForm.height),
        weight: parseFloat(growthForm.weight),
        head_circumference: growthForm.head_circumference ? parseFloat(growthForm.head_circumference) : null,
        notes: growthForm.notes || null,
        updated_by: user?.id,
        updated_by_doctor: doctorInfo,
        updated_by_hospital: hospitalInfo,
        created_by: user?.id  // Store doctor_id for delete authorization
      };
      
      console.log('=== GROWTH RECORD SUBMISSION DEBUG ===');
      console.log('user:', user);
      console.log('doctorInfo:', doctorInfo);
      console.log('hospitalInfo:', hospitalInfo);
      console.log('selectedBaby.id:', selectedBaby.id);
      console.log('growthForm:', growthForm);
      console.log('growthData being sent:', growthData);
      console.log('API endpoint:', `${API}/api/growth-records`);
      console.log('======================================');
      
      await axios.post(`${API}/api/growth-records`, growthData);
      
      // Assess growth against WHO standards
      if (selectedBaby?.date_of_birth) {
        const assessment = assessGrowthRecord(
          growthForm.weight,
          growthForm.height,
          growthForm.head_circumference,
          selectedBaby.date_of_birth,
          selectedBaby.sex,
          growthForm.date || new Date()
        );
        
        if (assessment && assessment.statuses && assessment.statuses.length > 0) {
          const status = assessment.statuses[0];
          const isNormal = status.type === 'normal';
          
          // Build assessment message
          let assessmentMsg = `Growth Assessment: ${status.label}`;
          if (assessment.weight?.percentile) {
            assessmentMsg += `\nWeight: ${assessment.weight.percentile.percentile}th percentile`;
          }
          if (assessment.height?.percentile) {
            assessmentMsg += `\nHeight: ${assessment.height.percentile.percentile}th percentile`;
          }
          
          if (isNormal) {
            toast.success('Growth record added successfully', {
              description: assessmentMsg,
              duration: 5000
            });
          } else {
            toast.warning('Growth record added', {
              description: assessmentMsg,
              duration: 7000
            });
          }
        } else {
          toast.success('Growth record added successfully');
        }
      } else {
        toast.success('Growth record added successfully');
      }
      
      setShowAddGrowth(false);
      setGrowthForm({ date: '', weight: '', height: '', head_circumference: '', notes: '' });
      await reloadBabyProfile();
    } catch (error) {
      console.error('Error adding growth record:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      toast.error(error.response?.data?.detail?.[0]?.msg || error.response?.data?.detail || 'Failed to add growth record');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditGrowth = async () => {
    if (!growthForm.weight || !growthForm.height || !growthForm.head_circumference) {
      toast.error('Please enter weight, height, and head circumference');
      return;
    }
    
    try {
      setSubmitting(true);
      await axios.put(`${API}/api/growth-records/${editingGrowth.id}`, growthForm);
      
      // Assess growth against WHO standards
      if (selectedBaby?.date_of_birth) {
        const assessment = assessGrowthRecord(
          growthForm.weight,
          growthForm.height,
          growthForm.head_circumference,
          selectedBaby.date_of_birth,
          selectedBaby.sex,
          growthForm.date || new Date()
        );
        
        if (assessment && assessment.statuses && assessment.statuses.length > 0) {
          const status = assessment.statuses[0];
          const isNormal = status.type === 'normal';
          
          // Build assessment message
          let assessmentMsg = `Growth Assessment: ${status.label}`;
          if (assessment.weight?.percentile) {
            assessmentMsg += `\nWeight: ${assessment.weight.percentile.percentile}th percentile`;
          }
          if (assessment.height?.percentile) {
            assessmentMsg += `\nHeight: ${assessment.height.percentile.percentile}th percentile`;
          }
          
          if (isNormal) {
            toast.success('Growth record updated successfully', {
              description: assessmentMsg,
              duration: 5000
            });
          } else {
            toast.warning('Growth record updated', {
              description: assessmentMsg,
              duration: 7000
            });
          }
        } else {
          toast.success('Growth record updated successfully');
        }
      } else {
        toast.success('Growth record updated successfully');
      }
      
      setEditingGrowth(null);
      setGrowthForm({ date: '', weight: '', height: '', head_circumference: '', notes: '' });
      await reloadBabyProfile();
    } catch (error) {
      console.error('Error updating growth record:', error);
      toast.error('Failed to update growth record');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteGrowth = async (growthId) => {
    if (!window.confirm('Are you sure you want to delete this growth record?')) return;
    
    try {
      await axios.delete(`${API}/api/growth-records/${growthId}`);
      toast.success('Growth record deleted successfully');
      await reloadBabyProfile();
    } catch (error) {
      console.error('Error deleting growth record:', error);
      toast.error('Failed to delete growth record');
    }
  };

  const openEditGrowth = (growth) => {
    setEditingGrowth(growth);
    setGrowthForm({
      date: growth.date || '',
      weight: growth.weight || '',
      height: growth.height || '',
      head_circumference: growth.head_circumference || '',
      notes: growth.notes || ''
    });
  };

  // Birth Record Edit
  const openEditBirthRecord = () => {
    try {
      console.log('Update Birth Record clicked', { babyDetails, selectedBaby });
      // Pre-fill form with existing birth record data from birthRecord or baby object
      const br = babyDetails?.birthRecord || {};
      const baby = babyDetails?.baby || {};
      
      setBirthRecordForm({
        sex: br.sex || baby.gender || '',
        mode_of_delivery: br.mode_of_delivery || baby.mode_of_delivery || '',
        birth_weight: br.birth_weight || baby.birth_weight || '',
        birth_length: br.length || baby.birth_length || baby.length || '',
        birth_head_circumference: br.head_circumference || baby.birth_head_circumference || baby.head_circumference || '',
        blood_group: br.blood_group || baby.blood_type || '',
        mother_name: br.mother_name || baby.mother_name || '',
        father_name: br.father_name || baby.father_name || '',
        family_history: br.family_history || '',
        allergies: br.allergies || '',
        remarks: br.remarks || '',
        antenatal_history: br.antenatal_history || '',
        birth_history: br.birth_history || ''
      });
      setShowEditBirthRecord(true);
    } catch (error) {
      console.error('Error in openEditBirthRecord:', error);
      toast.error('Failed to open form. Please try again.');
    }
  };

  const handleUpdateBirthRecord = async () => {
    if (!selectedBaby || !babyDetails?.baby) {
      toast.error('Baby data not loaded. Please try again.');
      return;
    }
    
    if (!birthRecordForm.sex || !birthRecordForm.mode_of_delivery || !birthRecordForm.birth_weight) {
      toast.error('Please fill sex, mode of delivery, and birth weight');
      return;
    }
    
    try {
      setSubmitting(true);
      
      // Prepare birth record data with all fields
      const birthRecordData = {
        sex: birthRecordForm.sex,
        mode_of_delivery: birthRecordForm.mode_of_delivery,
        birth_weight: parseFloat(birthRecordForm.birth_weight),
        length: birthRecordForm.birth_length ? parseFloat(birthRecordForm.birth_length) : null,
        head_circumference: birthRecordForm.birth_head_circumference ? parseFloat(birthRecordForm.birth_head_circumference) : null,
        blood_group: birthRecordForm.blood_group || null,
        mother_name: birthRecordForm.mother_name || null,
        father_name: birthRecordForm.father_name || null,
        family_history: birthRecordForm.family_history || null,
        allergies: birthRecordForm.allergies || null,
        remarks: birthRecordForm.remarks || null,
        antenatal_history: birthRecordForm.antenatal_history || null,
        birth_history: birthRecordForm.birth_history || null
      };
      
      if (babyDetails?.birthRecord?.id) {
        // Update existing birth record
        await axios.put(`${API}/api/birth-record/${babyDetails.birthRecord.id}`, birthRecordData);
      } else {
        // Create new birth record
        birthRecordData.baby_id = selectedBaby.id;
        await axios.post(`${API}/api/birth-record`, birthRecordData);
      }
      
      // Also update baby's mother_name and father_name to keep in sync
      await axios.put(`${API}/api/babies/${selectedBaby.id}`, {
        mother_name: birthRecordForm.mother_name || null,
        father_name: birthRecordForm.father_name || null,
        blood_type: birthRecordForm.blood_group || null
      });
      
      toast.success('Birth record updated successfully');
      setShowEditBirthRecord(false);
      await reloadBabyProfile();
    } catch (error) {
      console.error('Error updating birth record:', error);
      toast.error('Failed to update birth record');
    } finally {
      setSubmitting(false);
    }
  };

  // Template auto-fill handlers
  const handleChiefComplaintSelect = (chiefComplaintTemplateId) => {
    if (!chiefComplaintTemplateId) return;
    
    const chiefComplaintTemplate = chiefComplaintTemplates.find(c => c.id === chiefComplaintTemplateId);
    if (!chiefComplaintTemplate) return;
    
    // Check if there are sub-complaints
    if (chiefComplaintTemplate.sub_complaints && chiefComplaintTemplate.sub_complaints.length > 0) {
      // Store the selected chief complaint and show sub-complaints
      setSelectedChiefComplaint(chiefComplaintTemplate);
      setShowSubComplaints(true);
      return; // Don't add the main complaint yet, wait for sub-complaint selection
    }
    
    // No sub-complaints, ADD chief complaint to existing text directly
    const currentComplaints = sessionForm.chief_complaints || '';
    const newComplaint = chiefComplaintTemplate.complaint_name;
    const updatedComplaints = currentComplaints 
      ? `${currentComplaints}, ${newComplaint}`
      : newComplaint;
    
    setSessionForm(prev => ({ ...prev, chief_complaints: updatedComplaints }));
    
    // Auto-fill associated medications
    if (chiefComplaintTemplate.associated_medications && chiefComplaintTemplate.associated_medications.length > 0) {
      const newMedications = chiefComplaintTemplate.associated_medications
        .map(medId => {
          if (medId.startsWith('common-')) {
            const commonId = medId.replace('common-', '');
            const commonMed = commonMedications.find(m => m.id === commonId);
            if (!commonMed) return null;
            
            return {
              name: commonMed.medication_name,
              generic_name: commonMed.generic_name || '',
              treatment_for: commonMed.treatment_for || '',
              time_of_day: commonMed.time_of_day || [],
              frequency: commonMed.frequency || '',
              timing: commonMed.default_timing || 'Before Food',
              duration: commonMed.default_duration || '7 days',
              medication_type: commonMed.medication_type ? commonMed.medication_type.toLowerCase() : ''
            };
          } else {
            const medTemplate = medicationTemplates.find(m => m.id === medId);
            if (!medTemplate) return null;
            
            return {
              name: medTemplate.medication_name,
              generic_name: medTemplate.generic_name || '',
              treatment_for: medTemplate.treatment_for || '',
              time_of_day: medTemplate.time_of_day || [],
              frequency: medTemplate.frequency || '',
              timing: medTemplate.timing,
              duration: medTemplate.duration,
              medication_type: medTemplate.medication_type ? medTemplate.medication_type.toLowerCase() : ''
            };
          }
        })
        .filter(Boolean);
      
      if (newMedications.length > 0) {
        const firstMed = sessionForm.medications[0];
        if (!firstMed.name.trim()) {
          setSessionForm(prev => ({ ...prev, medications: newMedications }));
        } else {
          setSessionForm(prev => ({ ...prev, medications: [...prev.medications, ...newMedications] }));
        }
        toast.success(`${newMedications.length} medication(s) auto-filled from chief complaint`);
      }
    }
    
    // Auto-fill associated tests
    if (chiefComplaintTemplate.associated_tests && chiefComplaintTemplate.associated_tests.length > 0) {
      const currentTests = sessionForm.tests || '';
      const testsList = currentTests ? currentTests.split(',').map(t => t.trim()) : [];
      
      chiefComplaintTemplate.associated_tests.forEach(testId => {
        const testTemplate = testTemplates.find(t => t.id === testId);
        if (testTemplate && !testsList.includes(testTemplate.test_name)) {
          testsList.push(testTemplate.test_name);
        }
      });
      
      setSessionForm(prev => ({ ...prev, tests: testsList.join(', ') }));
      toast.success(`${chiefComplaintTemplate.associated_tests.length} test(s) auto-filled from chief complaint`);
    }
  };

  // Handle sub-complaint selection
  const handleSubComplaintSelect = (subComplaint) => {
    if (!selectedChiefComplaint || !subComplaint) return;
    
    // Extract name, medications, and sub-sub complaints (support both old string format and new object format)
    const subComplaintName = typeof subComplaint === 'string' ? subComplaint : subComplaint.name;
    const subComplaintMeds = typeof subComplaint === 'object' ? (subComplaint.associated_medications || []) : [];
    const subSubComplaints = typeof subComplaint === 'object' ? (subComplaint.sub_sub_complaints || []) : [];
    
    // Check if there are sub-sub complaints
    if (subSubComplaints.length > 0) {
      // Store the selected sub-complaint and show sub-sub complaints
      setSelectedSubComplaint(subComplaint);
      setShowSubSubComplaints(true);
      return; // Don't add the complaint yet, wait for sub-sub complaint selection
    }
    
    // No sub-sub complaints, combine chief complaint with sub-complaint
    const currentComplaints = sessionForm.chief_complaints || '';
    const fullComplaint = `${selectedChiefComplaint.complaint_name}: ${subComplaintName}`;
    const updatedComplaints = currentComplaints 
      ? `${currentComplaints}, ${fullComplaint}`
      : fullComplaint;
    
    setSessionForm(prev => ({ ...prev, chief_complaints: updatedComplaints }));
    
    // Auto-fill associated medications from SUB-COMPLAINT FIRST (priority)
    let medicationsToAdd = [];
    if (subComplaintMeds.length > 0) {
      medicationsToAdd = subComplaintMeds;
      toast.success(`Using medications from sub-complaint`);
    } else if (selectedChiefComplaint.associated_medications && selectedChiefComplaint.associated_medications.length > 0) {
      // Fallback to main complaint medications if sub-complaint has none
      medicationsToAdd = selectedChiefComplaint.associated_medications;
    }
    
    if (medicationsToAdd.length > 0) {
      const newMedications = medicationsToAdd
        .map(medId => {
          if (medId.startsWith('common-')) {
            const commonId = medId.replace('common-', '');
            const commonMed = commonMedications.find(m => m.id === commonId);
            if (!commonMed) return null;
            
            return {
              name: commonMed.medication_name,
              generic_name: commonMed.generic_name || '',
              treatment_for: commonMed.treatment_for || '',
              time_of_day: commonMed.time_of_day || [],
              frequency: commonMed.frequency || '',
              timing: commonMed.default_timing || 'Before Food',
              duration: commonMed.default_duration || '7 days',
              medication_type: commonMed.medication_type ? commonMed.medication_type.toLowerCase() : ''
            };
          } else {
            const medTemplate = medicationTemplates.find(m => m.id === medId);
            if (!medTemplate) return null;
            
            return {
              name: medTemplate.medication_name,
              generic_name: medTemplate.generic_name || '',
              treatment_for: medTemplate.treatment_for || '',
              time_of_day: medTemplate.time_of_day || [],
              frequency: medTemplate.frequency || '',
              timing: medTemplate.timing,
              duration: medTemplate.duration,
              medication_type: medTemplate.medication_type ? medTemplate.medication_type.toLowerCase() : ''
            };
          }
        })
        .filter(Boolean);
      
      if (newMedications.length > 0) {
        const firstMed = sessionForm.medications[0];
        if (!firstMed.name.trim()) {
          setSessionForm(prev => ({ ...prev, medications: newMedications }));
        } else {
          setSessionForm(prev => ({ ...prev, medications: [...prev.medications, ...newMedications] }));
        }
        toast.success(`${newMedications.length} medication(s) auto-filled`);
      }
    }
    
    // Auto-fill associated tests
    if (selectedChiefComplaint.associated_tests && selectedChiefComplaint.associated_tests.length > 0) {
      const currentTests = sessionForm.tests || '';
      const testsList = currentTests ? currentTests.split(',').map(t => t.trim()) : [];
      
      selectedChiefComplaint.associated_tests.forEach(testId => {
        const testTemplate = testTemplates.find(t => t.id === testId);
        if (testTemplate && !testsList.includes(testTemplate.test_name)) {
          testsList.push(testTemplate.test_name);
        }
      });
      
      setSessionForm(prev => ({ ...prev, tests: testsList.join(', ') }));
      toast.success(`${selectedChiefComplaint.associated_tests.length} test(s) auto-filled`);
    }
    
    // Close the sub-complaints dialog
    setShowSubComplaints(false);
    setSelectedChiefComplaint(null);
  };

  const handleSubSubComplaintSelect = (subSubComplaint) => {
    if (!selectedChiefComplaint || !selectedSubComplaint || !subSubComplaint) return;
    
    // Extract name, medications, and Level 4 complaints
    const subComplaintName = typeof selectedSubComplaint === 'string' ? selectedSubComplaint : selectedSubComplaint.name;
    const subSubComplaintName = typeof subSubComplaint === 'string' ? subSubComplaint : subSubComplaint.name;
    const subSubComplaintMeds = typeof subSubComplaint === 'object' ? (subSubComplaint.associated_medications || []) : [];
    const level4Complaints = typeof subSubComplaint === 'object' ? (subSubComplaint.level_4_complaints || []) : [];
    
    // Check if there are Level 4 complaints
    if (level4Complaints.length > 0) {
      // Store the selected sub-sub complaint and show Level 4 complaints
      setSelectedSubSubComplaint(subSubComplaint);
      setShowLevel4Complaints(true);
      return; // Don't add the complaint yet, wait for Level 4 selection
    }
    
    // No Level 4 complaints, combine chief complaint with sub-complaint and sub-sub complaint
    const currentComplaints = sessionForm.chief_complaints || '';
    const fullComplaint = `${selectedChiefComplaint.complaint_name}: ${subComplaintName} - ${subSubComplaintName}`;
    const updatedComplaints = currentComplaints 
      ? `${currentComplaints}, ${fullComplaint}`
      : fullComplaint;
    
    setSessionForm(prev => ({ ...prev, chief_complaints: updatedComplaints }));
    
    // Auto-fill associated medications from SUB-SUB-COMPLAINT (as per requirement 3.a - only from selected sub-sub)
    if (subSubComplaintMeds.length > 0) {
      const newMedications = subSubComplaintMeds
        .map(medId => {
          if (medId.startsWith('common-')) {
            const commonId = medId.replace('common-', '');
            const commonMed = commonMedications.find(m => m.id === commonId);
            if (!commonMed) return null;
            
            return {
              name: commonMed.medication_name,
              generic_name: commonMed.generic_name || '',
              treatment_for: commonMed.treatment_for || '',
              time_of_day: commonMed.time_of_day || [],
              frequency: commonMed.frequency || '',
              timing: commonMed.default_timing || 'Before Food',
              duration: commonMed.default_duration || '7 days',
              medication_type: commonMed.medication_type ? commonMed.medication_type.toLowerCase() : ''
            };
          } else {
            const medTemplate = medicationTemplates.find(m => m.id === medId);
            if (!medTemplate) return null;
            
            return {
              name: medTemplate.medication_name,
              generic_name: medTemplate.generic_name || '',
              treatment_for: medTemplate.treatment_for || '',
              time_of_day: medTemplate.time_of_day || [],
              frequency: medTemplate.frequency || '',
              timing: medTemplate.timing,
              duration: medTemplate.duration,
              medication_type: medTemplate.medication_type ? medTemplate.medication_type.toLowerCase() : ''
            };
          }
        })
        .filter(Boolean);
      
      if (newMedications.length > 0) {
        const firstMed = sessionForm.medications[0];
        if (!firstMed.name.trim()) {
          setSessionForm(prev => ({ ...prev, medications: newMedications }));
        } else {
          setSessionForm(prev => ({ ...prev, medications: [...prev.medications, ...newMedications] }));
        }
        toast.success(`${newMedications.length} medication(s) auto-filled from Level 3`);
      }
    }
    
    // Auto-fill associated tests from main chief complaint
    if (selectedChiefComplaint.associated_tests && selectedChiefComplaint.associated_tests.length > 0) {
      const currentTests = sessionForm.tests || '';
      const testsList = currentTests ? currentTests.split(',').map(t => t.trim()) : [];
      
      selectedChiefComplaint.associated_tests.forEach(testId => {
        const testTemplate = testTemplates.find(t => t.id === testId);
        if (testTemplate && !testsList.includes(testTemplate.test_name)) {
          testsList.push(testTemplate.test_name);
        }
      });
      
      setSessionForm(prev => ({ ...prev, tests: testsList.join(', ') }));
      toast.success(`${selectedChiefComplaint.associated_tests.length} test(s) auto-filled`);
    }
    
    // Close all dialogs
    setShowSubSubComplaints(false);
    setShowSubComplaints(false);
    setSelectedSubComplaint(null);
    setSelectedChiefComplaint(null);
  };

  const handleLevel4ComplaintSelect = (level4Complaint) => {
    if (!selectedChiefComplaint || !selectedSubComplaint || !selectedSubSubComplaint || !level4Complaint) return;
    
    // Extract names and check for Level 5
    const subComplaintName = typeof selectedSubComplaint === 'string' ? selectedSubComplaint : selectedSubComplaint.name;
    const subSubComplaintName = typeof selectedSubSubComplaint === 'string' ? selectedSubSubComplaint : selectedSubSubComplaint.name;
    const level4ComplaintName = typeof level4Complaint === 'string' ? level4Complaint : level4Complaint.name;
    const level4ComplaintMeds = typeof level4Complaint === 'object' ? (level4Complaint.associated_medications || []) : [];
    const level5Complaints = typeof level4Complaint === 'object' ? (level4Complaint.level_5_complaints || []) : [];
    
    // Check if there are Level 5 complaints
    if (level5Complaints.length > 0) {
      setSelectedLevel4Complaint(level4Complaint);
      setShowLevel5Complaints(true);
      return;
    }
    
    // No Level 5, finalize with Level 4
    const currentComplaints = sessionForm.chief_complaints || '';
    const fullComplaint = `${selectedChiefComplaint.complaint_name}: ${subComplaintName} - ${subSubComplaintName} - ${level4ComplaintName}`;
    const updatedComplaints = currentComplaints 
      ? `${currentComplaints}, ${fullComplaint}`
      : fullComplaint;
    
    setSessionForm(prev => ({ ...prev, chief_complaints: updatedComplaints }));
    
    // Auto-fill medications from Level 4 ONLY
    if (level4ComplaintMeds.length > 0) {
      const newMedications = level4ComplaintMeds
        .map(medId => {
          if (medId.startsWith('common-')) {
            const commonId = medId.replace('common-', '');
            const commonMed = commonMedications.find(m => m.id === commonId);
            if (!commonMed) return null;
            
            return {
              name: commonMed.medication_name,
              generic_name: commonMed.generic_name || '',
              treatment_for: commonMed.treatment_for || '',
              time_of_day: commonMed.time_of_day || [],
              frequency: commonMed.frequency || '',
              timing: commonMed.default_timing || 'Before Food',
              duration: commonMed.default_duration || '7 days',
              medication_type: commonMed.medication_type ? commonMed.medication_type.toLowerCase() : ''
            };
          } else {
            const medTemplate = medicationTemplates.find(m => m.id === medId);
            if (!medTemplate) return null;
            
            return {
              name: medTemplate.medication_name,
              generic_name: medTemplate.generic_name || '',
              treatment_for: medTemplate.treatment_for || '',
              time_of_day: medTemplate.time_of_day || [],
              frequency: medTemplate.frequency || '',
              timing: medTemplate.timing,
              duration: medTemplate.duration,
              medication_type: medTemplate.medication_type ? medTemplate.medication_type.toLowerCase() : ''
            };
          }
        })
        .filter(Boolean);
      
      if (newMedications.length > 0) {
        const firstMed = sessionForm.medications[0];
        if (!firstMed.name.trim()) {
          setSessionForm(prev => ({ ...prev, medications: newMedications }));
        } else {
          setSessionForm(prev => ({ ...prev, medications: [...prev.medications, ...newMedications] }));
        }
        toast.success(`${newMedications.length} medication(s) auto-filled from Level 4`);
      }
    }
    
    // Close all dialogs
    setShowLevel4Complaints(false);
    setShowSubSubComplaints(false);
    setShowSubComplaints(false);
    setSelectedSubSubComplaint(null);
    setSelectedSubComplaint(null);
    setSelectedChiefComplaint(null);
  };

  const handleLevel5ComplaintSelect = (level5Complaint) => {
    if (!selectedLevel4Complaint || !level5Complaint) return;
    
    const subComplaintName = typeof selectedSubComplaint === 'string' ? selectedSubComplaint : selectedSubComplaint.name;
    const subSubComplaintName = typeof selectedSubSubComplaint === 'string' ? selectedSubSubComplaint : selectedSubSubComplaint.name;
    const level4ComplaintName = typeof selectedLevel4Complaint === 'string' ? selectedLevel4Complaint : selectedLevel4Complaint.name;
    const level5ComplaintName = typeof level5Complaint === 'string' ? level5Complaint : level5Complaint.name;
    const level5ComplaintMeds = typeof level5Complaint === 'object' ? (level5Complaint.associated_medications || []) : [];
    const level6Complaints = typeof level5Complaint === 'object' ? (level5Complaint.level_6_complaints || []) : [];
    
    // Check if there are Level 6 complaints
    if (level6Complaints.length > 0) {
      setSelectedLevel5Complaint(level5Complaint);
      setShowLevel6Complaints(true);
      return;
    }
    
    // No Level 6, finalize with Level 5
    const currentComplaints = sessionForm.chief_complaints || '';
    const fullComplaint = `${selectedChiefComplaint.complaint_name}: ${subComplaintName} - ${subSubComplaintName} - ${level4ComplaintName} - ${level5ComplaintName}`;
    const updatedComplaints = currentComplaints 
      ? `${currentComplaints}, ${fullComplaint}`
      : fullComplaint;
    
    setSessionForm(prev => ({ ...prev, chief_complaints: updatedComplaints }));
    
    // Auto-fill medications from Level 5 ONLY
    if (level5ComplaintMeds.length > 0) {
      const newMedications = level5ComplaintMeds
        .map(medId => {
          if (medId.startsWith('common-')) {
            const commonId = medId.replace('common-', '');
            const commonMed = commonMedications.find(m => m.id === commonId);
            if (!commonMed) return null;
            
            return {
              name: commonMed.medication_name,
              generic_name: commonMed.generic_name || '',
              treatment_for: commonMed.treatment_for || '',
              time_of_day: commonMed.time_of_day || [],
              frequency: commonMed.frequency || '',
              timing: commonMed.default_timing || 'Before Food',
              duration: commonMed.default_duration || '7 days',
              medication_type: commonMed.medication_type ? commonMed.medication_type.toLowerCase() : ''
            };
          } else {
            const medTemplate = medicationTemplates.find(m => m.id === medId);
            if (!medTemplate) return null;
            
            return {
              name: medTemplate.medication_name,
              generic_name: medTemplate.generic_name || '',
              treatment_for: medTemplate.treatment_for || '',
              time_of_day: medTemplate.time_of_day || [],
              frequency: medTemplate.frequency || '',
              timing: medTemplate.timing,
              duration: medTemplate.duration,
              medication_type: medTemplate.medication_type ? medTemplate.medication_type.toLowerCase() : ''
            };
          }
        })
        .filter(Boolean);
      
      if (newMedications.length > 0) {
        const firstMed = sessionForm.medications[0];
        if (!firstMed.name.trim()) {
          setSessionForm(prev => ({ ...prev, medications: newMedications }));
        } else {
          setSessionForm(prev => ({ ...prev, medications: [...prev.medications, ...newMedications] }));
        }
        toast.success(`${newMedications.length} medication(s) auto-filled from Level 5`);
      }
    }
    
    // Close all dialogs
    setShowLevel5Complaints(false);
    setShowLevel4Complaints(false);
    setShowSubSubComplaints(false);
    setShowSubComplaints(false);
    setSelectedLevel5Complaint(null);
    setSelectedLevel4Complaint(null);
    setSelectedSubSubComplaint(null);
    setSelectedSubComplaint(null);
    setSelectedChiefComplaint(null);
  };

  const handleLevel6ComplaintSelect = (level6Complaint) => {
    if (!selectedLevel5Complaint || !level6Complaint) return;
    
    const subComplaintName = typeof selectedSubComplaint === 'string' ? selectedSubComplaint : selectedSubComplaint.name;
    const subSubComplaintName = typeof selectedSubSubComplaint === 'string' ? selectedSubSubComplaint : selectedSubSubComplaint.name;
    const level4ComplaintName = typeof selectedLevel4Complaint === 'string' ? selectedLevel4Complaint : selectedLevel4Complaint.name;
    const level5ComplaintName = typeof selectedLevel5Complaint === 'string' ? selectedLevel5Complaint : selectedLevel5Complaint.name;
    const level6ComplaintName = typeof level6Complaint === 'string' ? level6Complaint : level6Complaint.name;
    const level6ComplaintMeds = typeof level6Complaint === 'object' ? (level6Complaint.associated_medications || []) : [];
    
    // Finalize with Level 6 (deepest level)
    const currentComplaints = sessionForm.chief_complaints || '';
    const fullComplaint = `${selectedChiefComplaint.complaint_name}: ${subComplaintName} - ${subSubComplaintName} - ${level4ComplaintName} - ${level5ComplaintName} - ${level6ComplaintName}`;
    const updatedComplaints = currentComplaints 
      ? `${currentComplaints}, ${fullComplaint}`
      : fullComplaint;
    
    setSessionForm(prev => ({ ...prev, chief_complaints: updatedComplaints }));
    
    // Auto-fill medications from Level 6 ONLY
    if (level6ComplaintMeds.length > 0) {
      const newMedications = level6ComplaintMeds
        .map(medId => {
          if (medId.startsWith('common-')) {
            const commonId = medId.replace('common-', '');
            const commonMed = commonMedications.find(m => m.id === commonId);
            if (!commonMed) return null;
            
            return {
              name: commonMed.medication_name,
              generic_name: commonMed.generic_name || '',
              treatment_for: commonMed.treatment_for || '',
              time_of_day: commonMed.time_of_day || [],
              frequency: commonMed.frequency || '',
              timing: commonMed.default_timing || 'Before Food',
              duration: commonMed.default_duration || '7 days',
              medication_type: commonMed.medication_type ? commonMed.medication_type.toLowerCase() : ''
            };
          } else {
            const medTemplate = medicationTemplates.find(m => m.id === medId);
            if (!medTemplate) return null;
            
            return {
              name: medTemplate.medication_name,
              generic_name: medTemplate.generic_name || '',
              treatment_for: medTemplate.treatment_for || '',
              time_of_day: medTemplate.time_of_day || [],
              frequency: medTemplate.frequency || '',
              timing: medTemplate.timing,
              duration: medTemplate.duration,
              medication_type: medTemplate.medication_type ? medTemplate.medication_type.toLowerCase() : ''
            };
          }
        })
        .filter(Boolean);
      
      if (newMedications.length > 0) {
        const firstMed = sessionForm.medications[0];
        if (!firstMed.name.trim()) {
          setSessionForm(prev => ({ ...prev, medications: newMedications }));
        } else {
          setSessionForm(prev => ({ ...prev, medications: [...prev.medications, ...newMedications] }));
        }
        toast.success(`${newMedications.length} medication(s) auto-filled from Level 6`);
      }
    }
    
    // Close all dialogs
    setShowLevel6Complaints(false);
    setShowLevel5Complaints(false);
    setShowLevel4Complaints(false);
    setShowSubSubComplaints(false);
    setShowSubComplaints(false);
    setSelectedLevel5Complaint(null);
    setSelectedLevel4Complaint(null);
    setSelectedSubSubComplaint(null);
    setSelectedSubComplaint(null);
    setSelectedChiefComplaint(null);
  };

  const handleTestSelect = (testTemplateId) => {
    if (!testTemplateId) return;
    
    const testTemplate = testTemplates.find(t => t.id === testTemplateId);
    if (!testTemplate) return;
    
    // ADD test to existing tests with comma separator
    setSessionForm(prev => {
      const currentTests = prev.tests.trim();
      const newTest = testTemplate.test_name;
      
      // If tests field is empty, just add the new test
      if (!currentTests) {
        return { ...prev, tests: newTest };
      }
      
      // Check if this test already exists
      const testsList = currentTests.split(',').map(t => t.trim());
      if (testsList.includes(newTest)) {
        toast.info('Test already added');
        return prev;
      }
      
      // Add new test with comma separator
      return { ...prev, tests: `${currentTests}, ${newTest}` };
    });
    
    toast.success('Test added');
  };

  const handleAdditionalNotesSelect = (notesTemplateId) => {
    if (!notesTemplateId) return;
    
    const notesTemplate = additionalNotesTemplates.find(n => n.id === notesTemplateId);
    if (!notesTemplate) return;
    
    // ADD note to existing notes
    setSessionForm(prev => {
      const currentNotes = prev.notes?.trim() || '';
      const newNote = notesTemplate.note_content || notesTemplate.note_title;
      
      // If notes field is empty, just add the new note
      if (!currentNotes) {
        return { ...prev, notes: newNote };
      }
      
      // Add new note with line break
      return { ...prev, notes: `${currentNotes}\n${newNote}` };
    });
    
    toast.success('Note added from template');
  };

  const handleDiagnosisSelect = (diagnosisTemplateId) => {
    console.log('Diagnosis selected:', diagnosisTemplateId, 'Templates:', diagnosisTemplates);
    if (!diagnosisTemplateId) return;
    
    const diagnosisTemplate = diagnosisTemplates.find(d => d.id === diagnosisTemplateId);
    if (!diagnosisTemplate) return;
    
    setSelectedDiagnosis(diagnosisTemplate);
    
    // Check if diagnosis has Level 2 options
    if (diagnosisTemplate.level_2_diagnosis && diagnosisTemplate.level_2_diagnosis.length > 0) {
      setShowLevel2Diagnosis(true);
    } else {
      // No Level 2, add diagnosis directly
      addDiagnosisToSession(diagnosisTemplate.diagnosis_name);
      
      // Auto-fill associated notes if available
      if (diagnosisTemplate.associated_notes && diagnosisTemplate.associated_notes.length > 0) {
        autoFillDiagnosisNotes(diagnosisTemplate.associated_notes);
      }
    }
  };

  const addDiagnosisToSession = (diagnosisText) => {
    setSessionForm(prev => {
      const currentDiagnosis = prev.diagnosis?.trim() || '';
      
      // If diagnosis field is empty, just add the new diagnosis
      if (!currentDiagnosis) {
        return { ...prev, diagnosis: diagnosisText };
      }
      
      // Check if this diagnosis already exists
      const diagnosisList = currentDiagnosis.split(',').map(d => d.trim());
      if (diagnosisList.includes(diagnosisText)) {
        toast.info('Diagnosis already added');
        return prev;
      }
      
      // Add new diagnosis with comma separator
      return { ...prev, diagnosis: `${currentDiagnosis}, ${diagnosisText}` };
    });
    
    toast.success('Diagnosis added');
  };

  const autoFillDiagnosisNotes = (noteIds) => {
    // Get note titles from IDs
    const noteTitles = noteIds
      .map(noteId => {
        const noteTemplate = additionalNotesTemplates.find(n => n.id === noteId);
        return noteTemplate ? noteTemplate.note_title : null;
      })
      .filter(Boolean);
    
    if (noteTitles.length === 0) return;
    
    setSessionForm(prev => {
      const currentNotes = prev.notes?.trim() || '';
      const newNotes = noteTitles.join(', ');
      
      if (!currentNotes) {
        return { ...prev, notes: newNotes };
      }
      
      // Add new notes with comma separator
      return { ...prev, notes: `${currentNotes}, ${newNotes}` };
    });
    
    toast.success(`${noteTitles.length} advice item(s) auto-filled`);
  };

  const handleAddLevel2Diagnosis = (level2) => {
    if (!selectedDiagnosis || !level2) return;
    
    const level2Name = typeof level2 === 'string' ? level2 : level2.name;
    const level2Notes = typeof level2 === 'object' ? level2.associated_notes : [];
    const level3List = typeof level2 === 'object' ? level2.level_3_diagnosis || [] : [];
    
    setSelectedLevel2Diagnosis(level2);
    
    // Check if Level 2 has Level 3 options
    if (level3List.length > 0) {
      setShowLevel3Diagnosis(true);
      setShowLevel2Diagnosis(false);
    } else {
      // No Level 3, add diagnosis with Level 2
      const fullDiagnosis = `${selectedDiagnosis.diagnosis_name}: ${level2Name}`;
      addDiagnosisToSession(fullDiagnosis);
      
      // Auto-fill ONLY Level 2 advice (not main diagnosis advice)
      if (level2Notes && level2Notes.length > 0) {
        autoFillDiagnosisNotes(level2Notes);
      }
      
      // Reset states
      setShowLevel2Diagnosis(false);
      setSelectedDiagnosis(null);
      setSelectedLevel2Diagnosis(null);
    }
  };

  const handleAddLevel3Diagnosis = (level3) => {
    if (!selectedDiagnosis || !selectedLevel2Diagnosis || !level3) return;
    
    const level2Name = typeof selectedLevel2Diagnosis === 'string' ? selectedLevel2Diagnosis : selectedLevel2Diagnosis.name;
    const level3Name = typeof level3 === 'string' ? level3 : level3.name;
    const level3Notes = typeof level3 === 'object' ? level3.associated_notes : [];
    
    // Build full diagnosis string
    const fullDiagnosis = `${selectedDiagnosis.diagnosis_name}: ${level2Name} - ${level3Name}`;
    addDiagnosisToSession(fullDiagnosis);
    
    // Auto-fill ONLY Level 3 advice (not main or Level 2 advice)
    if (level3Notes && level3Notes.length > 0) {
      autoFillDiagnosisNotes(level3Notes);
    }
    
    // Reset states
    setShowLevel3Diagnosis(false);
    setShowLevel2Diagnosis(false);
    setSelectedDiagnosis(null);
    setSelectedLevel2Diagnosis(null);
    setSelectedLevel3Diagnosis(null);
  };

  const handleOpenTestSelection = () => {
    console.log('Test Selection button clicked', { testTemplates, sessionForm });
    
    // Reset search query
    setTestSearchQuery('');
    
    // Parse current tests from the textarea
    const currentTestsList = sessionForm.tests
      .split(',')
      .map(t => t.trim())
      .filter(t => t.length > 0);
    
    // Match with test template IDs
    const selectedIds = testTemplates
      .filter(template => currentTestsList.includes(template.test_name))
      .map(template => template.id);
    
    setSelectedTests(selectedIds);
    setShowTestSelectionDialog(true);
    console.log('Setting showTestSelectionDialog to true', { selectedIds });
  };

  const handleApplySelectedTests = () => {
    // Get test names from selected IDs
    const testNames = testTemplates
      .filter(template => selectedTests.includes(template.id))
      .map(template => template.test_name);
    
    // Update the correct field based on target
    if (testFieldTarget === 'additional_tests') {
      setSessionForm(prev => ({ ...prev, additional_tests: testNames.join(', ') }));
    } else {
      setSessionForm(prev => ({ ...prev, tests: testNames.join(', ') }));
    }
    
    setShowTestSelectionDialog(false);
    toast.success('Tests applied');
  };

  const handleToggleTest = (testId) => {
    setSelectedTests(prev => {
      if (prev.includes(testId)) {
        return prev.filter(id => id !== testId);
      } else {
        return [...prev, testId];
      }
    });
  };
  

  // Handle category-level selection
  const handleToggleCategory = (categoryTests) => {
    const categoryTestIds = categoryTests.map(t => t.id);
    const allSelected = categoryTestIds.every(id => selectedTests.includes(id));
    
    if (allSelected) {
      // Unselect all tests in this category
      setSelectedTests(prev => prev.filter(id => !categoryTestIds.includes(id)));
    } else {
      // Select all tests in this category
      setSelectedTests(prev => [...new Set([...prev, ...categoryTestIds])]);
    }
  };

  const handleMedicationSourceSelect = (index, sourceId) => {
    if (!sourceId) return;
    
    let newMedication;
    
    // Check if it's a common medication (prefixed with 'common-')
    if (sourceId.startsWith('common-')) {
      const commonMedId = sourceId.replace('common-', '');
      const commonMed = commonMedications.find(m => m.id === commonMedId);
      if (!commonMed) return;
      
      newMedication = {
        name: commonMed.medication_name,
        generic_name: commonMed.generic_name || '',
        treatment_for: commonMed.treatment_for || '',
        time_of_day: commonMed.time_of_day || [],
        frequency: commonMed.frequency || '',
        timing: commonMed.default_timing || 'Before Food',
        duration: commonMed.default_duration || '7 days',
        medication_type: commonMed.medication_type ? commonMed.medication_type.toLowerCase() : ''
      };
      toast.success('Common medication added');
    } else {
      // It's a personal medication template
      const medTemplate = medicationTemplates.find(m => m.id === sourceId);
      if (!medTemplate) return;
      
      newMedication = {
        name: medTemplate.medication_name,
        generic_name: medTemplate.generic_name || '',
        treatment_for: medTemplate.treatment_for || '',
        time_of_day: medTemplate.time_of_day || [],
        frequency: medTemplate.frequency || '',
        timing: medTemplate.timing,
        duration: medTemplate.duration,
        medication_type: medTemplate.medication_type ? medTemplate.medication_type.toLowerCase() : ''
      };
      toast.success('Medication added from template');
    }
    
    // Check if current medication at index is empty, if yes replace it, otherwise add new
    const currentMed = sessionForm.medications[index];
    if (!currentMed.name.trim()) {
      // Current medication is empty, replace it
      const updatedMedications = [...sessionForm.medications];
      updatedMedications[index] = newMedication;
      setSessionForm(prev => ({ ...prev, medications: updatedMedications }));
    } else {
      // Current medication has content, add new one after current
      const updatedMedications = [...sessionForm.medications];
      updatedMedications.splice(index + 1, 0, newMedication);
      setSessionForm(prev => ({ ...prev, medications: updatedMedications }));
    }
  };

  // Handler for combined medication search input with template selection
  const handleMedicationSearchSelect = (index, sourceId, templateData, isTemplate, isCommon) => {
    if (!sourceId || !templateData) return;
    
    const newMedication = {
      name: templateData.medication_name,
      generic_name: templateData.generic_name || '',
      treatment_for: templateData.treatment_for || '',
      time_of_day: templateData.time_of_day || [],
      frequency: templateData.frequency || '',
      timing: isCommon ? (templateData.default_timing || 'Before Food') : (templateData.timing || 'Before Food'),
      duration: isCommon ? (templateData.default_duration || '') : (templateData.duration || ''),
      medication_type: templateData.medication_type ? templateData.medication_type.toLowerCase() : ''
    };
    
    // Update the medication at the current index
    const updatedMedications = [...sessionForm.medications];
    updatedMedications[index] = newMedication;
    setSessionForm(prev => ({ ...prev, medications: updatedMedications }));
    
    if (isTemplate) {
      toast.success('Applied from your template');
    } else if (isCommon) {
      toast.success('Applied from common medication');
    }
  };

  // Medical Sessions CRUD
  const handleAddSession = async () => {
    // Helper function to sort time_of_day in M A E N sequence
    const sortTimeOfDay = (times) => {
      const order = ['Morning', 'Afternoon', 'Evening', 'Night'];
      return times.sort((a, b) => order.indexOf(a) - order.indexOf(b));
    };

    if (!selectedBaby || !selectedBaby.id) {
      toast.error('Baby data not loaded. Please try again.');
      return;
    }
    
    if (!sessionForm.doctor_name) {
      toast.error('Please enter doctor name');
      return;
    }
    
    try {
      setSubmitting(true);
      
      // Convert date from DD/MM/YYYY to YYYY-MM-DD for backend
      const isoDate = sessionForm.session_date.includes('/') 
        ? convertToISODate(sessionForm.session_date)
        : sessionForm.session_date;
      
      // Prepare medication as formatted text with time_of_day and duration
      const medicationText = sessionForm.medications
        .filter(med => med.name)
        .map(med => {
          let medStr = med.name;
          // Add medication type if available
          if (med.medication_type) medStr += ` [Type: ${med.medication_type}]`;
          // Add generic name if available
          if (med.generic_name) medStr += ` [Generic: ${med.generic_name}]`;
          // Add treatment for if available
          if (med.treatment_for) medStr += ` [For: ${med.treatment_for}]`;
          if (med.timing) medStr += ` (${med.timing})`;
          // Include both time_of_day and frequency if present
          if (med.time_of_day && med.time_of_day.length > 0) {
            const sortedTimes = sortTimeOfDay([...med.time_of_day]);
            medStr += ` - ${sortedTimes.join(', ')}`;
          }
          if (med.frequency) {
            medStr += ` [Frequency: ${med.frequency}]`;
          }
          if (med.duration) medStr += ` for ${med.duration}`;
          return medStr;
        })
        .join(' | '); // Use pipe separator to avoid confusion with commas in time_of_day
      
      const sessionData = {
        baby_id: selectedBaby.id,
        session_date: isoDate,
        doctor_name: sessionForm.doctor_name || getFormattedDoctorName(user) || 'Dr. Doctor',
        hospital_name: localStorage.getItem('currentHospital') ? JSON.parse(localStorage.getItem('currentHospital')).name : null,
        chief_complaints: sessionForm.chief_complaints || null,
        diagnosis: sessionForm.diagnosis || null,
        assessment: sessionForm.assessment || null,
        medication: medicationText || null,
        temperature: sessionForm.temperature ? parseFloat(sessionForm.temperature) : null,
        notes: sessionForm.notes || null,
        follow_up_of: sessionForm.follow_up_of || followUpFromSession?.id || null,
        documents: uploadedReports.map(report => ({
          id: report.id,
          filename: report.originalName,
          file_path: report.fileUrl,
          file_size: report.fileSize,
          upload_date: report.uploadDate,
          analysis_file_url: report.analysis_file_url || null,
          abnormal_count: report.abnormal_count || 0,
          borderline_count: report.borderline_count || 0,
          report_date: report.reportDate || null,
          report_type: report.report_type || 'Blood Test'
        })),  // Include uploaded reports with analysis data
        updated_by: user?.id,  // Use doctor ID for ownership tracking
        // Vitals
        height: sessionForm.height ? parseFloat(sessionForm.height) : null,
        weight: sessionForm.weight ? parseFloat(sessionForm.weight) : null,
        blood_pressure: sessionForm.blood_pressure || null,
        pulse: sessionForm.pulse ? parseInt(sessionForm.pulse) : null,
        respiratory_rate: sessionForm.respiratory_rate ? parseInt(sessionForm.respiratory_rate) : null,
        spo2: sessionForm.spo2 ? parseInt(sessionForm.spo2) : null,
        // Tests
        tests: sessionForm.tests || null,
        additional_tests: sessionForm.additional_tests || null,
        // Next follow-up
        next_followup: sessionForm.next_followup || null,
        // AI Clinical Assessment
        ai_assessment_id: sessionForm.ai_assessment_id || null,
        ai_soap_notes: sessionForm.ai_soap_notes || null
      };
      
      console.log('Creating session with updated_by:', user?.id, 'Full user:', user);
      console.log('📝 Session data ai_soap_notes:', sessionData.ai_soap_notes ? 'PRESENT' : 'MISSING', sessionData.ai_soap_notes);
      
      const response = await axios.post(`${API}/api/medical-sessions`, sessionData);
      
      console.log('Session created, response:', response.data);
      
      // Sync vitals to respective modules
      const syncPromises = [];
      
      // 1. Sync Height/Weight to Growth Tracker
      if (sessionForm.height || sessionForm.weight) {
        const growthData = {
          baby_id: selectedBaby.id,
          date: isoDate,
          height: sessionForm.height ? parseFloat(sessionForm.height) : null,
          weight: sessionForm.weight ? parseFloat(sessionForm.weight) : null,
          head_circumference: null,
          updated_by_doctor: user?.id,
          updated_by_hospital: localStorage.getItem('currentHospital') 
            ? JSON.parse(localStorage.getItem('currentHospital')).id 
            : null
        };
        
        syncPromises.push(
          axios.post(`${API}/api/growth`, growthData)
            .then(() => console.log('✅ Growth record synced'))
            .catch(err => console.error('Growth sync error:', err))
        );
      }
      
      // 2. Sync Temperature to Medical Conditions
      if (sessionForm.temperature) {
        const tempData = {
          baby_id: selectedBaby.id,
          temperature: parseFloat(sessionForm.temperature),
          date_recorded: isoDate,
          updated_by_doctor: user?.id,
          updated_by_hospital: localStorage.getItem('currentHospital') 
            ? JSON.parse(localStorage.getItem('currentHospital')).id 
            : null
        };
        
        syncPromises.push(
          axios.post(`${API}/api/temperature`, tempData)
            .then(() => console.log('✅ Temperature record synced'))
            .catch(err => console.error('Temperature sync error:', err))
        );
      }
      
      // 3. Sync Blood Pressure to Medical Conditions
      if (sessionForm.blood_pressure && sessionForm.blood_pressure.includes('/')) {
        const [systolic, diastolic] = sessionForm.blood_pressure.split('/').map(v => parseInt(v.trim()));
        if (systolic && diastolic) {
          const bpData = {
            baby_id: selectedBaby.id,
            systolic: systolic,
            diastolic: diastolic,
            date_recorded: isoDate,
            updated_by_doctor: user?.id,
            updated_by_hospital: localStorage.getItem('currentHospital') 
              ? JSON.parse(localStorage.getItem('currentHospital')).id 
              : null
          };
        
          syncPromises.push(
            axios.post(`${API}/api/blood-pressure`, bpData)
              .then(() => console.log('✅ Blood pressure record synced'))
              .catch(err => console.error('BP sync error:', err))
          );
        }
      }
      
      // Execute all sync operations
      if (syncPromises.length > 0) {
        await Promise.all(syncPromises);
        console.log('✅ All vitals synced successfully');
      }
      
      // Mark appointment as completed if session is complete
      if (sessionForm.diagnosis) {
        const todayAppointment = appointments.find(apt => 
          apt.baby_id === selectedBaby.id && 
          normalizeDateForComparison(apt.appointment_date) === new Date().toISOString().split('T')[0] &&
          apt.status !== 'completed'
        );
        
        if (todayAppointment) {
          await axios.put(`${API}/api/appointments/${todayAppointment.id}`, {
            status: 'completed'
          });
          // Reload appointments
          await loadData(user.id);
        }
      }
      
      toast.success('Medical session added successfully');
      setShowAddSession(false);
      setFollowUpFromSession(null);
      setSelectedFiles([]);
      setUploadedReports([]); // Clear uploaded reports
      setSessionForm({ 
        session_date: '', 
        doctor_name: '', 
        chief_complaints: '',
        diagnosis: '', 
        assessment: '',
        medications: [{ name: '', timing: 'Before Food', duration: '', time_of_day: [], frequency: '', medication_type: '', treatment_for: '' }],
        temperature: '',
        notes: '',
        follow_up_of: '',
        next_followup: '',
        pulse: '',
        respiratory_rate: '',
        spo2: '',
        blood_pressure: '',
        height: '',
        weight: '',
        tests: '',
        additional_tests: ''
      });
      await reloadBabyProfile();
    } catch (error) {
      console.error('Error adding session:', error);
      toast.error('Failed to add medical session');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditSession = async () => {
    // Helper function to sort time_of_day in M A E N sequence
    const sortTimeOfDay = (times) => {
      const order = ['Morning', 'Afternoon', 'Evening', 'Night'];
      return times.sort((a, b) => order.indexOf(a) - order.indexOf(b));
    };

    if (!selectedBaby || !selectedBaby.id) {
      toast.error('Baby data not loaded. Please try again.');
      return;
    }
    
    if (!sessionForm.doctor_name) {
      toast.error('Please enter doctor name');
      return;
    }
    
    try {
      setSubmitting(true);
      
      // Convert date from DD/MM/YYYY to YYYY-MM-DD for backend
      const isoDate = sessionForm.session_date.includes('/') 
        ? convertToISODate(sessionForm.session_date)
        : sessionForm.session_date;
      
      // Prepare medication as formatted text with time_of_day and duration
      const medicationText = sessionForm.medications
        .filter(med => med.name)
        .map(med => {
          let medStr = med.name;
          // Add medication type if available
          if (med.medication_type) medStr += ` [Type: ${med.medication_type}]`;
          // Add generic name if available
          if (med.generic_name) medStr += ` [Generic: ${med.generic_name}]`;
          // Add treatment for if available
          if (med.treatment_for) medStr += ` [For: ${med.treatment_for}]`;
          if (med.timing) medStr += ` (${med.timing})`;
          // Include both time_of_day and frequency if present
          if (med.time_of_day && med.time_of_day.length > 0) {
            const sortedTimes = sortTimeOfDay([...med.time_of_day]);
            medStr += ` - ${sortedTimes.join(', ')}`;
          }
          if (med.frequency) {
            medStr += ` [Frequency: ${med.frequency}]`;
          }
          if (med.duration) medStr += ` for ${med.duration}`;
          return medStr;
        })
        .join(' | '); // Use pipe separator to avoid confusion with commas in time_of_day
      
      const sessionData = {
        baby_id: selectedBaby.id,
        session_date: isoDate,
        doctor_name: sessionForm.doctor_name || getFormattedDoctorName(user) || 'Dr. Doctor',
        hospital_name: localStorage.getItem('currentHospital') ? JSON.parse(localStorage.getItem('currentHospital')).name : null,
        chief_complaints: sessionForm.chief_complaints || null,
        diagnosis: sessionForm.diagnosis || null,
        assessment: sessionForm.assessment || null,
        medication: medicationText || null,
        temperature: sessionForm.temperature ? parseFloat(sessionForm.temperature) : null,
        notes: sessionForm.notes || null,
        follow_up_of: sessionForm.follow_up_of || null,
        documents: uploadedReports.map(report => ({
          id: report.id,
          filename: report.originalName,
          file_path: report.fileUrl,
          file_size: report.fileSize,
          upload_date: report.uploadDate,
          analysis_file_url: report.analysis_file_url || null,
          abnormal_count: report.abnormal_count || 0,
          borderline_count: report.borderline_count || 0,
          report_date: report.reportDate || null,
          report_type: report.report_type || 'Blood Test'
        })),  // Include uploaded reports with analysis data
        updated_by: user?.id,  // Keep the original doctor ID
        // Vitals
        height: sessionForm.height ? parseFloat(sessionForm.height) : null,
        weight: sessionForm.weight ? parseFloat(sessionForm.weight) : null,
        blood_pressure: sessionForm.blood_pressure || null,
        pulse: sessionForm.pulse ? parseInt(sessionForm.pulse) : null,
        respiratory_rate: sessionForm.respiratory_rate ? parseInt(sessionForm.respiratory_rate) : null,
        spo2: sessionForm.spo2 ? parseInt(sessionForm.spo2) : null,
        // Tests
        tests: sessionForm.tests || null,
        additional_tests: sessionForm.additional_tests || null,
        // AI Clinical Assessment - preserve existing or add new
        ai_assessment_id: sessionForm.ai_assessment_id || editingSession.ai_assessment_id || null,
        ai_soap_notes: sessionForm.ai_soap_notes || editingSession.ai_soap_notes || null
      };
      
      await axios.put(`${API}/api/medical-sessions/${editingSession.id}`, sessionData);
      
      // Sync vitals to respective modules (same as add session)
      const syncPromises = [];
      
      // 1. Sync Height/Weight to Growth Tracker
      if (sessionForm.height || sessionForm.weight) {
        const growthData = {
          baby_id: selectedBaby.id,
          date: isoDate,
          height: sessionForm.height ? parseFloat(sessionForm.height) : null,
          weight: sessionForm.weight ? parseFloat(sessionForm.weight) : null,
          head_circumference: null,
          updated_by_doctor: user?.id,
          updated_by_hospital: localStorage.getItem('currentHospital') 
            ? JSON.parse(localStorage.getItem('currentHospital')).id 
            : null
        };
        
        syncPromises.push(
          axios.post(`${API}/api/growth`, growthData)
            .then(() => console.log('✅ Growth record synced'))
            .catch(err => console.error('Growth sync error:', err))
        );
      }
      
      // 2. Sync Temperature to Medical Conditions
      if (sessionForm.temperature) {
        const tempData = {
          baby_id: selectedBaby.id,
          temperature: parseFloat(sessionForm.temperature),
          date_recorded: isoDate,
          updated_by_doctor: user?.id,
          updated_by_hospital: localStorage.getItem('currentHospital') 
            ? JSON.parse(localStorage.getItem('currentHospital')).id 
            : null
        };
        
        syncPromises.push(
          axios.post(`${API}/api/temperature`, tempData)
            .then(() => console.log('✅ Temperature record synced'))
            .catch(err => console.error('Temperature sync error:', err))
        );
      }
      
      // 3. Sync Blood Pressure to Medical Conditions
      if (sessionForm.blood_pressure && sessionForm.blood_pressure.includes('/')) {
        const [systolic, diastolic] = sessionForm.blood_pressure.split('/').map(v => parseInt(v.trim()));
        if (systolic && diastolic) {
          const bpData = {
            baby_id: selectedBaby.id,
            systolic: systolic,
            diastolic: diastolic,
            date_recorded: isoDate,
            updated_by_doctor: user?.id,
            updated_by_hospital: localStorage.getItem('currentHospital') 
              ? JSON.parse(localStorage.getItem('currentHospital')).id 
              : null
          };
        
          syncPromises.push(
            axios.post(`${API}/api/blood-pressure`, bpData)
              .then(() => console.log('✅ Blood pressure record synced'))
              .catch(err => console.error('BP sync error:', err))
          );
        }
      }
      
      // Execute all sync operations
      if (syncPromises.length > 0) {
        await Promise.all(syncPromises);
        console.log('✅ All vitals synced successfully');
      }
      
      toast.success('Medical session updated successfully');
      setEditingSession(null);
      setShowAddSession(false);
      setSelectedFiles([]);
      setUploadedReports([]); // Clear uploaded reports
      setSessionForm({ 
        session_date: '', 
        doctor_name: '', 
        chief_complaints: '',
        diagnosis: '', 
        assessment: '',
        medications: [{ name: '', timing: 'Before Food', duration: '', time_of_day: [], frequency: '', medication_type: '', treatment_for: '' }],
        temperature: '',
        notes: '',
        follow_up_of: '',
        pulse: '',
        respiratory_rate: '',
        spo2: '',
        blood_pressure: '',
        height: '',
        weight: '',
        tests: '',
        additional_tests: ''
      });
      await reloadBabyProfile();
    } catch (error) {
      console.error('Error updating session:', error);
      toast.error('Failed to update medical session');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteSession = async (sessionId) => {
    if (!window.confirm('Are you sure you want to delete this medical session?')) return;
    
    try {
      // Send doctor_id to allow doctors to delete their own sessions
      await axios.delete(`${API}/api/medical-sessions/${sessionId}?doctor_id=${user.id}`);
      toast.success('Medical session deleted successfully');
      // Reload baby profile to update the session list
      try {
        await reloadBabyProfile();
      } catch (reloadError) {
        // Ignore reload errors, deletion was successful
        console.log('Session deleted successfully, minor reload error:', reloadError);
      }
    } catch (error) {
      console.error('Error deleting session:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete medical session');
    }
  };

  // Vaccination handlers
  const handleUpdateVaccinationSchedule = async () => {
    if (!selectedBaby || !selectedBaby.id) {
      toast.error('Baby data not loaded. Please try again.');
      return;
    }

    if (!window.confirm('This will add any missing vaccinations to the schedule. Existing vaccinations will be preserved. Continue?')) {
      return;
    }

    try {
      toast.info('Updating vaccination schedule...');
      const response = await axios.post(`${API}/api/vaccinations/reinitialize/${selectedBaby.id}`);
      toast.success(response.data.message);
      // Reload baby profile to get updated vaccinations
      await reloadBabyProfile();
    } catch (error) {
      console.error('Error updating vaccination schedule:', error);
      toast.error('Failed to update vaccination schedule');
    }
  };
  
  const handleOpenAddVaccination = () => {
    setShowAddNewVaccination(true);
  };
  
  // Handler for adding new vaccination (matches Baby App)
  const handleAddNewVaccination = async (e) => {
    e.preventDefault();
    
    if (!addVaccinationForm.vaccine_name) {
      toast.error('Please enter vaccine name');
      return;
    }
    
    // Validate date format
    const dateRegex = /^\d{2}\/\d{2}\/\d{4}$/;
    if (!dateRegex.test(addVaccinationForm.given_date)) {
      toast.error('Date given must be in DD/MM/YYYY format');
      return;
    }
    
    try {
      setSubmitting(true);
      await axios.post(`${API}/api/vaccinations`, {
        baby_id: selectedBaby.id,
        vaccine_name: addVaccinationForm.vaccine_name,
        trade_name: addVaccinationForm.trade_name || undefined,
        given_date: addVaccinationForm.given_date,
        status: 'given'
      });
      
      toast.success('Vaccination added successfully!');
      setShowAddNewVaccination(false);
      setAddVaccinationForm({ vaccine_name: '', trade_name: '', given_date: '' });
      await reloadBabyProfile();
    } catch (error) {
      console.error('Error adding vaccination:', error);
      toast.error('Failed to add vaccination');
    } finally {
      setSubmitting(false);
    }
  };



  
  // Date input handlers for Add Vaccination form (matches Baby App)
  const handleAddVaccinationDateInput = (e) => {
    let value = e.target.value;
    
    // Allow backspace/delete to work naturally
    if (e.nativeEvent.inputType === 'deleteContentBackward' || e.nativeEvent.inputType === 'deleteContentForward') {
      setAddVaccinationForm(prev => ({ ...prev, given_date: value }));
      return;
    }
    
    // Remove all non-numeric characters except /
    value = value.replace(/[^\d/]/g, '');
    
    // Auto-add slashes
    if (value.length === 2 && !value.includes('/')) {
      value = value + '/';
    } else if (value.length === 5 && value.split('/').length === 2) {
      value = value + '/';
    }
    
    // Limit to DD/MM/YYYY format (10 characters)
    if (value.length > 10) {
      value = value.substring(0, 10);
    }
    
    setAddVaccinationForm(prev => ({ ...prev, given_date: value }));
  };

  const startEditSession = (session) => {
    // Only allow editing if doctor created it (already checked by button visibility)
    setEditingSession(session);
    
    // Parse medications from text format back to array
    let medicationsArray = [{ name: '', timing: 'Before Food', duration: '', time_of_day: [], frequency: '', medication_type: '', treatment_for: '' }];
    if (session.medication) {
      // Parse medication string like "Paracetamol (After Food) - Morning, Night for 7 days | Ibuprofen (Before Food) - Evening for 3 days"
      medicationsArray = session.medication.split(' | ').map(med => {
        const trimmedMed = med.trim();
        
        // Initialize medication object
        let medObj = { name: '', timing: 'Before Food', duration: '', time_of_day: [], frequency: '', medication_type: '', treatment_for: '' };
        
        let workingMed = trimmedMed;
        
        // Extract medication type if present
        const typeMatch = workingMed.match(/\[Type:\s*([^\]]+)\]/);
        if (typeMatch) {
          medObj.medication_type = typeMatch[1].trim();
          workingMed = workingMed.replace(typeMatch[0], '').trim();
        }
        
        // Extract generic name if present
        const genericMatch = workingMed.match(/\[Generic:\s*([^\]]+)\]/);
        if (genericMatch) {
          medObj.generic_name = genericMatch[1].trim();
          workingMed = workingMed.replace(genericMatch[0], '').trim();
        }
        
        // Extract treatment for if present
        const treatmentMatch = workingMed.match(/\[For:\s*([^\]]+)\]/);
        if (treatmentMatch) {
          medObj.treatment_for = treatmentMatch[1].trim();
          workingMed = workingMed.replace(treatmentMatch[0], '').trim();
        }
        
        // Extract frequency if present
        const frequencyMatch = workingMed.match(/\[Frequency:\s*([^\]]+)\]/);
        if (frequencyMatch) {
          medObj.frequency = frequencyMatch[1].trim();
          workingMed = workingMed.replace(frequencyMatch[0], '').trim();
        }
        
        // Extract duration if present (e.g., "for 7 days")
        const durationMatch = workingMed.match(/\s+for\s+(.+)$/i);
        if (durationMatch) {
          medObj.duration = durationMatch[1].trim();
          workingMed = workingMed.substring(0, durationMatch.index);
        }
        
        // Extract time_of_day if present (e.g., "- Morning, Night")
        const timeMatch = workingMed.match(/\s+-\s+(.+)$/);
        if (timeMatch) {
          const timeStr = timeMatch[1].trim();
          medObj.time_of_day = timeStr.split(',').map(t => t.trim()).filter(t => t);
          workingMed = workingMed.substring(0, timeMatch.index);
        }
        
        // Extract name and timing (e.g., "Paracetamol (After Food)")
        const nameTimingMatch = workingMed.match(/^(.+?)\s*\((.+?)\)$/);
        if (nameTimingMatch) {
          medObj.name = nameTimingMatch[1].trim();
          medObj.timing = nameTimingMatch[2].trim();
        } else {
          medObj.name = workingMed.trim();
        }
        
        return medObj;
      });
    }
    
    // Load existing uploaded reports if any
    if (session.documents && Array.isArray(session.documents)) {
      const existingReports = session.documents.map(doc => ({
        id: doc.id || Date.now() + Math.random(),
        originalName: doc.filename || doc.original_filename || 'Unknown',
        fileSize: doc.file_size || 0,
        uploadDate: doc.upload_date || new Date().toISOString(),
        fileUrl: doc.file_path || doc.fileUrl,
        analysis_file_url: doc.analysis_file_url || null,
        abnormal_count: doc.abnormal_count || 0,
        borderline_count: doc.borderline_count || 0,
        reportDate: doc.report_date || null
      }));
      setUploadedReports(existingReports);
    } else {
      setUploadedReports([]);
    }
    
    // Format date to DD/MM/YYYY if needed
    let formattedDate = session.session_date;
    if (session.session_date && session.session_date.includes('-')) {
      const [year, month, day] = session.session_date.split('-');
      formattedDate = `${day}/${month}/${year}`;
    }
    
    setSessionForm({
      session_date: formattedDate || '',
      doctor_name: session.doctor_name || '',
      chief_complaints: session.chief_complaints || session.chief_complaint || session.symptoms || '',
      diagnosis: session.diagnosis || '',
      assessment: session.assessment || '',
      medications: medicationsArray,
      temperature: session.temperature ? String(session.temperature) : '',
      notes: session.notes || '',
      follow_up_of: session.follow_up_of || '',
      height: session.height ? String(session.height) : '',
      weight: session.weight ? String(session.weight) : '',
      blood_pressure: session.blood_pressure || (session.bp_systolic && session.bp_diastolic ? `${session.bp_systolic}/${session.bp_diastolic}` : ''),
      pulse: session.pulse ? String(session.pulse) : '',
      respiratory_rate: session.respiratory_rate ? String(session.respiratory_rate) : '',
      spo2: session.spo2 ? String(session.spo2) : '',
      tests: session.tests || '',
      additional_tests: session.additional_tests || '',
      // AI Clinical Assessment
      ai_assessment_id: session.ai_assessment_id || '',
      ai_soap_notes: session.ai_soap_notes || null
    });
    
    // Load documents if any
    if (session.documents && session.documents.length > 0) {
      // You might want to handle existing documents here
      setSelectedFiles([]);
    } else {
      setSelectedFiles([]);
    }
    
    // Load previous sessions for this doctor
    if (selectedBaby?.id && user?.id) {
      loadPreviousDoctorSessions(selectedBaby.id, user.id);
    }
    
    setShowAddSession(true);
  };

  const createFollowUpSession = (session) => {
    console.log('Creating follow-up for session:', session);
    setFollowUpFromSession(session);
    const today = new Date();
    const day = String(today.getDate()).padStart(2, '0');
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const year = today.getFullYear();
    const todayFormatted = `${day}/${month}/${year}`;
    
    setSessionForm({
      session_date: todayFormatted,
      doctor_name: getFormattedDoctorName(user) || 'Dr. Doctor',
      chief_complaints: '', // Ensure empty
      diagnosis: '',
      assessment: '',
      medications: [{ name: '', timing: 'Before Food', duration: '', time_of_day: [], frequency: '', medication_type: '', treatment_for: '' }],
      temperature: '',
      duration: '',
      notes: '',
      follow_up_of: session.id,
      pulse: '',
      respiratory_rate: '',
      spo2: '',
      blood_pressure: '',
      height: '',
      weight: '',
      tests: '',
      advice: ''
    });
    setSelectedFiles([]);
    
    // Load previous sessions and set the session being followed up as selected
    if (selectedBaby?.id && user?.id) {
      loadPreviousDoctorSessions(selectedBaby.id, user.id);
      setSelectedPreviousSession(session);
    }
    
    setShowAddSession(true);
  };

  // Find previous session based on appointment follow-up date
  const findPreviousSessionByDate = (sessions, followupOfDate, doctorId) => {
    if (!sessions || !followupOfDate) return null;
    
    // Parse the followup_of_date (format: DD/MM/YYYY)
    const [day, month, year] = followupOfDate.split('/');
    const targetDate = new Date(year, month - 1, day);
    
    // Find sessions that match the date and doctor
    return sessions.find(session => {
      const sessionDate = new Date(session.session_date);
      // Compare dates (ignoring time)
      const sameDate = sessionDate.toDateString() === targetDate.toDateString();
      const sameDoctor = session.doctor_id === doctorId || session.doctor_name === getFormattedDoctorName(user);
      return sameDate && sameDoctor;
    });
  };

  // Helper to parse medication name and extract clean data
  const parseMedicationFromName = (fullName) => {
    if (!fullName) return { name: '', type: '', generic: '', treatmentFor: '', timing: '', timeOfDay: [], duration: '' };
    
    // Extract clean name (before any [Type: or [Generic: or (After Food))
    let cleanName = fullName;
    const typeMatch = fullName.match(/^(.+?)\s*\[Type:/i);
    if (typeMatch) cleanName = typeMatch[1].trim();
    else {
      const genericMatch = fullName.match(/^(.+?)\s*\[Generic:/i);
      if (genericMatch) cleanName = genericMatch[1].trim();
      else {
        const forMatch = fullName.match(/^(.+?)\s*\[For:/i);
        if (forMatch) cleanName = forMatch[1].trim();
        else {
          const timingMatch = fullName.match(/^(.+?)\s*\((Before|After)\s+Food\)/i);
          if (timingMatch) cleanName = timingMatch[1].trim();
        }
      }
    }
    
    // Extract type
    const typeExtract = fullName.match(/\[Type:\s*([^\]]+)\]/i);
    const type = typeExtract ? typeExtract[1].trim().toLowerCase() : '';
    
    // Extract generic
    const genericExtract = fullName.match(/\[Generic:\s*([^\]]+)\]/i);
    const generic = genericExtract ? genericExtract[1].trim() : '';
    
    // Extract treatment for
    const treatmentExtract = fullName.match(/\[For:\s*([^\]]+)\]/i);
    const treatmentFor = treatmentExtract ? treatmentExtract[1].trim() : '';
    
    // Extract timing from name string
    const timingExtract = fullName.match(/\((Before|After)\s+Food\)/i);
    const timing = timingExtract ? `${timingExtract[1]} Food` : '';
    
    // Extract time of day (Morning, Afternoon, Night, etc.)
    const timeOfDayMatch = fullName.match(/(?:\)\s*-\s*|\s+-\s+)((?:Morning|Afternoon|Evening|Night|Bedtime)(?:\s*,\s*(?:Morning|Afternoon|Evening|Night|Bedtime))*)/i);
    let timeOfDay = [];
    if (timeOfDayMatch) {
      timeOfDay = timeOfDayMatch[1].split(',').map(t => t.trim()).filter(t => t);
    }
    
    // Extract duration (e.g., "for 7 days", "7 days", "5 Days")
    const durationMatch = fullName.match(/(?:for\s+)?(\d+\s*(?:days?|weeks?|months?))/i);
    const duration = durationMatch ? durationMatch[1].trim() : '';
    
    return { name: cleanName, type, generic, treatmentFor, timing, timeOfDay, duration };
  };

  // Handle opening reuse medications dialog
  const handleOpenReuseMedications = () => {
    if (!followUpFromSession) {
      toast.error('No previous session selected for follow-up');
      return;
    }
    
    // Get medications from the follow-up session
    let medications = [];
    
    // Check medications array
    if (followUpFromSession.medications && Array.isArray(followUpFromSession.medications)) {
      medications = followUpFromSession.medications.map((med, idx) => {
        const rawName = typeof med === 'string' ? med : (med.name || med.medication_name || '');
        const parsed = parseMedicationFromName(rawName);
        
        return {
          id: idx,
          // Use parsed name (clean) or original if no parsing needed
          name: parsed.name || rawName,
          // Use parsed values, then fall back to object properties
          timing: parsed.timing || (typeof med === 'object' ? (med.timing || 'Before Food') : 'Before Food'),
          duration: parsed.duration || (typeof med === 'object' ? (med.duration || '') : ''),
          time_of_day: parsed.timeOfDay.length > 0 ? parsed.timeOfDay : (typeof med === 'object' ? (med.time_of_day || []) : []),
          frequency: typeof med === 'object' ? (med.frequency || '') : '',
          medication_type: parsed.type || (typeof med === 'object' ? (med.medication_type || '') : ''),
          treatment_for: parsed.treatmentFor || (typeof med === 'object' ? (med.treatment_for || '') : ''),
          generic_name: parsed.generic || (typeof med === 'object' ? (med.generic_name || '') : '')
        };
      }).filter(med => med.name);
    }
    
    // Also check medication string field (split by ' | ' for multiple medications)
    if (followUpFromSession.medication && typeof followUpFromSession.medication === 'string') {
      // Split by ' | ' (pipe separator) which is the standard format
      const medStrings = followUpFromSession.medication.split(' | ').filter(s => s.trim());
      medStrings.forEach((medStr, idx) => {
        const parsed = parseMedicationFromName(medStr.trim());
        const cleanName = parsed.name || medStr.trim();
        // Only add if not already in the list
        if (cleanName && !medications.find(m => m.name.toLowerCase() === cleanName.toLowerCase())) {
          medications.push({
            id: medications.length + idx,
            name: cleanName,
            timing: parsed.timing || 'Before Food',
            duration: parsed.duration || '',
            time_of_day: parsed.timeOfDay || [],
            frequency: '',
            medication_type: parsed.type || '',
            treatment_for: parsed.treatmentFor || '',
            generic_name: parsed.generic || ''
          });
        }
      });
    }
    
    if (medications.length === 0) {
      toast.info('No medications found in the previous session');
      return;
    }
    
    setReusableMedications(medications);
    setSelectedReuseMedications([]); // Reset selection
    setShowReuseMedicationsDialog(true);
  };

  // Handle adding selected medications to the form
  const handleAddReusedMedications = () => {
    if (selectedReuseMedications.length === 0) {
      toast.error('Please select at least one medication to reuse');
      return;
    }
    
    const medsToAdd = reusableMedications.filter(med => 
      selectedReuseMedications.includes(med.id)
    ).map(med => ({
      name: med.name,
      timing: med.timing || 'Before Food',
      duration: med.duration || '',
      time_of_day: Array.isArray(med.time_of_day) ? med.time_of_day : [],
      frequency: med.frequency || '',
      medication_type: med.medication_type || '',
      treatment_for: med.treatment_for || '',
      generic_name: med.generic_name || ''
    }));
    
    setSessionForm(prev => {
      // Check if there's only an empty medication entry
      const hasOnlyEmptyMed = prev.medications.length === 1 && !prev.medications[0].name;
      
      if (hasOnlyEmptyMed) {
        return { ...prev, medications: medsToAdd };
      } else {
        return { ...prev, medications: [...prev.medications, ...medsToAdd] };
      }
    });
    
    setShowReuseMedicationsDialog(false);
    toast.success(`Added ${medsToAdd.length} medication(s) from previous session`);
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 max-w-full overflow-x-hidden">
      {/* Header - Mobile Responsive */}
      <div className="bg-white border-b border-gray-200">
        <div className="container mx-auto px-4 md:px-6 py-2 md:py-3 max-w-full">
          <div className="flex items-center justify-between">
            <div className="flex flex-col">
              <h1 className="text-lg md:text-2xl font-bold text-gray-900" style={{fontFamily: 'Space Grotesk'}}>
                Doctor Dashboard
              </h1>
              <div className="flex items-start space-x-3 md:space-x-4 mt-1">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center mt-1">
                  <Stethoscope className="w-5 h-5 md:w-6 md:h-6 text-white" />
                </div>
                <div>
                  {hospital && (
                    <p className="text-purple-600 text-sm md:text-lg font-bold">{hospital.name}</p>
                  )}
                  <p className="text-purple-600 text-xs md:text-base font-medium">Dr. {user.name}</p>
                  <p className="text-gray-600 text-xs font-medium">
                    {user.super_specialization || user.specialization}
                  </p>
                </div>
              </div>
            </div>
            
            <Button
              onClick={handleLogout}
              variant="outline"
              className="text-gray-700 border-gray-300 hover:bg-gray-50 px-2 md:px-4"
            >
              <LogOut className="w-4 h-4 md:w-5 md:h-5 md:mr-2" />
              <span className="hidden md:inline">Logout</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Tabs - Mobile Responsive - Hide when viewing baby profile */}
      {!showBabyProfile && (
        <div className="bg-white border-b">
          <div className="container mx-auto px-4 md:px-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 py-3 sm:py-0">
              <div className="flex space-x-2 sm:space-x-4 md:space-x-8 overflow-x-auto no-scrollbar -mx-4 px-4 sm:mx-0 sm:px-0">
                <button
                  onClick={() => setActiveTab('appointments')}
                  className={`flex items-center space-x-2 py-3 sm:py-4 px-3 sm:px-2 border-b-2 transition-colors whitespace-nowrap flex-shrink-0 ${
                    activeTab === 'appointments'
                      ? 'border-purple-600 text-purple-600'
                      : 'border-transparent text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Calendar className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span className="font-medium text-xs sm:text-sm md:text-base">My Appointments</span>
                </button>
                
                <button
                  onClick={() => setActiveTab('all-patients')}
                  className={`flex items-center space-x-2 py-3 sm:py-4 px-3 sm:px-2 border-b-2 transition-colors whitespace-nowrap flex-shrink-0 ${
                    activeTab === 'all-patients'
                      ? 'border-purple-600 text-purple-600'
                      : 'border-transparent text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Users className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span className="font-medium text-xs sm:text-sm md:text-base">All Patients</span>
                </button>
                
                <button
                  onClick={() => setActiveTab('statistics')}
                  className={`flex items-center space-x-2 py-3 sm:py-4 px-3 sm:px-2 border-b-2 transition-colors whitespace-nowrap flex-shrink-0 ${
                    activeTab === 'statistics'
                      ? 'border-purple-600 text-purple-600'
                      : 'border-transparent text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <BarChart3 className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span className="font-medium text-xs sm:text-sm md:text-base">Statistics</span>
                </button>
                
                <button
                  onClick={() => setActiveTab('configuration')}
                  className={`flex items-center space-x-2 py-3 sm:py-4 px-3 sm:px-2 border-b-2 transition-colors whitespace-nowrap flex-shrink-0 ${
                    activeTab === 'configuration'
                      ? 'border-purple-600 text-purple-600'
                      : 'border-transparent text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Settings className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span className="font-medium text-xs sm:text-sm md:text-base">Configuration</span>
                </button>
                
                <button
                  onClick={() => setActiveTab('settings')}
                  className={`flex items-center space-x-2 py-3 sm:py-4 px-3 sm:px-2 border-b-2 transition-colors whitespace-nowrap flex-shrink-0 ${
                    activeTab === 'settings'
                      ? 'border-purple-600 text-purple-600'
                      : 'border-transparent text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <UserCog className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span className="font-medium text-xs sm:text-sm md:text-base">Settings</span>
                </button>
              </div>
              
              {/* Quick Appointment & Quick Lookup Buttons - Stack on mobile */}
              <div className="flex items-center gap-2 flex-shrink-0">
                {/* Quick Appointment Button - Only show if enabled */}
                {doctorSettings.quick_appointment_enabled && (
                  <Button
                    onClick={() => setShowQuickAppointment(true)}
                    className="flex items-center gap-2 w-full sm:w-auto text-sm sm:text-base bg-green-600 hover:bg-green-700"
                    data-testid="quick-appointment-btn"
                  >
                    <Zap className="w-4 h-4" />
                    <span className="hidden sm:inline">Quick Appointment</span>
                    <span className="sm:hidden">Quick</span>
                  </Button>
                )}
                
                {/* Quick Lookup Button */}
                <Button
                  onClick={() => setShowQuickLookup(true)}
                  variant="outline"
                  className="flex items-center gap-2 w-full sm:w-auto text-sm sm:text-base"
                  data-testid="quick-lookup-btn"
                >
                  <Search className="w-4 h-4" />
                  <span className="hidden sm:inline">Quick Lookup</span>
                  <span className="sm:hidden">Lookup</span>
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Content - Mobile Responsive */}
      <div className="container mx-auto px-4 md:px-6 py-6 md:py-8">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading...</p>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* All Patients Tab */}
            {activeTab === 'all-patients' && (
              <div className="space-y-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <h2 className="text-2xl font-bold text-gray-900">All Patients</h2>
                  
                  {/* Search Input */}
                  <div className="relative w-full sm:w-80">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      placeholder="Search by ID, Name or Phone..."
                      value={allPatientsSearch}
                      onChange={(e) => setAllPatientsSearch(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                    {allPatientsSearch && (
                      <button
                        onClick={() => setAllPatientsSearch('')}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Patient List */}
                {allPatientsLoading ? (
                  <div className="flex items-center justify-center py-20">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto mb-4"></div>
                      <p className="text-gray-600">Loading patients...</p>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Results Count */}
                    <p className="text-sm text-gray-500">
                      {(() => {
                        const filtered = allPatients.filter(patient => {
                          if (!allPatientsSearch) return true;
                          const search = allPatientsSearch.toLowerCase();
                          return (
                            (patient.short_id || '').toLowerCase().includes(search) ||
                            (patient.name || '').toLowerCase().includes(search) ||
                            (patient.phone || '').toLowerCase().includes(search) ||
                            (patient.parent_phone || '').toLowerCase().includes(search)
                          );
                        });
                        return `Showing ${filtered.length} of ${allPatients.length} patients`;
                      })()}
                    </p>

                    {/* Patient Table */}
                    <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-gray-50 border-b">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Visit</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {allPatients
                              .filter(patient => {
                                if (!allPatientsSearch) return true;
                                const search = allPatientsSearch.toLowerCase();
                                return (
                                  (patient.short_id || '').toLowerCase().includes(search) ||
                                  (patient.name || '').toLowerCase().includes(search) ||
                                  (patient.phone || '').toLowerCase().includes(search) ||
                                  (patient.parent_phone || '').toLowerCase().includes(search)
                                );
                              })
                              .map((patient) => (
                                <tr key={patient.id} className="hover:bg-gray-50">
                                  <td className="px-4 py-3 whitespace-nowrap">
                                    <span className="text-sm font-mono text-purple-600 bg-purple-50 px-2 py-1 rounded">
                                      {patient.short_id || patient.id?.slice(0, 8)}
                                    </span>
                                  </td>
                                  <td className="px-4 py-3 whitespace-nowrap">
                                    <div className="flex items-center">
                                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center text-white text-sm font-medium mr-3">
                                        {patient.name?.charAt(0)?.toUpperCase() || '?'}
                                      </div>
                                      <div>
                                        <p className="text-sm font-medium text-gray-900">{patient.name || 'Unknown'}</p>
                                        {patient.date_of_birth && (
                                          <p className="text-xs text-gray-500">DOB: {patient.date_of_birth}</p>
                                        )}
                                      </div>
                                    </div>
                                  </td>
                                  <td className="px-4 py-3 whitespace-nowrap">
                                    <span className="text-sm text-gray-700">
                                      {patient.phone || patient.parent_phone || 'N/A'}
                                    </span>
                                  </td>
                                  <td className="px-4 py-3 whitespace-nowrap">
                                    <span className="text-sm text-gray-600">
                                      {patient.last_visit || 'N/A'}
                                    </span>
                                  </td>
                                  <td className="px-4 py-3 whitespace-nowrap">
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={async () => {
                                        // Use loadBabyProfile to fetch complete baby data
                                        setSelectedBaby(patient);
                                        setProfileDisplayMode('full');
                                        setActiveTab('appointments');
                                        await loadBabyProfile(patient.id, 'full');
                                      }}
                                      className="text-purple-600 border-purple-200 hover:bg-purple-50"
                                    >
                                      <Eye className="w-4 h-4 mr-1" />
                                      View
                                    </Button>
                                  </td>
                                </tr>
                              ))}
                            {allPatients.filter(patient => {
                              if (!allPatientsSearch) return true;
                              const search = allPatientsSearch.toLowerCase();
                              return (
                                (patient.short_id || '').toLowerCase().includes(search) ||
                                (patient.name || '').toLowerCase().includes(search) ||
                                (patient.phone || '').toLowerCase().includes(search) ||
                                (patient.parent_phone || '').toLowerCase().includes(search)
                              );
                            }).length === 0 && (
                              <tr>
                                <td colSpan="5" className="px-4 py-8 text-center text-gray-500">
                                  {allPatientsSearch ? (
                                    <div>
                                      <Search className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                                      <p>No patients found matching "{allPatientsSearch}"</p>
                                    </div>
                                  ) : (
                                    <div>
                                      <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                                      <p>No patients with completed appointments yet</p>
                                    </div>
                                  )}
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Statistics Tab */}
            {activeTab === 'statistics' && (
              <>
                <h2 className="text-2xl font-bold text-gray-900">My Statistics</h2>
                
                {/* Statistics Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  {(() => {
                    const stats = calculateStats();
                    return (
                      <>
                        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-6 text-white">
                          <p className="text-sm opacity-90">Today's Appointments</p>
                          <p className="text-3xl font-bold mt-2">{stats.today}</p>
                        </div>
                        <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-xl p-6 text-white">
                          <p className="text-sm opacity-90">Today Completed</p>
                          <p className="text-3xl font-bold mt-2">{stats.todayCompleted}</p>
                        </div>
                        <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl p-6 text-white">
                          <p className="text-sm opacity-90">Weekly Appointments</p>
                          <p className="text-3xl font-bold mt-2">{stats.weekly}</p>
                        </div>
                        <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl p-6 text-white">
                          <p className="text-sm opacity-90">Weekly Completed</p>
                          <p className="text-3xl font-bold mt-2">{stats.weeklyCompleted}</p>
                        </div>
                      </>
                    );
                  })()}
                </div>
              </>
            )}

            {/* Configuration Tab */}
            {activeTab === 'configuration' && (
              <ConfigurationTab user={user} />
            )}

            {/* Settings Tab */}
            {activeTab === 'settings' && (
              <SettingsTab user={user} />
            )}

            {/* Appointments Tab */}
            {activeTab === 'appointments' && (
              <>
                {/* Show loading if restoring baby profile */}
                {showBabyProfile && !babyDetails ? (
                  <div className="flex items-center justify-center py-20">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto mb-4"></div>
                      <p className="text-gray-600">Loading baby profile...</p>
                    </div>
                  </div>
                ) : showBabyProfile && profileDisplayMode === 'full' && babyDetails ? (
                  <div className="space-y-6 relative">
                    {/* Back Button only - no card wrapper */}
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:justify-between">
                      <Button
                        onClick={() => {
                          setShowBabyProfile(false);
                          setSelectedBaby(null);
                          setBabyDetails(null);
                          clearBabyViewState();
                        }}
                        variant="outline"
                        className="border-gray-300 hover:bg-gray-50 whitespace-nowrap"
                      >
                        <ArrowLeft className="w-5 h-5 mr-2" />
                        Back to Appointments
                      </Button>
                      
                      {/* Quick Add Session Button */}
                      <Button
                        onClick={async () => {
                          // Pre-fill form with doctor details and today's date
                          setFollowUpFromSession(null);
                          const today = new Date();
                          const day = String(today.getDate()).padStart(2, '0');
                          const month = String(today.getMonth() + 1).padStart(2, '0');
                          const year = today.getFullYear();
                          const todayFormatted = `${day}/${month}/${year}`;
                          
                          // Try to fetch MA vitals if appointment is selected
                          let initialVitals = {
                            temperature: '',
                            pulse: '',
                            respiratory_rate: '',
                            spo2: '',
                            blood_pressure: '',
                            height: '',
                            weight: ''
                          };
                          
                          if (currentAppointmentId) {
                            const maVitals = await fetchMAVitals(currentAppointmentId);
                            if (maVitals) {
                              initialVitals = {
                                temperature: maVitals.temperature ? String(maVitals.temperature) : '',
                                pulse: maVitals.pulse ? String(maVitals.pulse) : '',
                                respiratory_rate: maVitals.respiratory_rate ? String(maVitals.respiratory_rate) : '',
                                spo2: maVitals.spo2 ? String(maVitals.spo2) : '',
                                blood_pressure: maVitals.blood_pressure || '',
                                height: maVitals.height ? String(maVitals.height) : '',
                                weight: maVitals.weight ? String(maVitals.weight) : ''
                              };
                              toast.info(`Vitals pre-filled by ${maVitals.entered_by_name}`);
                            }
                          }
                          
                          setSessionForm({ 
                            session_date: todayFormatted, 
                            doctor_name: getFormattedDoctorName(user) || 'Dr. Doctor',
                            chief_complaints: '',
                            diagnosis: '', 
                            assessment: '',
                            medications: [{ name: '', timing: 'Before Food', duration: '', time_of_day: [], frequency: '', medication_type: '', treatment_for: '' }],
                            notes: '',
                            follow_up_of: '',
                            tests: '',
                            ...initialVitals
                          });
                          setSelectedFiles([]);
                          setFollowUpFromSession(null); // Clear any previous follow-up
                          // Load previous sessions for this doctor
                          if (selectedBaby?.id && user?.id) {
                            loadPreviousDoctorSessions(selectedBaby.id, user.id);
                          }
                          setShowAddSession(true);
                        }}
                        className="bg-gray-900 hover:bg-gray-800 text-white whitespace-nowrap"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Quick Add Session
                      </Button>
                    </div>
                    
                    {/* Baby Card */}
                    <DoctorBabyCard 
                      babyDetails={babyDetails} 
                      formatDate={formatDate} 
                      calculateAge={calculateAge} 
                    />

                    {/* 6-Tab Grid Layout */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {/* Medical Conditions - Enhanced Card */}
                      <div
                        onClick={() => setShowMedicalConditionsDialog(true)}
                        className="bg-white rounded-xl shadow-sm border p-6 cursor-pointer hover:shadow-md transition-shadow"
                      >
                        {/* Header: Icon left, Title and Count vertically centered with icon */}
                        <div className="flex mb-3">
                          <div className="w-12 h-12 rounded-full flex items-center justify-center card-icon medical-condition flex-shrink-0">
                            <Heart className="w-6 h-6 text-white" />
                          </div>
                          <div className="flex-1 flex items-center ml-3 h-12 translate-y-px">
                            <span className="font-semibold text-gray-900 flex-1">Medical Conditions</span>
                            <span className="text-2xl font-bold text-red-600">{babyDetails?.conditions?.length || 0}</span>
                          </div>
                        </div>
                        
                        {/* Conditions with Medications - aligned with title */}
                        {babyDetails?.conditions?.length > 0 && (
                          <div className="space-y-1 pt-3 border-t border-gray-100 ml-15" style={{marginLeft: '60px'}}>
                            {babyDetails.conditions.slice(0, 3).map((c, idx) => (
                              <div key={idx} className="text-sm">
                                <span className="text-gray-800 font-medium">{c.condition_name}</span>
                                {c.medication && (
                                  <span className="text-gray-500 ml-1">
                                    - {c.medication}
                                  </span>
                                )}
                              </div>
                            ))}
                            {babyDetails.conditions.length > 3 && (
                              <p className="text-sm text-gray-400">+{babyDetails.conditions.length - 3} more</p>
                            )}
                          </div>
                        )}
                        
                        {/* Latest Vitals - aligned with title (only show if from past 7 days) */}
                        {(() => {
                          const sevenDaysAgo = new Date();
                          sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
                          
                          // Check if temperature is from past 7 days
                          const latestTemp = babyDetails?.temperature?.[0];
                          const tempDate = latestTemp?.created_at ? new Date(latestTemp.created_at) : null;
                          const showTemp = latestTemp && tempDate && tempDate >= sevenDaysAgo;
                          
                          // Check if BP is from past 7 days
                          const latestBP = babyDetails?.bloodPressure?.[0];
                          const bpDate = latestBP?.created_at ? new Date(latestBP.created_at) : null;
                          const showBP = latestBP && bpDate && bpDate >= sevenDaysAgo;
                          
                          return (showTemp || showBP) && (
                            <div className="flex flex-wrap gap-2 pt-3 mt-3 border-t border-gray-100" style={{marginLeft: '60px'}}>
                              {showTemp && (
                                <div className="flex items-center gap-1 bg-orange-50 px-2 py-1 rounded text-sm">
                                  <span className="text-orange-600 font-medium">
                                    {latestTemp.temperature}°{latestTemp.unit || 'F'}
                                  </span>
                                </div>
                              )}
                              {showBP && (
                                <div className="flex items-center gap-1 bg-blue-50 px-2 py-1 rounded text-sm">
                                  <span className="text-blue-600 font-medium">
                                    {latestBP.systolic}/{latestBP.diastolic}
                                  </span>
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </div>

                      {/* Allergies - Enhanced Card */}
                      <div
                        onClick={() => setShowAllergiesDialog(true)}
                        className="bg-white rounded-xl shadow-sm border p-6 cursor-pointer hover:shadow-md transition-shadow"
                      >
                        {/* Header: Icon left, Title and Count vertically centered with icon */}
                        <div className="flex mb-3">
                          <div className="w-12 h-12 rounded-full flex items-center justify-center card-icon allergies flex-shrink-0">
                            <ShieldAlert className="w-6 h-6 text-white" />
                          </div>
                          <div className="flex-1 flex items-center ml-3 h-12 translate-y-px">
                            <span className="font-semibold text-gray-900 flex-1">Allergies</span>
                            <span className="text-2xl font-bold text-blue-600">{babyDetails?.allergies?.length || 0}</span>
                          </div>
                        </div>
                        
                        {/* Allergies List - aligned with title */}
                        {babyDetails?.allergies?.length > 0 && (
                          <div className="space-y-1 pt-3 border-t border-gray-100" style={{marginLeft: '60px'}}>
                            {babyDetails.allergies.slice(0, 3).map((a, idx) => (
                              <div key={idx} className="text-sm">
                                <span className="text-gray-800 font-medium">{a.allergen}</span>
                                {a.severity && (
                                  <span className={`ml-2 px-1.5 py-0.5 rounded text-xs ${
                                    a.severity === 'severe' ? 'bg-red-100 text-red-700' :
                                    a.severity === 'moderate' ? 'bg-yellow-100 text-yellow-700' :
                                    'bg-green-100 text-green-700'
                                  }`}>
                                    {a.severity}
                                  </span>
                                )}
                              </div>
                            ))}
                            {babyDetails.allergies.length > 3 && (
                              <p className="text-sm text-gray-400">+{babyDetails.allergies.length - 3} more</p>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Vaccinations - Enhanced Card */}
                      {(() => {
                        const ageInMonths = babyDetails?.baby?.date_of_birth ? getAgeInMonths(babyDetails.baby.date_of_birth) : null;
                        const isUnder12 = ageInMonths !== null && ageInMonths < 144; // 12 years = 144 months
                        
                        // Get scheduled vaccines (with age_group)
                        const scheduledVaccines = babyDetails?.vaccinations?.filter(v => v.age_group) || [];
                        const scheduledGiven = scheduledVaccines.filter(v => v.status === 'given').length;
                        const scheduledTotal = scheduledVaccines.length;
                        
                        // Get additional vaccines (without age_group)
                        const additionalVaccines = babyDetails?.vaccinations?.filter(v => !v.age_group) || [];
                        
                        // Get upcoming vaccines (not given, with due date) - for scheduled vaccines
                        const upcomingVaccines = scheduledVaccines.filter(v => v.status !== 'given' && v.due_date)
                          .sort((a, b) => new Date(a.due_date) - new Date(b.due_date));
                        
                        // Group vaccines by age_group
                        const groupedUpcoming = upcomingVaccines.reduce((acc, v) => {
                          const key = v.age_group || 'Other';
                          if (!acc[key]) {
                            acc[key] = { age_group: key, due_date: v.due_date, vaccines: [] };
                          }
                          acc[key].vaccines.push(v.vaccine_name);
                          return acc;
                        }, {});
                        const allGroups = Object.values(groupedUpcoming).sort((a, b) => new Date(a.due_date) - new Date(b.due_date));
                        
                        // Show only the next overdue OR next upcoming (not all)
                        const today = new Date();
                        const overdueGroups = allGroups.filter(g => new Date(g.due_date) < today);
                        const futureGroups = allGroups.filter(g => new Date(g.due_date) >= today);
                        
                        // Take first overdue (if any) + first upcoming (if any)
                        let displayGroups = [];
                        if (overdueGroups.length > 0) {
                          displayGroups = [overdueGroups[0]]; // Show only the first/most urgent overdue
                        }
                        if (futureGroups.length > 0) {
                          displayGroups.push(futureGroups[0]); // Add next upcoming
                        }
                        
                        // For patients 12+ years old, don't show overdue/upcoming list for scheduled vaccines
                        const showVaccineList = isUnder12;
                        
                        return (
                          <div
                            onClick={() => setShowVaccinationsDialog(true)}
                            className="bg-white rounded-xl shadow-sm border p-6 cursor-pointer hover:shadow-md transition-shadow"
                          >
                            {/* Header: Icon left, Title and Count vertically centered with icon */}
                            <div className="flex mb-3">
                              <div className="w-12 h-12 rounded-full flex items-center justify-center card-icon vaccinations flex-shrink-0">
                                <Syringe className="w-6 h-6 text-white" />
                              </div>
                              <div className="flex-1 flex items-center ml-3 h-12 translate-y-px">
                                <span className="font-semibold text-gray-900 flex-1">Vaccinations</span>
                                <span className="text-2xl font-bold text-purple-600">
                                  {scheduledGiven}/{scheduledTotal}
                                </span>
                              </div>
                            </div>
                            
                            {/* Show overdue vaccine and/or next upcoming one - only for patients under 12 */}
                            {showVaccineList && displayGroups.length > 0 && (
                              <div className="space-y-1 pt-3 border-t border-gray-100" style={{marginLeft: '60px'}}>
                                {displayGroups.map((group, idx) => {
                                  const isOverdue = new Date(group.due_date) < today;
                                  return (
                                    <div key={idx} className="text-sm flex items-center justify-between">
                                      <span className={`font-medium ${isOverdue ? 'text-red-700' : 'text-gray-800'}`}>
                                        {group.age_group} vaccine
                                      </span>
                                      <span className={`text-xs ${isOverdue ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                                        {formatDate(group.due_date)}
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                            
                            {/* Additional Vaccines */}
                            {additionalVaccines.length > 0 && (
                              <div className={`space-y-1 pt-3 border-t border-gray-100 ${showVaccineList && displayGroups.length > 0 ? 'mt-2' : ''}`} style={{marginLeft: '60px'}}>
                                <p className="text-xs text-gray-500 mb-1">Additional Vaccines:</p>
                                {additionalVaccines.slice(0, 2).map((v, idx) => (
                                  <div key={idx} className="text-sm flex items-center justify-between">
                                    <span className="text-gray-800 font-medium">{v.vaccine_name}</span>
                                    <span className={`text-xs px-1.5 py-0.5 rounded ${v.status === 'given' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                                      {v.status === 'given' ? 'Given' : 'Pending'}
                                    </span>
                                  </div>
                                ))}
                                {additionalVaccines.length > 2 && (
                                  <p className="text-sm text-gray-400">+{additionalVaccines.length - 2} more</p>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })()}

                      {/* Growth Tracker - Enhanced Card */}
                      <div
                        onClick={() => setShowGrowthDialog(true)}
                        className="bg-white rounded-xl shadow-sm border p-6 cursor-pointer hover:shadow-md transition-shadow"
                      >
                        {/* Header: Icon left, Title and Count vertically centered with icon */}
                        <div className="flex mb-3">
                          <div className="w-12 h-12 rounded-full flex items-center justify-center card-icon growth-tracker flex-shrink-0">
                            <TrendingUp className="w-6 h-6 text-white" />
                          </div>
                          <div className="flex-1 flex items-center ml-3 h-12 translate-y-px">
                            <span className="font-semibold text-gray-900 flex-1">Growth Tracker</span>
                            <span className="text-2xl font-bold text-pink-600">{babyDetails?.growth?.length || 0}</span>
                          </div>
                        </div>
                        
                        {/* Latest Growth Data - aligned with title */}
                        {babyDetails?.growth?.length > 0 && (() => {
                          const latestGrowth = babyDetails.growth[0];
                          const heightCm = latestGrowth.height;
                          const weightKg = latestGrowth.weight;
                          const bmiValue = heightCm && weightKg ? (weightKg / Math.pow(heightCm / 100, 2)).toFixed(1) : null;
                          
                          // Get age category and BMI status
                          const ageInMonths = babyDetails?.baby?.date_of_birth ? getAgeInMonths(babyDetails.baby.date_of_birth) : null;
                          let ageCategory = 'Adult';
                          let bmiStatus = 'Normal';
                          let statusColor = 'text-green-600';
                          
                          if (ageInMonths !== null) {
                            if (ageInMonths < 144) { // Under 12 years
                              ageCategory = 'Child';
                            } else if (ageInMonths < 228) { // 12-19 years
                              ageCategory = 'Teen';
                            } else {
                              ageCategory = 'Adult';
                            }
                          }
                          
                          // BMI classification based on age
                          if (bmiValue) {
                            const bmi = parseFloat(bmiValue);
                            if (ageCategory === 'Child') {
                              // Pediatric BMI (using percentile-based approximation)
                              if (bmi < 14) { bmiStatus = 'Underweight'; statusColor = 'text-blue-600'; }
                              else if (bmi < 18) { bmiStatus = 'Normal'; statusColor = 'text-green-600'; }
                              else if (bmi < 21) { bmiStatus = 'Overweight'; statusColor = 'text-yellow-600'; }
                              else { bmiStatus = 'Obese'; statusColor = 'text-red-600'; }
                            } else if (ageCategory === 'Teen') {
                              // Teen BMI
                              if (bmi < 16) { bmiStatus = 'Underweight'; statusColor = 'text-blue-600'; }
                              else if (bmi < 23) { bmiStatus = 'Normal'; statusColor = 'text-green-600'; }
                              else if (bmi < 27) { bmiStatus = 'Overweight'; statusColor = 'text-yellow-600'; }
                              else { bmiStatus = 'Obese'; statusColor = 'text-red-600'; }
                            } else {
                              // Adult BMI
                              if (bmi < 18.5) { bmiStatus = 'Underweight'; statusColor = 'text-blue-600'; }
                              else if (bmi < 25) { bmiStatus = 'Normal'; statusColor = 'text-green-600'; }
                              else if (bmi < 30) { bmiStatus = 'Overweight'; statusColor = 'text-yellow-600'; }
                              else { bmiStatus = 'Obese'; statusColor = 'text-red-600'; }
                            }
                          }
                          
                          return (
                            <div className="pt-3 border-t border-gray-100" style={{marginLeft: '60px'}}>
                              <div className="flex flex-wrap gap-2 mb-2">
                                {heightCm && (
                                  <div className="flex items-center gap-1 bg-blue-50 px-2 py-1 rounded text-sm">
                                    <span className="text-blue-600 font-medium">{heightCm} cm</span>
                                  </div>
                                )}
                                {weightKg && (
                                  <div className="flex items-center gap-1 bg-green-50 px-2 py-1 rounded text-sm">
                                    <span className="text-green-600 font-medium">{weightKg} kg</span>
                                  </div>
                                )}
                                {bmiValue && (
                                  <div className="flex items-center gap-1 bg-purple-50 px-2 py-1 rounded text-sm">
                                    <span className="text-purple-600 font-medium">BMI: {bmiValue}</span>
                                  </div>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-sm text-gray-500">{ageCategory}</span>
                                <span className="text-gray-300">|</span>
                                <span className={`text-sm font-medium ${statusColor}`}>{bmiStatus}</span>
                              </div>
                            </div>
                          );
                        })()}
                      </div>

                      {/* Birth Record - Enhanced Card */}
                      <div
                        onClick={() => setShowBirthRecordDialog(true)}
                        className="bg-white rounded-xl shadow-sm border p-6 cursor-pointer hover:shadow-md transition-shadow"
                      >
                        {/* Header: Icon left, Title vertically centered with icon */}
                        <div className="flex mb-3">
                          <div className="w-12 h-12 rounded-full flex items-center justify-center card-icon birth-record flex-shrink-0">
                            <Baby className="w-6 h-6 text-white" />
                          </div>
                          <div className="flex-1 flex items-center ml-3 h-12 translate-y-px">
                            <span className="font-semibold text-gray-900 flex-1">Birth Record</span>
                            <span className={`text-sm font-medium ${babyDetails?.baby?.birth_weight ? 'text-green-600' : 'text-gray-400'}`}>
                              {babyDetails?.baby?.birth_weight ? 'Recorded' : 'Not Recorded'}
                            </span>
                          </div>
                        </div>
                        
                        {/* Birth Details - aligned with title */}
                        {babyDetails?.baby?.birth_weight && (
                          <div className="pt-3 border-t border-gray-100" style={{marginLeft: '60px'}}>
                            <div className="flex flex-wrap gap-2 mb-2">
                              <div className="flex items-center gap-1 bg-pink-50 px-2 py-1 rounded text-sm">
                                <span className="text-pink-600 font-medium">{babyDetails.baby.birth_weight} kg</span>
                              </div>
                              {babyDetails.baby.birth_length && (
                                <div className="flex items-center gap-1 bg-blue-50 px-2 py-1 rounded text-sm">
                                  <span className="text-blue-600 font-medium">{babyDetails.baby.birth_length} cm</span>
                                </div>
                              )}
                              {babyDetails.baby.gestational_age && (
                                <div className="flex items-center gap-1 bg-purple-50 px-2 py-1 rounded text-sm">
                                  <span className="text-purple-600 font-medium">{babyDetails.baby.gestational_age} weeks</span>
                                </div>
                              )}
                            </div>
                            {babyDetails.baby.delivery_type && (
                              <div className="text-sm text-gray-600">
                                {babyDetails.baby.delivery_type}
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Medical Sessions - Enhanced Card */}
                      <div
                        onClick={() => setShowMedicalSessionsDialog(true)}
                        className="bg-white rounded-xl shadow-sm border p-6 cursor-pointer hover:shadow-md transition-shadow"
                      >
                        {/* Header: Icon left, Title and Count vertically centered with icon */}
                        <div className="flex mb-3">
                          <div className="w-12 h-12 rounded-full flex items-center justify-center card-icon medical-sessions flex-shrink-0">
                            <Stethoscope className="w-6 h-6 text-white" />
                          </div>
                          <div className="flex-1 flex items-center ml-3 h-12 translate-y-px">
                            <span className="font-semibold text-gray-900 flex-1">Medical Sessions</span>
                            <span className="text-2xl font-bold text-teal-600">{babyDetails?.sessions?.length || 0}</span>
                          </div>
                        </div>
                        
                        {/* Last 3 Sessions with Diagnosis/Chief Complaint */}
                        {babyDetails?.sessions?.length > 0 && (
                          <div className="space-y-1.5 pt-3 border-t border-gray-100" style={{marginLeft: '60px'}}>
                            {babyDetails.sessions.slice(0, 3).map((s, idx) => (
                              <div key={idx} className="text-sm">
                                <div className="flex items-center justify-between">
                                  <span className="text-gray-800 font-medium">
                                    {s.diagnosis || s.chief_complaint || 'Session'}
                                  </span>
                                  <span className="text-xs text-gray-500 ml-2 flex-shrink-0">
                                    {s.session_date ? formatDate(s.session_date) : ''}
                                  </span>
                                </div>
                              </div>
                            ))}
                            {babyDetails.sessions.length > 3 && (
                              <p className="text-sm text-gray-400">+{babyDetails.sessions.length - 3} more</p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Date Filter */}
                    <div className="bg-white rounded-xl shadow-sm border p-3 md:p-4">
                      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-4">
                    <Label className="text-sm sm:text-base whitespace-nowrap">Filter by Date:</Label>
                    <div className="relative w-full sm:w-auto">
                      <DateInput
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                        className="h-10 w-full sm:w-44 px-3 pr-2 rounded-md border border-gray-300 bg-white text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent cursor-pointer opacity-0 absolute inset-0 z-10"
                        style={{ colorScheme: 'light' }}
                      />
                      {/* Custom date display for consistent format */}
                      <div className="h-10 w-full sm:w-44 px-3 rounded-md border border-gray-300 bg-white text-sm sm:text-base flex items-center justify-between cursor-pointer">
                        <span>{formatDate(selectedDate)}</span>
                        <Calendar className="w-4 h-4 text-gray-400" />
                      </div>
                    </div>
                    <Button
                      onClick={() => setSelectedDate(new Date().toISOString().split('T')[0])}
                      variant="outline"
                      size="sm"
                      className="w-full sm:w-auto"
                    >
                      Today
                    </Button>
                  </div>
                </div>

            {/* Appointments List */}
            <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
              <div className="p-3 md:p-6 border-b bg-gray-50 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                <h2 className="text-base md:text-xl font-bold">Appointments for {formatDate(selectedDate)}</h2>
                {(() => {
                  const selectedDateAppointments = appointments.filter(apt => normalizeDateForComparison(apt.appointment_date) === selectedDate);
                  // Exclude cancelled appointments from total count
                  const activeAppointments = selectedDateAppointments.filter(apt => apt.status !== 'cancelled');
                  const completedCount = activeAppointments.filter(apt => apt.status === 'completed').length;
                  const totalCount = activeAppointments.length;
                  return (
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-gray-600">Cases:</span>
                      <span className="text-lg font-bold text-purple-600">{completedCount}/{totalCount}</span>
                    </div>
                  );
                })()}
              </div>
              
              <div className="divide-y">
                {appointments
                  .filter(apt => normalizeDateForComparison(apt.appointment_date) === selectedDate)
                  .sort((a, b) => {
                    // Quick appointments first (only for pending status)
                    const aIsQuickPending = a.is_quick_appointment && a.status === 'pending';
                    const bIsQuickPending = b.is_quick_appointment && b.status === 'pending';
                    if (aIsQuickPending && !bIsQuickPending) return -1;
                    if (!aIsQuickPending && bIsQuickPending) return 1;
                    
                    // Sort by time (earliest first), then by creation time for same slot
                    const timeA = timeToMinutes(a.appointment_time);
                    const timeB = timeToMinutes(b.appointment_time);
                    if (timeA !== timeB) return timeA - timeB;
                    // For same time slot, sort by created_at (earliest created first)
                    return new Date(a.created_at) - new Date(b.created_at);
                  })
                  .map(appointment => (
                    <div
                      key={appointment.id}
                      className="p-3 md:p-6 hover:bg-gray-50 transition-colors"
                    >
                      <div 
                        className={`flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 ${appointment.status !== 'cancelled' ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`}
                        onClick={() => {
                          if (appointment.status !== 'cancelled') {
                            handleAppointmentClick(appointment);
                          } else {
                            toast.error('Cannot view cancelled appointment details');
                          }
                        }}
                      >
                        <div className="flex-1 w-full sm:w-auto">
                          <div className="flex flex-wrap items-center gap-2 mb-2">
                            <h3 className="text-base md:text-lg font-semibold text-gray-900">
                              {appointment.baby?.name || 'Unknown'}
                            </h3>
                            <span className="text-xs md:text-sm text-gray-500">
                              ({appointment.baby?.short_id || appointment.baby?.id?.substring(0, 8).toUpperCase()})
                            </span>
                            {appointment.status === 'rescheduled' ? (
                              <>
                                <span className="px-3 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
                                  Rescheduled
                                </span>
                                <span className="px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
                                  Pending
                                </span>
                              </>
                            ) : (
                              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                appointment.status === 'completed' ? 'bg-green-100 text-green-700' :
                                appointment.status === 'pending' || appointment.status === 'scheduled' ? 'bg-yellow-100 text-yellow-700' :
                                appointment.status === 'in-progress' ? 'bg-blue-100 text-blue-700' :
                                appointment.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                                'bg-gray-100 text-gray-700'
                              }`}>
                                {appointment.status === 'cancelled' && appointment.notes?.includes('Auto-cancelled') ? 'Auto Cancelled' :
                                 appointment.status === 'scheduled' ? 'Pending' : 
                                 appointment.status === 'in-progress' ? 'In Progress' :
                                 appointment.status === 'pending' ? 'Pending' :
                                 appointment.status.charAt(0).toUpperCase() + appointment.status.slice(1)}
                              </span>
                            )}
                            {appointment.booking_source === 'direct_booking' && (
                              <span className="px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700 ml-2">
                                Direct Booking
                              </span>
                            )}
                            {/* Quick Appointment Badge */}
                            {appointment.is_quick_appointment && (
                              <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700 ml-2 flex items-center gap-1">
                                <Zap className="w-3 h-3" />
                                Quick
                              </span>
                            )}
                            {/* New/Follow-up Badge with Previous Visit Date */}
                            {appointment.appointment_type === 'followup' ? (
                              <div className="flex flex-col items-start">
                                <span className="px-2 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">
                                  {appointment.is_free_visit ? 'Follow-Up (Free – 7 Days)' : 'Follow-Up'}
                                </span>
                                {appointment.followup_of_date && (
                                  <span className="text-[10px] text-indigo-600 mt-0.5 pl-1">
                                    Prev: {appointment.followup_of_date}
                                  </span>
                                )}
                              </div>
                            ) : (
                              <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">New</span>
                            )}
                            {/* Vitals Recorded Badge */}
                            {appointment.has_vitals && (
                              <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700 flex items-center gap-1">
                                <CheckCircle className="w-3 h-3" />
                                Vitals Recorded
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600">
                            Age: {appointment.baby ? calculateAge(appointment.baby.date_of_birth) : 'N/A'}
                            {appointment.baby?.gender && (
                              <span className="text-gray-500"> • {['male', 'boy', 'm'].includes(appointment.baby.gender.toLowerCase()) ? 'Male' : ['female', 'girl', 'f'].includes(appointment.baby.gender.toLowerCase()) ? 'Female' : appointment.baby.gender}</span>
                            )}
                          </p>
                          <p className="text-sm text-gray-500 mt-1">
                            📞 {appointment.baby?.parent_phone || appointment.baby?.mother_contact_phone || appointment.baby?.father_contact_phone || appointment.baby?.emergency_contact_phone || appointment.notes || 'No contact'}
                          </p>
                        </div>
                        <div className="text-left sm:text-right w-full sm:w-auto">
                          <p className="text-base md:text-lg font-bold text-purple-600">{appointment.appointment_time}</p>
                        </div>
                      </div>
                      
                      {appointment.status !== 'cancelled' && (
                        <div className="mt-3 flex justify-end">
                          {appointment.status === 'completed' ? (
                            <Button 
                              size="sm" 
                              className="bg-gray-400 cursor-not-allowed text-xs sm:text-sm w-full sm:w-auto"
                              disabled
                            >
                              <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                              Session Completed
                            </Button>
                          ) : (
                            <Button 
                              size="sm" 
                              className="bg-green-600 hover:bg-green-700 text-xs sm:text-sm w-full sm:w-auto"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCaseDone(appointment.id);
                              }}
                            >
                              <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                              Session Done
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                
                {appointments.filter(apt => normalizeDateForComparison(apt.appointment_date) === selectedDate).length === 0 && (
                  <div className="p-12 text-center text-gray-500">
                    <Calendar className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                    <p>No appointments scheduled for this date</p>
                  </div>
                )}
              </div>
            </div>
                  </>
                )}
            </>
            )}
          </div>
        )}
      </div>

      {/* Baby Profile Side Panel - Only for Quick Lookup */}
      <Dialog open={showBabyProfile && profileDisplayMode === 'panel' && babyDetails} onOpenChange={(open) => {
        setShowBabyProfile(open);
        if (!open) {
          setActivePanelSection(null); // Reset section when closing
          setSelectedBaby(null);
          setBabyDetails(null);
        }
      }}>
        <DialogContent className="max-w-full sm:max-w-5xl h-[90vh] overflow-y-auto p-0">
          <DialogTitle className="sr-only">
            {!activePanelSection 
              ? `${babyDetails?.baby?.name || selectedBaby?.name || 'Baby'} Profile`
              : activePanelSection === 'medical-conditions' ? 'Medical Conditions'
              : activePanelSection === 'allergies' ? 'Allergies'
              : activePanelSection === 'vaccinations' ? 'Vaccinations'
              : activePanelSection === 'growth' ? 'Growth Tracker'
              : activePanelSection === 'birth-record' ? 'Birth Record'
              : activePanelSection === 'medical-sessions' ? 'Medical Sessions'
              : 'Baby Profile'}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {!activePanelSection 
              ? 'View baby profile and access medical records'
              : 'View and manage medical information'}
          </DialogDescription>
          {/* Conditional rendering based on active section */}
          {!activePanelSection ? (
            <>
              {/* Profile Overview */}
              <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-pink-600 text-white p-6 z-10 flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <h2 className="text-2xl font-bold">
                    {babyDetails?.baby?.name || selectedBaby?.name || 'Baby'} Profile
                  </h2>
                </div>
                <Button
                  onClick={() => {
                    setShowBabyProfile(false);
                    setSelectedBaby(null);
                    setBabyDetails(null);
                    setActivePanelSection(null);
                    clearBabyViewState();
                  }}
                  variant="ghost"
                  className="text-white hover:bg-white hover:bg-opacity-20"
                >
                  <X className="w-6 h-6" />
                </Button>
              </div>
              
              <div className="p-6 space-y-6">
                {/* Baby Card */}
                <DoctorBabyCard 
                  babyDetails={babyDetails} 
                  formatDate={formatDate} 
                  calculateAge={calculateAge} 
                />

                {/* 6-Tab Grid Layout */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Medical Conditions - Enhanced Card */}
                  <div
                    onClick={() => setActivePanelSection('medical-conditions')}
                    className="bg-white rounded-xl shadow-sm border p-6 cursor-pointer hover:shadow-md transition-shadow"
                  >
                    {/* Header: Icon left, Title and Count vertically centered with icon */}
                    <div className="flex mb-3">
                      <div className="w-12 h-12 rounded-full flex items-center justify-center card-icon medical-condition flex-shrink-0">
                        <Heart className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1 flex items-center ml-3 h-12 translate-y-px">
                        <span className="font-semibold text-gray-900 flex-1">Medical Conditions</span>
                        <span className="text-2xl font-bold text-red-600">{babyDetails?.conditions?.length || 0}</span>
                      </div>
                    </div>
                    
                    {/* Conditions with Medications - aligned with title */}
                    {babyDetails?.conditions?.length > 0 && (
                      <div className="space-y-1 pt-3 border-t border-gray-100" style={{marginLeft: '60px'}}>
                        {babyDetails.conditions.slice(0, 3).map((c, idx) => (
                          <div key={idx} className="text-sm">
                            <span className="text-gray-800 font-medium">{c.condition_name}</span>
                            {c.medication && (
                              <span className="text-gray-500 ml-1">
                                - {c.medication}
                              </span>
                            )}
                          </div>
                        ))}
                        {babyDetails.conditions.length > 3 && (
                          <p className="text-sm text-gray-400">+{babyDetails.conditions.length - 3} more</p>
                        )}
                      </div>
                    )}
                    
                    {/* Latest Vitals - aligned with title (only show if from past 7 days) */}
                    {(() => {
                      const sevenDaysAgo = new Date();
                      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
                      
                      // Check if temperature is from past 7 days
                      const latestTemp = babyDetails?.temperature?.[0];
                      const tempDate = latestTemp?.created_at ? new Date(latestTemp.created_at) : null;
                      const showTemp = latestTemp && tempDate && tempDate >= sevenDaysAgo;
                      
                      // Check if BP is from past 7 days
                      const latestBP = babyDetails?.bloodPressure?.[0];
                      const bpDate = latestBP?.created_at ? new Date(latestBP.created_at) : null;
                      const showBP = latestBP && bpDate && bpDate >= sevenDaysAgo;
                      
                      return (showTemp || showBP) && (
                        <div className="flex flex-wrap gap-2 pt-3 mt-3 border-t border-gray-100" style={{marginLeft: '60px'}}>
                          {showTemp && (
                            <div className="flex items-center gap-1 bg-orange-50 px-2 py-1 rounded text-sm">
                              <span className="text-orange-600 font-medium">
                                {latestTemp.temperature}°{latestTemp.unit || 'F'}
                              </span>
                            </div>
                          )}
                          {showBP && (
                            <div className="flex items-center gap-1 bg-blue-50 px-2 py-1 rounded text-sm">
                              <span className="text-blue-600 font-medium">
                                {latestBP.systolic}/{latestBP.diastolic}
                              </span>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>

                  {/* Allergies - Enhanced Card */}
                  <div
                    onClick={() => setActivePanelSection('allergies')}
                    className="bg-white rounded-xl shadow-sm border p-6 cursor-pointer hover:shadow-md transition-shadow"
                  >
                    {/* Header: Icon left, Title and Count vertically centered with icon */}
                    <div className="flex mb-3">
                      <div className="w-12 h-12 rounded-full flex items-center justify-center card-icon allergies flex-shrink-0">
                        <ShieldAlert className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1 flex items-center ml-3 h-12 translate-y-px">
                        <span className="font-semibold text-gray-900 flex-1">Allergies</span>
                        <span className="text-2xl font-bold text-blue-600">{babyDetails?.allergies?.length || 0}</span>
                      </div>
                    </div>
                    
                    {/* Allergies List - aligned with title */}
                    {babyDetails?.allergies?.length > 0 && (
                      <div className="space-y-1 pt-3 border-t border-gray-100" style={{marginLeft: '60px'}}>
                        {babyDetails.allergies.slice(0, 3).map((a, idx) => (
                          <div key={idx} className="text-sm">
                            <span className="text-gray-800 font-medium">{a.allergen}</span>
                            {a.severity && (
                              <span className={`ml-2 px-1.5 py-0.5 rounded text-xs ${
                                a.severity === 'severe' ? 'bg-red-100 text-red-700' :
                                a.severity === 'moderate' ? 'bg-yellow-100 text-yellow-700' :
                                'bg-green-100 text-green-700'
                              }`}>
                                {a.severity}
                              </span>
                            )}
                          </div>
                        ))}
                        {babyDetails.allergies.length > 3 && (
                          <p className="text-sm text-gray-400">+{babyDetails.allergies.length - 3} more</p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Vaccinations - Enhanced Card */}
                  {(() => {
                    const ageInMonths = babyDetails?.baby?.date_of_birth ? getAgeInMonths(babyDetails.baby.date_of_birth) : null;
                    const isUnder12 = ageInMonths !== null && ageInMonths < 144; // 12 years = 144 months
                    
                    // Get scheduled vaccines (with age_group)
                    const scheduledVaccines = babyDetails?.vaccinations?.filter(v => v.age_group) || [];
                    const scheduledGiven = scheduledVaccines.filter(v => v.status === 'given').length;
                    const scheduledTotal = scheduledVaccines.length;
                    
                    // Get additional vaccines (without age_group)
                    const additionalVaccines = babyDetails?.vaccinations?.filter(v => !v.age_group) || [];
                    
                    // Get upcoming vaccines (not given, with due date) - for scheduled vaccines
                    const upcomingVaccines = scheduledVaccines.filter(v => v.status !== 'given' && v.due_date)
                      .sort((a, b) => new Date(a.due_date) - new Date(b.due_date));
                    
                    // Group vaccines by age_group
                    const groupedUpcoming = upcomingVaccines.reduce((acc, v) => {
                      const key = v.age_group || 'Other';
                      if (!acc[key]) {
                        acc[key] = { age_group: key, due_date: v.due_date, vaccines: [] };
                      }
                      acc[key].vaccines.push(v.vaccine_name);
                      return acc;
                    }, {});
                    const allGroups = Object.values(groupedUpcoming).sort((a, b) => new Date(a.due_date) - new Date(b.due_date));
                    
                    // Show only the next overdue OR next upcoming (not all)
                    const today = new Date();
                    const overdueGroups = allGroups.filter(g => new Date(g.due_date) < today);
                    const futureGroups = allGroups.filter(g => new Date(g.due_date) >= today);
                    
                    // Take first overdue (if any) + first upcoming (if any)
                    let displayGroups = [];
                    if (overdueGroups.length > 0) {
                      displayGroups = [overdueGroups[0]]; // Show only the first/most urgent overdue
                    }
                    if (futureGroups.length > 0) {
                      displayGroups.push(futureGroups[0]); // Add next upcoming
                    }
                    
                    // For patients 12+ years old, don't show overdue/upcoming list for scheduled vaccines
                    const showVaccineList = isUnder12;
                    
                    return (
                      <div
                        onClick={() => setActivePanelSection('vaccinations')}
                        className="bg-white rounded-xl shadow-sm border p-6 cursor-pointer hover:shadow-md transition-shadow"
                      >
                        {/* Header: Icon left, Title and Count vertically centered with icon */}
                        <div className="flex mb-3">
                          <div className="w-12 h-12 rounded-full flex items-center justify-center card-icon vaccinations flex-shrink-0">
                            <Syringe className="w-6 h-6 text-white" />
                          </div>
                          <div className="flex-1 flex items-center ml-3 h-12 translate-y-px">
                            <span className="font-semibold text-gray-900 flex-1">Vaccinations</span>
                            <span className="text-2xl font-bold text-purple-600">
                              {scheduledGiven}/{scheduledTotal}
                            </span>
                          </div>
                        </div>
                        
                        {/* Show overdue vaccine and/or next upcoming one - only for patients under 12 */}
                        {showVaccineList && displayGroups.length > 0 && (
                          <div className="space-y-1 pt-3 border-t border-gray-100" style={{marginLeft: '60px'}}>
                            {displayGroups.map((group, idx) => {
                              const isOverdue = new Date(group.due_date) < today;
                              return (
                                <div key={idx} className="text-sm flex items-center justify-between">
                                  <span className={`font-medium ${isOverdue ? 'text-red-700' : 'text-gray-800'}`}>
                                    {group.age_group} vaccine
                                  </span>
                                  <span className={`text-xs ${isOverdue ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                                    {formatDate(group.due_date)}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                        
                        {/* Additional Vaccines */}
                        {additionalVaccines.length > 0 && (
                          <div className={`space-y-1 pt-3 border-t border-gray-100 ${showVaccineList && displayGroups.length > 0 ? 'mt-2' : ''}`} style={{marginLeft: '60px'}}>
                            <p className="text-xs text-gray-500 mb-1">Additional Vaccines:</p>
                            {additionalVaccines.slice(0, 2).map((v, idx) => (
                              <div key={idx} className="text-sm flex items-center justify-between">
                                <span className="text-gray-800 font-medium">{v.vaccine_name}</span>
                                <span className={`text-xs px-1.5 py-0.5 rounded ${v.status === 'given' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                                  {v.status === 'given' ? 'Given' : 'Pending'}
                                </span>
                              </div>
                            ))}
                            {additionalVaccines.length > 2 && (
                              <p className="text-sm text-gray-400">+{additionalVaccines.length - 2} more</p>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Growth Tracker - Enhanced Card */}
                  <div
                    onClick={() => setActivePanelSection('growth')}
                    className="bg-white rounded-xl shadow-sm border p-6 cursor-pointer hover:shadow-md transition-shadow"
                  >
                    {/* Header: Icon left, Title and Count vertically centered with icon */}
                    <div className="flex mb-3">
                      <div className="w-12 h-12 rounded-full flex items-center justify-center card-icon growth-tracker flex-shrink-0">
                        <TrendingUp className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1 flex items-center ml-3 h-12 translate-y-px">
                        <span className="font-semibold text-gray-900 flex-1">Growth Tracker</span>
                        <span className="text-2xl font-bold text-pink-600">{babyDetails?.growth?.length || 0}</span>
                      </div>
                    </div>
                    
                    {/* Latest Growth Data - aligned with title */}
                    {babyDetails?.growth?.length > 0 && (() => {
                      const latestGrowth = babyDetails.growth[0];
                      const heightCm = latestGrowth.height;
                      const weightKg = latestGrowth.weight;
                      const bmiValue = heightCm && weightKg ? (weightKg / Math.pow(heightCm / 100, 2)).toFixed(1) : null;
                      
                      // Get age category and BMI status
                      const ageInMonths = babyDetails?.baby?.date_of_birth ? getAgeInMonths(babyDetails.baby.date_of_birth) : null;
                      let ageCategory = 'Adult';
                      let bmiStatus = 'Normal';
                      let statusColor = 'text-green-600';
                      
                      if (ageInMonths !== null) {
                        if (ageInMonths < 144) { // Under 12 years
                          ageCategory = 'Child';
                        } else if (ageInMonths < 228) { // 12-19 years
                          ageCategory = 'Teen';
                        } else {
                          ageCategory = 'Adult';
                        }
                      }
                      
                      // BMI classification based on age
                      if (bmiValue) {
                        const bmi = parseFloat(bmiValue);
                        if (ageCategory === 'Child') {
                          if (bmi < 14) { bmiStatus = 'Underweight'; statusColor = 'text-blue-600'; }
                          else if (bmi < 18) { bmiStatus = 'Normal'; statusColor = 'text-green-600'; }
                          else if (bmi < 21) { bmiStatus = 'Overweight'; statusColor = 'text-yellow-600'; }
                          else { bmiStatus = 'Obese'; statusColor = 'text-red-600'; }
                        } else if (ageCategory === 'Teen') {
                          if (bmi < 16) { bmiStatus = 'Underweight'; statusColor = 'text-blue-600'; }
                          else if (bmi < 23) { bmiStatus = 'Normal'; statusColor = 'text-green-600'; }
                          else if (bmi < 27) { bmiStatus = 'Overweight'; statusColor = 'text-yellow-600'; }
                          else { bmiStatus = 'Obese'; statusColor = 'text-red-600'; }
                        } else {
                          if (bmi < 18.5) { bmiStatus = 'Underweight'; statusColor = 'text-blue-600'; }
                          else if (bmi < 25) { bmiStatus = 'Normal'; statusColor = 'text-green-600'; }
                          else if (bmi < 30) { bmiStatus = 'Overweight'; statusColor = 'text-yellow-600'; }
                          else { bmiStatus = 'Obese'; statusColor = 'text-red-600'; }
                        }
                      }
                      
                      return (
                        <div className="pt-3 border-t border-gray-100" style={{marginLeft: '60px'}}>
                          <div className="flex flex-wrap gap-2 mb-2">
                            {heightCm && (
                              <div className="flex items-center gap-1 bg-blue-50 px-2 py-1 rounded text-sm">
                                <span className="text-blue-600 font-medium">{heightCm} cm</span>
                              </div>
                            )}
                            {weightKg && (
                              <div className="flex items-center gap-1 bg-green-50 px-2 py-1 rounded text-sm">
                                <span className="text-green-600 font-medium">{weightKg} kg</span>
                              </div>
                            )}
                            {bmiValue && (
                              <div className="flex items-center gap-1 bg-purple-50 px-2 py-1 rounded text-sm">
                                <span className="text-purple-600 font-medium">BMI: {bmiValue}</span>
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-500">{ageCategory}</span>
                            <span className="text-gray-300">|</span>
                            <span className={`text-sm font-medium ${statusColor}`}>{bmiStatus}</span>
                          </div>
                        </div>
                      );
                    })()}
                  </div>

                  {/* Birth Record - Enhanced Card */}
                  <div
                    onClick={() => setActivePanelSection('birth-record')}
                    className="bg-white rounded-xl shadow-sm border p-6 cursor-pointer hover:shadow-md transition-shadow"
                  >
                    {/* Header: Icon left, Title vertically centered with icon */}
                    <div className="flex mb-3">
                      <div className="w-12 h-12 rounded-full flex items-center justify-center card-icon birth-record flex-shrink-0">
                        <Baby className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1 flex items-center ml-3 h-12 translate-y-px">
                        <span className="font-semibold text-gray-900 flex-1">Birth Record</span>
                        <span className={`text-sm font-medium ${babyDetails?.baby?.birth_weight ? 'text-green-600' : 'text-gray-400'}`}>
                          {babyDetails?.baby?.birth_weight ? 'Recorded' : 'Not Recorded'}
                        </span>
                      </div>
                    </div>
                    
                    {/* Birth Details - aligned with title */}
                    {babyDetails?.baby?.birth_weight && (
                      <div className="pt-3 border-t border-gray-100" style={{marginLeft: '60px'}}>
                        <div className="flex flex-wrap gap-2 mb-2">
                          <div className="flex items-center gap-1 bg-pink-50 px-2 py-1 rounded text-sm">
                            <span className="text-pink-600 font-medium">{babyDetails.baby.birth_weight} kg</span>
                          </div>
                          {babyDetails.baby.birth_length && (
                            <div className="flex items-center gap-1 bg-blue-50 px-2 py-1 rounded text-sm">
                              <span className="text-blue-600 font-medium">{babyDetails.baby.birth_length} cm</span>
                            </div>
                          )}
                          {babyDetails.baby.gestational_age && (
                            <div className="flex items-center gap-1 bg-purple-50 px-2 py-1 rounded text-sm">
                              <span className="text-purple-600 font-medium">{babyDetails.baby.gestational_age} weeks</span>
                            </div>
                          )}
                        </div>
                        {babyDetails.baby.delivery_type && (
                          <div className="text-sm text-gray-600">
                            {babyDetails.baby.delivery_type}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Medical Sessions - Enhanced Card */}
                  <div
                    onClick={() => setActivePanelSection('medical-sessions')}
                    className="bg-white rounded-xl shadow-sm border p-6 cursor-pointer hover:shadow-md transition-shadow"
                  >
                    {/* Header: Icon left, Title and Count vertically centered with icon */}
                    <div className="flex mb-3">
                      <div className="w-12 h-12 rounded-full flex items-center justify-center card-icon medical-sessions flex-shrink-0">
                        <Stethoscope className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1 flex items-center ml-3 h-12 translate-y-px">
                        <span className="font-semibold text-gray-900 flex-1">Medical Sessions</span>
                        <span className="text-2xl font-bold text-teal-600">{babyDetails?.sessions?.length || 0}</span>
                      </div>
                    </div>
                    
                    {/* Last 3 Sessions with Diagnosis/Chief Complaint */}
                    {babyDetails?.sessions?.length > 0 && (
                      <div className="space-y-1.5 pt-3 border-t border-gray-100" style={{marginLeft: '60px'}}>
                        {babyDetails.sessions.slice(0, 3).map((s, idx) => (
                          <div key={idx} className="text-sm">
                            <div className="flex items-center justify-between">
                              <span className="text-gray-800 font-medium">
                                {s.diagnosis || s.chief_complaint || 'Session'}
                              </span>
                              <span className="text-xs text-gray-500 ml-2 flex-shrink-0">
                                {s.session_date ? formatDate(s.session_date) : ''}
                              </span>
                            </div>
                          </div>
                        ))}
                        {babyDetails.sessions.length > 3 && (
                          <p className="text-sm text-gray-400">+{babyDetails.sessions.length - 3} more</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Section Detail View */}
              <div className="min-h-full bg-gray-50">
                {/* Header with Back Button */}
                <div className="bg-white border-b p-4 flex items-center space-x-4 sticky top-0 z-10">
                  <Button
                    onClick={() => setActivePanelSection(null)}
                    variant="outline"
                    size="sm"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to Profile
                  </Button>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {activePanelSection === 'medical-conditions' && 'Medical Conditions'}
                    {activePanelSection === 'allergies' && 'Allergies'}
                    {activePanelSection === 'vaccinations' && 'Vaccinations'}
                    {activePanelSection === 'growth' && 'Growth Tracker'}
                    {activePanelSection === 'birth-record' && 'Birth Record'}
                    {activePanelSection === 'medical-sessions' && 'Medical Sessions'}
                  </h3>
                </div>

                {/* Section Content */}
                <div className="p-6">
                  {activePanelSection === 'medical-conditions' && babyDetails && (
                    <MedicalConditionsSection 
                      conditions={babyDetails.conditions || []}
                      temperatureReadings={babyDetails.temperature || []}
                      bloodPressureReadings={babyDetails.bloodPressure || []}
                      onAdd={() => setShowAddCondition(true)}
                      onEdit={openEditCondition}
                      onDelete={handleDeleteCondition}
                      onAddTemp={() => setShowTempDialog(true)}
                      onEditTemp={openEditTemperature}
                      onDeleteTemp={handleDeleteTemperature}
                      onAddBp={() => setShowBpDialog(true)}
                      onEditBp={openEditBloodPressure}
                      onDeleteBp={handleDeleteBloodPressure}
                      currentUserId={user?.id}
                      formatDate={formatDate}
                      viewOnlyMode={false}
                    />
                  )}
                  {activePanelSection === 'allergies' && babyDetails && (
                    <AllergiesSection 
                      allergies={babyDetails.allergies || []}
                      onAdd={() => setShowAddAllergy(true)}
                      onEdit={openEditAllergy}
                      onDelete={handleDeleteAllergy}
                      formatDate={formatDate}
                      viewOnlyMode={false}
                      currentUserId={user?.id}
                    />
                  )}
                  {activePanelSection === 'vaccinations' && babyDetails && (
                    <VaccinationsSection 
                      vaccinations={babyDetails.vaccinations || []}
                      onMarkAsGiven={(vaccine) => {
                        setEditingVaccination(vaccine);
                        setVaccinationForm({
                          vaccine_name: vaccine.vaccine_name,
                          date_administered: new Date().toISOString().split('T')[0],
                          batch_number: '',
                          administered_by: getFormattedDoctorName(user) || 'Dr. Doctor'
                        });
                        setShowAddVaccination(true);
                      }}
                      onUpdateSchedule={handleUpdateVaccinationSchedule}
                      onAddVaccination={handleOpenAddVaccination}
                      formatDate={formatDate}
                      viewOnlyMode={false}
                    />
                  )}
                  {activePanelSection === 'growth' && babyDetails && (
                    <GrowthTrackerSection 
                      growthRecords={babyDetails.growth || []}
                      baby={babyDetails.baby}
                      onAdd={() => setShowAddGrowth(true)}
                      onDelete={handleDeleteGrowth}
                      formatDate={formatDate}
                      calculateAge={calculateAge}
                      viewOnlyMode={false}
                      currentDoctorId={user?.id}
                    />
                  )}
                  {activePanelSection === 'birth-record' && babyDetails && (
                    <BirthRecordSection 
                      baby={babyDetails.baby}
                      birthRecord={babyDetails.birthRecord}
                      onEdit={() => setShowEditBirthRecord(true)}
                      formatDate={formatDate}
                      viewOnlyMode={true}
                    />
                  )}
                  {activePanelSection === 'medical-sessions' && babyDetails && (
                    <MedicalSessionsSection 
                      sessions={babyDetails.sessions || []}
                      baby={babyDetails.baby}
                      currentUserId={user?.id}
                      onAdd={async () => {
                        // Reset form with basic info
                        const today = new Date();
                        let initialVitals = {
                          temperature: '',
                          pulse: '',
                          respiratory_rate: '',
                          spo2: '',
                          blood_pressure: '',
                          height: '',
                          weight: ''
                        };
                        
                        // Try to fetch MA vitals if appointment is selected
                        if (currentAppointmentId) {
                          const maVitals = await fetchMAVitals(currentAppointmentId);
                          if (maVitals) {
                            initialVitals = {
                              temperature: maVitals.temperature ? String(maVitals.temperature) : '',
                              pulse: maVitals.pulse ? String(maVitals.pulse) : '',
                              respiratory_rate: maVitals.respiratory_rate ? String(maVitals.respiratory_rate) : '',
                              spo2: maVitals.spo2 ? String(maVitals.spo2) : '',
                              blood_pressure: maVitals.blood_pressure || '',
                              height: maVitals.height ? String(maVitals.height) : '',
                              weight: maVitals.weight ? String(maVitals.weight) : ''
                            };
                            toast.info(`Vitals pre-filled by ${maVitals.entered_by_name}`);
                          }
                        }
                        
                        setSessionForm({
                          session_date: `${String(today.getDate()).padStart(2, '0')}/${String(today.getMonth() + 1).padStart(2, '0')}/${today.getFullYear()}`,
                          doctor_name: getFormattedDoctorName(user) || 'Dr. Doctor',
                          chief_complaints: '',
                          diagnosis: '',
                          medications: [{ name: '', timing: 'Before Food', duration: '', time_of_day: [], frequency: '', medication_type: '', treatment_for: '' }],
                          notes: '',
                          follow_up_of: '',
                          tests: '',
                          ...initialVitals
                        });
                        setSelectedFiles([]);
                        setFollowUpFromSession(null);
                        setEditingSession(null);
                        // Load previous sessions
                        if (selectedBaby?.id && user?.id) {
                          loadPreviousDoctorSessions(selectedBaby.id, user.id);
                        }
                        setShowAddSession(true);
                      }}
                      onEdit={(session) => handleEditSession(session)}
                      onDelete={(sessionId) => handleDeleteSession(sessionId)}
                      onCreateFollowUp={(session) => createFollowUpSession(session)}
                      onViewPrevious={(session) => {
                        setPreviousSessionData(session);
                        setShowPreviousSessionDialog(true);
                      }}
                      onViewSOAP={handleViewSOAPNotes}
                      formatDate={formatDate}
                      viewOnlyMode={true}
                      API={API}
                    />
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Medical Conditions Dialog - Fullscreen */}
      <Dialog open={showMedicalConditionsDialog} onOpenChange={setShowMedicalConditionsDialog}>
        <DialogContent className="max-w-full h-full m-0 p-0 rounded-none overflow-y-auto">
          <DialogTitle className="sr-only">Medical Conditions</DialogTitle>
          <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="dashboard-header">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setShowMedicalConditionsDialog(false)}
                    className="text-white hover:bg-white hover:bg-opacity-20"
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </Button>
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                      <Heart className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h1 className="text-xl font-bold" style={{fontFamily: 'Space Grotesk'}}>Medical Conditions</h1>
                      <p className="text-blue-200 text-sm">Manage medical conditions</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="px-6 py-6">
              <MedicalConditionsSection 
                conditions={babyDetails?.conditions || []}
                temperatureReadings={babyDetails?.temperature || []}
                bloodPressureReadings={babyDetails?.bloodPressure || []}
                onAdd={() => setShowAddCondition(true)}
                onEdit={openEditCondition}
                onDelete={handleDeleteCondition}
                onAddTemp={() => setShowTempDialog(true)}
                onEditTemp={openEditTemperature}
                onDeleteTemp={handleDeleteTemperature}
                onAddBp={() => setShowBpDialog(true)}
                onEditBp={openEditBloodPressure}
                onDeleteBp={handleDeleteBloodPressure}
                currentUserId={user?.id}
                formatDate={formatDate}
                viewOnlyMode={false}
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Medical Condition Form Dialog */}
      <Dialog open={showAddCondition || editingCondition !== null} onOpenChange={(open) => {
        if (!open) {
          setShowAddCondition(false);
          setEditingCondition(null);
          setConditionForm({ condition_name: '', description: '', diagnosed_date: '', medication: '', status: 'active' });
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingCondition ? 'Edit' : 'Add'} Medical Condition</DialogTitle>
            <DialogDescription>
              {editingCondition ? 'Update the medical condition details below.' : 'Add a new medical condition for this baby.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Condition Name *</Label>
              <Input
                value={conditionForm.condition_name}
                onChange={(e) => setConditionForm({...conditionForm, condition_name: e.target.value})}
                placeholder="e.g., Asthma"
              />
            </div>
            
            <div>
              <Label>Description</Label>
              <Textarea
                value={conditionForm.description}
                onChange={(e) => setConditionForm({...conditionForm, description: e.target.value})}
                placeholder="Additional details..."
                rows={3}
              />
            </div>
            
            <div>
              <Label>Diagnosed Date</Label>
              <Input
                type="text"
                placeholder="DD/MM/YYYY"
                value={conditionForm.diagnosed_date}
                onChange={handleDateInput(setConditionForm, 'diagnosed_date')}
                onKeyDown={handleDateKeyDown(setConditionForm, 'diagnosed_date')}
                maxLength={10}
              />
            </div>
            
            <div>
              <Label>Status</Label>
              <Select 
                value={conditionForm.status} 
                onValueChange={(value) => setConditionForm({...conditionForm, status: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                  <SelectItem value="chronic">Chronic</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Medication</Label>
              <Textarea
                value={conditionForm.medication}
                onChange={(e) => setConditionForm({...conditionForm, medication: e.target.value})}
                placeholder="Enter medication details"
                rows={2}
              />
            </div>
            
            <div className="flex gap-2">
              <Button 
                onClick={editingCondition ? handleEditCondition : handleAddCondition}
                className="flex-1 bg-red-600 hover:bg-red-700"
                disabled={submitting}
              >
                {submitting ? 'Saving...' : (editingCondition ? 'Update Condition' : 'Save Condition')}
              </Button>
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowAddCondition(false);
                  setEditingCondition(null);
                  setConditionForm({ condition_name: '', description: '', diagnosed_date: '', medication: '', status: 'active' });
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Allergies Dialog - Fullscreen */}
      <Dialog open={showAllergiesDialog} onOpenChange={setShowAllergiesDialog}>
        <DialogContent className="max-w-full h-full m-0 p-0 rounded-none overflow-y-auto">
          <DialogTitle className="sr-only">Allergies</DialogTitle>
          <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="dashboard-header">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setShowAllergiesDialog(false)}
                    className="text-white hover:bg-white hover:bg-opacity-20"
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </Button>
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                      <ShieldAlert className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h1 className="text-xl font-bold" style={{fontFamily: 'Space Grotesk'}}>Allergies</h1>
                      <p className="text-blue-200 text-sm">Manage allergies</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="px-6 py-6">
              <AllergiesSection 
                allergies={babyDetails?.allergies || []}
                onAdd={() => setShowAddAllergy(true)}
                onEdit={openEditAllergy}
                onDelete={handleDeleteAllergy}
                formatDate={formatDate}
                viewOnlyMode={false}
                currentUserId={user?.id}
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Allergy Form Dialog */}
      <Dialog open={showAddAllergy || editingAllergy !== null} onOpenChange={(open) => {
        if (!open) {
          setShowAddAllergy(false);
          setEditingAllergy(null);
          setAllergyForm({ allergen: '', reaction: '', severity: 'Mild', discovered_date: '', notes: '' });
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingAllergy ? 'Edit' : 'Add'} Allergy</DialogTitle>
            <DialogDescription>
              {editingAllergy ? 'Update the allergy information below.' : 'Add a new allergy record for this baby.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Allergen *</Label>
              <Input
                value={allergyForm.allergen}
                onChange={(e) => setAllergyForm({...allergyForm, allergen: e.target.value})}
                placeholder="e.g., Peanuts, Milk, Eggs"
                required
              />
            </div>
            
            <div>
              <Label>Reaction *</Label>
              <Input
                value={allergyForm.reaction}
                onChange={(e) => setAllergyForm({...allergyForm, reaction: e.target.value})}
                placeholder="e.g., Rash, Difficulty breathing"
                required
              />
            </div>
            
            <div>
              <Label>Severity *</Label>
              <Select 
                value={allergyForm.severity} 
                onValueChange={(value) => setAllergyForm({...allergyForm, severity: value})}
                required
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select severity" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Mild">Mild</SelectItem>
                  <SelectItem value="Moderate">Moderate</SelectItem>
                  <SelectItem value="Severe">Severe</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Discovered Date (DD/MM/YYYY) *</Label>
              <Input
                type="text"
                placeholder="DD/MM/YYYY"
                value={allergyForm.discovered_date}
                onChange={handleDateInput(setAllergyForm, 'discovered_date')}
                onKeyDown={handleDateKeyDown(setAllergyForm, 'discovered_date')}
                maxLength={10}
                required
              />
            </div>
            
            <div>
              <Label>Notes (Optional)</Label>
              <Textarea
                value={allergyForm.notes}
                onChange={(e) => setAllergyForm({...allergyForm, notes: e.target.value})}
                placeholder="Additional notes about the allergy..."
                rows={3}
              />
            </div>
            
            <div className="flex gap-2">
              <Button 
                onClick={editingAllergy ? handleEditAllergy : handleAddAllergy}
                className="flex-1 bg-orange-600 hover:bg-orange-700"
                disabled={submitting}
              >
                {submitting ? 'Saving...' : (editingAllergy ? 'Update' : 'Save Allergy')}
              </Button>
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowAddAllergy(false);
                  setEditingAllergy(null);
                  setAllergyForm({ allergen: '', reaction: '', severity: 'Mild', discovered_date: '', notes: '' });
                }}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Vaccinations Dialog - Fullscreen */}
      <Dialog open={showVaccinationsDialog} onOpenChange={setShowVaccinationsDialog}>
        <DialogContent className="max-w-full h-full m-0 p-0 rounded-none overflow-y-auto">
          <DialogTitle className="sr-only">Vaccinations</DialogTitle>
          <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="dashboard-header">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setShowVaccinationsDialog(false)}
                    className="text-white hover:bg-white hover:bg-opacity-20"
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </Button>
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                      <Syringe className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h1 className="text-xl font-bold" style={{fontFamily: 'Space Grotesk'}}>Vaccinations</h1>
                      <p className="text-blue-200 text-sm">{selectedBaby?.name}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="px-6 py-6">
              <VaccinationsSection 
                vaccinations={babyDetails?.vaccinations || []}
                onMarkAsGiven={(vaccine) => {
                  setEditingVaccination(vaccine);
                  setVaccinationForm({
                    vaccine_name: vaccine.vaccine_name,
                    date_administered: new Date().toISOString().split('T')[0],
                    batch_number: '',
                    administered_by: getFormattedDoctorName(user) || 'Dr. Doctor'
                  });
                  setShowAddVaccination(true);
                }}
                onUpdateSchedule={handleUpdateVaccinationSchedule}
                onAddVaccination={handleOpenAddVaccination}
                formatDate={formatDate}
                viewOnlyMode={false}
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Keep old vaccinations list section for reference */}
      {false && (
          <div className="space-y-3">
            {/* Vaccinations List */}
            {babyDetails?.vaccinations && babyDetails.vaccinations.length > 0 ? (
              babyDetails.vaccinations.map((vaccine) => {
                const isGiven = vaccine.date_administered;
                const isPending = !isGiven && vaccine.due_date;
                const isOverdue = isPending && new Date(vaccine.due_date) < new Date();
                
                return (
                  <div key={vaccine.id} className={`p-4 border rounded-lg ${
                    isGiven ? 'bg-green-50 border-green-200' : 
                    isOverdue ? 'bg-red-50 border-red-200' : 
                    'bg-yellow-50 border-yellow-200'
                  }`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-semibold text-gray-900">{vaccine.vaccine_name}</h4>
                          {isGiven && <CheckCircle className="w-4 h-4 text-green-600" />}
                          {isOverdue && <AlertCircle className="w-4 h-4 text-red-600" />}
                          {isPending && !isOverdue && <Clock className="w-4 h-4 text-yellow-600" />}
                        </div>
                        
                        {vaccine.due_date && (
                          <p className="text-sm text-gray-600">
                            Due Date: {formatDate(vaccine.due_date)}
                          </p>
                        )}
                        
                        {isGiven && (
                          <>
                            <p className="text-sm text-green-700 font-medium mt-1">
                              Given on: {formatDate(vaccine.date_administered)}
                            </p>
                            {vaccine.batch_number && (
                              <p className="text-xs text-gray-500 mt-1">Batch: {vaccine.batch_number}</p>
                            )}
                            {vaccine.administered_by && (
                              <p className="text-xs text-gray-500">Given by: {vaccine.administered_by}</p>
                            )}
                          </>
                        )}
                      </div>
                      
                      {!isGiven && (
                        <Button 
                          size="sm" 
                          className="bg-green-600 hover:bg-green-700 ml-4"
                          onClick={() => {
                            setEditingVaccination(vaccine);
                            setVaccinationForm({
                              vaccine_name: vaccine.vaccine_name,
                              given_date: formatDateToDDMMYYYY(new Date()),
                              trade_name: '',
                              batch_number: '',
                              expiry_date: ''
                            });
                            setEditingVaccination(vaccine);
                            setShowAddVaccination(true);
                          }}
                        >
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Mark as Given
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Syringe className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>No vaccinations in schedule</p>
              </div>
            )}
          </div>
      )}

      {/* Mark Vaccination as Given Form Dialog */}
      <Dialog open={showAddVaccination} onOpenChange={(open) => {
        if (!open) {
          setShowAddVaccination(false);
          setEditingVaccination(null);
          setVaccinationForm({ vaccine_name: '', given_date: '', trade_name: '', batch_number: '', expiry_date: '' });
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Mark Vaccination as Given</DialogTitle>
            <DialogDescription>
              Record the vaccination details and mark it as administered.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Vaccine Name</Label>
              <Input
                value={vaccinationForm.vaccine_name}
                disabled
                className="bg-gray-50"
              />
            </div>
            
            <div>
              <Label htmlFor="given_date">Date Given *</Label>
              <div className="relative">
                <DateInput
                  id="given_date"
                  value={vaccinationForm.given_date}
                  onChange={(e) => setVaccinationForm({...vaccinationForm, given_date: e.target.value})}
                  onKeyDown={(e) => e.preventDefault()}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-base cursor-pointer"
                  style={{ colorScheme: 'light' }}
                />
                {vaccinationForm.given_date && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setVaccinationForm({...vaccinationForm, given_date: ''});
                    }}
                    className="absolute right-8 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 z-10"
                    title="Clear date"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
            
            <div>
              <Label htmlFor="trade_name">Trade Name</Label>
              <Input
                id="trade_name"
                placeholder="Enter trade name (optional)"
                value={vaccinationForm.trade_name || ''}
                onChange={(e) => setVaccinationForm({...vaccinationForm, trade_name: e.target.value})}
              />
            </div>
            
            <div>
              <Label htmlFor="batch_number">Batch Number</Label>
              <Input
                id="batch_number"
                placeholder="Enter batch number (optional)"
                value={vaccinationForm.batch_number || ''}
                onChange={(e) => setVaccinationForm({...vaccinationForm, batch_number: e.target.value})}
              />
            </div>
            
            <div>
              <Label htmlFor="expiry_date">Expiry Date</Label>
              <Input
                id="expiry_date"
                type="text"
                placeholder="DD/MM/YYYY"
                value={vaccinationForm.expiry_date || ''}
                onChange={handleDateInput(setVaccinationForm, 'expiry_date')}
                onKeyDown={handleDateKeyDown(setVaccinationForm, 'expiry_date')}
                maxLength={10}
              />
            </div>
            
            <div className="flex gap-2">
              <Button 
                onClick={handleEditVaccination}
                className="flex-1 bg-green-600 hover:bg-green-700"
                disabled={submitting}
              >
                {submitting ? 'Saving...' : 'Mark as Given'}
              </Button>
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowAddVaccination(false);
                  setEditingVaccination(null);
                  setVaccinationForm({ vaccine_name: '', given_date: '', trade_name: '', batch_number: '', expiry_date: '' });
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>


      {/* Add New Vaccination Dialog (matches Baby App) */}
      <Dialog open={showAddNewVaccination} onOpenChange={setShowAddNewVaccination}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add New Vaccination</DialogTitle>
            <DialogDescription>
              Add a new vaccination to {selectedBaby?.name || 'baby'}'s schedule
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddNewVaccination} className="space-y-4">
            <div>
              <Label htmlFor="add_vaccine_name">Vaccine Name *</Label>
              <Input
                id="add_vaccine_name"
                value={addVaccinationForm.vaccine_name}
                onChange={(e) => setAddVaccinationForm({...addVaccinationForm, vaccine_name: e.target.value})}
                placeholder="e.g., BCG, Hepatitis B"
                required
              />
            </div>

            <div>
              <Label htmlFor="add_trade_name">Trade Name (Optional)</Label>
              <Input
                id="add_trade_name"
                value={addVaccinationForm.trade_name}
                onChange={(e) => setAddVaccinationForm({...addVaccinationForm, trade_name: e.target.value})}
                placeholder="e.g., Brand name"
              />
            </div>

            <div>
              <Label htmlFor="add_given_date">Date Given *</Label>
              <div className="relative">
                <Input
                  id="add_given_date"
                  value={addVaccinationForm.given_date}
                  onChange={handleAddVaccinationDateInput}
                  placeholder="DD/MM/YYYY"
                  maxLength={10}
                  required
                />
                <Calendar className="absolute right-3 top-2.5 h-5 w-5 text-gray-400 pointer-events-none" />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  setShowAddNewVaccination(false);
                  setAddVaccinationForm({ vaccine_name: '', trade_name: '', given_date: '' });
                }}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? 'Adding...' : 'Add Vaccination'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>


      {/* Growth Tracker Dialog - Fullscreen */}
      <Dialog open={showGrowthDialog} onOpenChange={setShowGrowthDialog}>
        <DialogContent className="max-w-full h-full m-0 p-0 rounded-none overflow-y-auto">
          <DialogTitle className="sr-only">Growth Tracker</DialogTitle>
          <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="dashboard-header">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setShowGrowthDialog(false)}
                    className="text-white hover:bg-white hover:bg-opacity-20"
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </Button>
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                      <TrendingUp className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h1 className="text-xl font-bold" style={{fontFamily: 'Space Grotesk'}}>Growth Tracker</h1>
                      <p className="text-blue-200 text-sm">{selectedBaby?.name}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="px-6 py-6">
              <GrowthTrackerSection 
                growthRecords={babyDetails?.growth || []}
                baby={babyDetails?.baby}
                onAdd={() => {
                  const today = new Date();
                  const day = String(today.getDate()).padStart(2, '0');
                  const month = String(today.getMonth() + 1).padStart(2, '0');
                  const year = today.getFullYear();
                  const todayFormatted = `${day}/${month}/${year}`;
                  setGrowthForm({ date: todayFormatted, weight: '', height: '', head_circumference: '', notes: '' });
                  setShowAddGrowth(true);
                }}
                onDelete={handleDeleteGrowth}
                formatDate={formatDate}
                viewOnlyMode={false}
                currentDoctorId={user?.id}
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Growth Record Form Dialog */}
      <Dialog open={showAddGrowth || editingGrowth !== null} onOpenChange={(open) => {
        if (!open) {
          setShowAddGrowth(false);
          setEditingGrowth(null);
          setGrowthForm({ date: '', weight: '', height: '', head_circumference: '', notes: '' });
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="flex items-center justify-between pr-6">
              <div>
                <DialogTitle>{editingGrowth ? 'Edit' : 'Add'} Growth Record</DialogTitle>
                <DialogDescription>
                  {editingGrowth ? 'Update the growth measurements below.' : 'Record new growth measurements for this baby.'}
                </DialogDescription>
              </div>
              {/* Voice Rx for Growth Record */}
              {aiFeatureSettings.voice_dictation_enabled && hospitalAiFeaturesEnabled && (
                <GrowthRecordVoiceRx
                  patientContext={selectedBaby ? `Patient: ${selectedBaby.name}, DOB: ${selectedBaby.date_of_birth || 'Unknown'}` : ''}
                  disabled={false}
                  onEntitiesExtracted={(entities) => {
                    setGrowthForm(prev => ({
                      ...prev,
                      ...(entities.weight && { weight: entities.weight }),
                      ...(entities.height && { height: entities.height }),
                      ...(entities.head_circumference && { head_circumference: entities.head_circumference }),
                      ...(entities.date && { date: entities.date }),
                      ...(entities.notes && { 
                        notes: prev.notes 
                          ? `${prev.notes}\n${entities.notes}` 
                          : entities.notes 
                      })
                    }));
                  }}
                />
              )}
            </div>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Date (DD/MM/YYYY) *</Label>
              <Input
                type="text"
                placeholder="DD/MM/YYYY"
                value={growthForm.date}
                onChange={handleDateInput(setGrowthForm, 'date')}
                onKeyDown={handleDateKeyDown(setGrowthForm, 'date')}
                maxLength={10}
                required
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Height (cm) *</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={growthForm.height}
                  onChange={(e) => setGrowthForm({...growthForm, height: e.target.value})}
                  placeholder="50.0"
                  min="30"
                  max="200"
                  required
                />
              </div>
              
              <div>
                <Label>Weight (kg) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={growthForm.weight}
                  onChange={(e) => setGrowthForm({...growthForm, weight: e.target.value})}
                  placeholder="3.5"
                  min="1"
                  max="50"
                  required
                />
              </div>
            </div>
            
            <div>
              <Label>Head Circumference (cm)</Label>
              <Input
                type="number"
                step="0.1"
                value={growthForm.head_circumference}
                onChange={(e) => setGrowthForm({...growthForm, head_circumference: e.target.value})}
                placeholder="35.0"
                min="20"
                max="60"
              />
            </div>
            
            <div>
              <Label>Notes</Label>
              <Textarea
                value={growthForm.notes}
                onChange={(e) => setGrowthForm({...growthForm, notes: e.target.value})}
                placeholder="Any advice or notes about this measurement..."
                rows={3}
              />
            </div>
            
            <div className="flex gap-2 pt-4">
              <Button 
                onClick={editingGrowth ? handleEditGrowth : handleAddGrowth}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
                disabled={submitting}
              >
                {submitting ? 'Saving...' : (editingGrowth ? 'Update' : 'Save Record')}
              </Button>
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowAddGrowth(false);
                  setEditingGrowth(null);
                  setGrowthForm({ date: '', weight: '', height: '', head_circumference: '', notes: '' });
                }}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Birth Record Dialog - Fullscreen */}
      <Dialog open={showBirthRecordDialog} onOpenChange={setShowBirthRecordDialog}>
        <DialogContent className="max-w-full h-full m-0 p-0 rounded-none overflow-y-auto">
          <DialogTitle className="sr-only">Birth Record</DialogTitle>
          <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="dashboard-header">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setShowBirthRecordDialog(false)}
                    className="text-white hover:bg-white hover:bg-opacity-20"
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </Button>
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                      <Baby className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h1 className="text-xl font-bold" style={{fontFamily: 'Space Grotesk'}}>Birth Record</h1>
                      <p className="text-blue-200 text-sm">{selectedBaby?.name}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="px-6 py-6">
              <BirthRecordSection 
                baby={babyDetails?.baby || {}}
                onUpdate={openEditBirthRecord}
                viewOnlyMode={false}
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Birth Record Form Dialog */}
      <Dialog open={showEditBirthRecord} onOpenChange={setShowEditBirthRecord}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between pr-12">
              <div>
                <DialogTitle>Update Birth Record</DialogTitle>
                <DialogDescription>
                  Update the birth record information and medical details.
                </DialogDescription>
              </div>
              {/* Voice Rx for Birth Record */}
              {aiFeatureSettings.voice_dictation_enabled && hospitalAiFeaturesEnabled && (
                <BirthRecordVoiceRx
                  babyContext={selectedBaby ? `Baby: ${selectedBaby.name}, DOB: ${selectedBaby.date_of_birth || 'Unknown'}` : ''}
                  disabled={false}
                  onEntitiesExtracted={(entities) => {
                    setBirthRecordForm(prev => ({
                      ...prev,
                      ...(entities.sex && { sex: entities.sex }),
                      ...(entities.mode_of_delivery && { mode_of_delivery: entities.mode_of_delivery }),
                      ...(entities.birth_weight && { birth_weight: String(entities.birth_weight) }),
                      ...(entities.birth_length && { birth_length: String(entities.birth_length) }),
                      ...(entities.head_circumference && { birth_head_circumference: String(entities.head_circumference) }),
                      ...(entities.blood_group && { blood_group: entities.blood_group }),
                      ...(entities.mother_name && { mother_name: entities.mother_name }),
                      ...(entities.father_name && { father_name: entities.father_name }),
                      ...(entities.family_history && { 
                        family_history: prev.family_history 
                          ? `${prev.family_history}\n${entities.family_history}` 
                          : entities.family_history 
                      }),
                      ...(entities.allergies && { 
                        allergies: prev.allergies 
                          ? `${prev.allergies}\n${entities.allergies}` 
                          : entities.allergies 
                      }),
                      ...(entities.remarks && { 
                        remarks: prev.remarks 
                          ? `${prev.remarks}\n${entities.remarks}` 
                          : entities.remarks 
                      }),
                      ...(entities.antenatal_history && { 
                        antenatal_history: prev.antenatal_history 
                          ? `${prev.antenatal_history}\n${entities.antenatal_history}` 
                          : entities.antenatal_history 
                      }),
                      ...(entities.birth_history && { 
                        birth_history: prev.birth_history 
                          ? `${prev.birth_history}\n${entities.birth_history}` 
                          : entities.birth_history 
                      })
                    }));
                  }}
                />
              )}
            </div>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Sex *</Label>
                <Select 
                  value={birthRecordForm.sex} 
                  onValueChange={(value) => setBirthRecordForm({...birthRecordForm, sex: value})}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select sex" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Male">Male</SelectItem>
                    <SelectItem value="Female">Female</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Mode of Delivery *</Label>
                <Select 
                  value={birthRecordForm.mode_of_delivery} 
                  onValueChange={(value) => setBirthRecordForm({...birthRecordForm, mode_of_delivery: value})}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select delivery mode" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Normal">Normal</SelectItem>
                    <SelectItem value="Cesarean">Cesarean</SelectItem>
                    <SelectItem value="Cesarean Section">Cesarean Section</SelectItem>
                    <SelectItem value="Assisted">Assisted</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Birth Weight (kg) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={birthRecordForm.birth_weight}
                  onChange={(e) => setBirthRecordForm({...birthRecordForm, birth_weight: e.target.value})}
                  placeholder="3.5"
                  required
                />
              </div>
              
              <div>
                <Label>Birth Length (cm)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={birthRecordForm.birth_length}
                  onChange={(e) => setBirthRecordForm({...birthRecordForm, birth_length: e.target.value})}
                  placeholder="50"
                />
              </div>
              
              <div>
                <Label>Head Circumference (cm)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={birthRecordForm.birth_head_circumference}
                  onChange={(e) => setBirthRecordForm({...birthRecordForm, birth_head_circumference: e.target.value})}
                  placeholder="35"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Blood Group</Label>
                <Select 
                  value={birthRecordForm.blood_group} 
                  onValueChange={(value) => setBirthRecordForm({...birthRecordForm, blood_group: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select blood group" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="not_specified">Not specified</SelectItem>
                    <SelectItem value="A+">A+</SelectItem>
                    <SelectItem value="A-">A-</SelectItem>
                    <SelectItem value="B+">B+</SelectItem>
                    <SelectItem value="B-">B-</SelectItem>
                    <SelectItem value="AB+">AB+</SelectItem>
                    <SelectItem value="AB-">AB-</SelectItem>
                    <SelectItem value="O+">O+</SelectItem>
                    <SelectItem value="O-">O-</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Mother's Name</Label>
                <Input
                  value={birthRecordForm.mother_name}
                  onChange={(e) => setBirthRecordForm({...birthRecordForm, mother_name: e.target.value})}
                  placeholder="Mother's full name"
                />
              </div>
              
              <div>
                <Label>Father's Name</Label>
                <Input
                  value={birthRecordForm.father_name}
                  onChange={(e) => setBirthRecordForm({...birthRecordForm, father_name: e.target.value})}
                  placeholder="Father's full name"
                />
              </div>
            </div>
            
            <div>
              <Label>Antenatal History</Label>
              <Textarea
                value={birthRecordForm.antenatal_history}
                onChange={(e) => setBirthRecordForm({...birthRecordForm, antenatal_history: e.target.value})}
                placeholder="Pregnancy history, complications, medications during pregnancy, prenatal checkups..."
                rows={3}
              />
            </div>
            
            <div>
              <Label>Birth History</Label>
              <Textarea
                value={birthRecordForm.birth_history}
                onChange={(e) => setBirthRecordForm({...birthRecordForm, birth_history: e.target.value})}
                placeholder="Labor details, birth complications, APGAR scores, resuscitation needed, NICU stay..."
                rows={3}
              />
            </div>
            
            <div>
              <Label>Family History</Label>
              <Textarea
                value={birthRecordForm.family_history}
                onChange={(e) => setBirthRecordForm({...birthRecordForm, family_history: e.target.value})}
                placeholder="Any relevant family medical history..."
                rows={2}
              />
            </div>
            
            <div>
              <Label>Known Allergies</Label>
              <Textarea
                value={birthRecordForm.allergies}
                onChange={(e) => setBirthRecordForm({...birthRecordForm, allergies: e.target.value})}
                placeholder="Any known allergies..."
                rows={2}
              />
            </div>
            
            <div>
              <Label>Additional Remarks</Label>
              <Textarea
                value={birthRecordForm.remarks}
                onChange={(e) => setBirthRecordForm({...birthRecordForm, remarks: e.target.value})}
                placeholder="Any advice, notes or remarks..."
                rows={2}
              />
            </div>
            
            <div className="flex gap-2">
              <Button 
                onClick={handleUpdateBirthRecord}
                className="flex-1 bg-purple-600 hover:bg-purple-700"
                disabled={submitting}
              >
                {submitting ? 'Saving...' : 'Update'}
              </Button>
              <Button 
                variant="outline" 
                onClick={() => setShowEditBirthRecord(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Medical Sessions Dialog - Fullscreen */}
      <Dialog open={showMedicalSessionsDialog} onOpenChange={setShowMedicalSessionsDialog}>
        <DialogContent className="max-w-full h-full m-0 p-0 rounded-none overflow-y-auto">
          <DialogTitle className="sr-only">Medical Sessions</DialogTitle>
          <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="dashboard-header">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setShowMedicalSessionsDialog(false)}
                    className="text-white hover:bg-white hover:bg-opacity-20"
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </Button>
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                      <Stethoscope className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h1 className="text-xl font-bold" style={{fontFamily: 'Space Grotesk'}}>Medical Sessions</h1>
                      <p className="text-blue-200 text-sm">{selectedBaby?.name}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="px-6 py-6">
              <MedicalSessionsSection 
                sessions={babyDetails?.sessions || []}
                baby={babyDetails?.baby || selectedBaby}
                onAdd={async () => {
                  try {
                    console.log('Add Session clicked', { user, selectedBaby, currentAppointment });
                    const today = new Date();
                    const day = String(today.getDate()).padStart(2, '0');
                    const month = String(today.getMonth() + 1).padStart(2, '0');
                    const year = today.getFullYear();
                    const todayFormatted = `${day}/${month}/${year}`;
                    
                    // Try to fetch MA vitals if appointment is selected
                    let initialVitals = {
                      temperature: '',
                      pulse: '',
                      respiratory_rate: '',
                      spo2: '',
                      blood_pressure: '',
                      height: '',
                      weight: ''
                    };
                    
                    if (currentAppointmentId) {
                      const maVitals = await fetchMAVitals(currentAppointmentId);
                      if (maVitals) {
                        initialVitals = {
                          temperature: maVitals.temperature ? String(maVitals.temperature) : '',
                          pulse: maVitals.pulse ? String(maVitals.pulse) : '',
                          respiratory_rate: maVitals.respiratory_rate ? String(maVitals.respiratory_rate) : '',
                          spo2: maVitals.spo2 ? String(maVitals.spo2) : '',
                          blood_pressure: maVitals.blood_pressure || '',
                          height: maVitals.height ? String(maVitals.height) : '',
                          weight: maVitals.weight ? String(maVitals.weight) : ''
                        };
                        toast.info(`Vitals pre-filled by ${maVitals.entered_by_name}`);
                      }
                    }
                    
                    // Check if this is a follow-up appointment
                    let foundPreviousSession = null;
                    if (currentAppointment?.appointment_type === 'followup' && currentAppointment?.followup_of_date && babyDetails?.sessions) {
                      // Find the previous session by date
                      foundPreviousSession = findPreviousSessionByDate(
                        babyDetails.sessions, 
                        currentAppointment.followup_of_date, 
                        user?.id
                      );
                      
                      if (foundPreviousSession) {
                        console.log('Found previous session for follow-up:', foundPreviousSession);
                        setFollowUpFromSession(foundPreviousSession);
                      } else {
                        console.log('Could not find previous session for date:', currentAppointment.followup_of_date);
                        setFollowUpFromSession(null);
                      }
                    } else {
                      setFollowUpFromSession(null);
                    }
                    
                    setSessionForm({ 
                      session_date: todayFormatted, 
                      doctor_name: getFormattedDoctorName(user) || 'Dr. Doctor',
                      chief_complaints: '',
                      diagnosis: '', 
                      medications: [{ name: '', timing: 'Before Food', duration: '', time_of_day: [], frequency: '', medication_type: '', treatment_for: '' }],
                      notes: '',
                      follow_up_of: foundPreviousSession?.id || '',
                      tests: '',
                      ...initialVitals
                    });
                    setSelectedFiles([]);
                    // Load previous sessions for this doctor to show Last Visit indicator
                    if (selectedBaby?.id && user?.id) {
                      loadPreviousDoctorSessions(selectedBaby.id, user.id);
                    }
                    setShowAddSession(true);
                  } catch (error) {
                    console.error('Error in onAdd:', error);
                    toast.error('Failed to open form. Please try again.');
                  }
                }}
                onFollowUp={createFollowUpSession}
                onEdit={(session) => {
                  startEditSession(session);
                }}
                onDelete={handleDeleteSession}
                onViewPrevious={(session) => {
                  setPreviousSessionData(session);
                  setShowPreviousSessionDialog(true);
                }}
                onViewSOAP={handleViewSOAPNotes}
                formatDate={formatDate}
                viewOnlyMode={false}
                currentUserId={user?.id}
                API={API}
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Keep old code for reference */}
      {false && (
              <div className="text-center py-8 text-gray-500">
                <Stethoscope className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>No medical sessions recorded</p>
              </div>
      )}

      {/* Add/Edit Medical Session Form Dialog */}
      <Dialog open={showAddSession || editingSession !== null} onOpenChange={(open) => {
        if (!open) {
          setShowAddSession(false);
          setEditingSession(null);
          setFollowUpFromSession(null);
          setCurrentAppointment(null); // Clear appointment context when closing
          setSelectedFiles([]);
          setUploadedReports([]); // Clear uploaded reports
          setSessionForm({ 
            session_date: '', 
            doctor_name: '', 
            
             
            diagnosis: '', 
            assessment: '',
            medications: [{ name: '', timing: 'Before Food', duration: '', time_of_day: [], frequency: '', medication_type: '', treatment_for: '' }],
            temperature: '',
            notes: '',
            follow_up_of: '',
            pulse: '',
            respiratory_rate: '',
            spo2: '',
            blood_pressure: '',
            height: '',
            weight: '',
            tests: ''
          });
        }
      }}>
        <DialogContent className={`${followUpFromSession ? 'max-w-[95vw] lg:max-w-5xl' : 'max-w-full sm:max-w-2xl'} h-[95vh] sm:h-auto sm:max-h-[92vh] overflow-hidden p-0`}>
          <div className="flex h-full max-h-[92vh]">
            {/* Left Panel - Past Snapshot with Follow-up Chain (ONLY show for follow-up sessions) */}
            {followUpFromSession && (
              <div className="w-64 md:w-72 border-r bg-gray-50 flex-shrink-0 hidden md:block overflow-y-auto">
                <div className="p-3 md:p-4 border-b bg-white sticky top-0 z-10">
                  <h3 className="font-semibold text-gray-800 flex items-center gap-2 text-sm">
                    <ChevronRight className="w-4 h-4 text-blue-600" />
                    Past Snapshots
                  </h3>
                  <p className="text-xs text-gray-500 mt-1">
                    Follow-up chain history
                  </p>
                </div>
                
                {/* Render follow-up chain - get all linked sessions */}
                {(() => {
                  // Build the follow-up chain from the selected session backwards
                  const getFollowUpChain = () => {
                    const chain = [followUpFromSession];
                    let currentSession = followUpFromSession;
                    
                    // Trace back through follow_up_of links
                    while (currentSession?.follow_up_of && babyDetails?.sessions) {
                      const prevSession = babyDetails.sessions.find(s => s.id === currentSession.follow_up_of);
                      if (prevSession && !chain.find(s => s.id === prevSession.id)) {
                        chain.push(prevSession);
                        currentSession = prevSession;
                      } else {
                        break;
                      }
                    }
                    
                    // Return chain with latest first (already in correct order since we started from selected)
                    return chain;
                  };
                  
                  const followUpChain = getFollowUpChain();
                  
                  return followUpChain.map((session, chainIndex) => (
                    <div key={session.id} className={`p-3 md:p-4 space-y-2 text-sm ${chainIndex > 0 ? 'border-t-2 border-blue-200 bg-blue-50/30' : ''}`}>
                      {/* Session header with date */}
                      <div className="flex items-center justify-between mb-2">
                        <span className={`text-xs font-semibold ${chainIndex === 0 ? 'text-blue-700' : 'text-gray-600'}`}>
                          {chainIndex === 0 ? '📋 Selected Session' : `📁 Previous Visit ${chainIndex}`}
                        </span>
                        <span className="text-xs text-gray-500 bg-white px-2 py-0.5 rounded border">
                          {formatDate(session.session_date)}
                        </span>
                      </div>
                      
                      {/* Chief Complaints */}
                      <div className="flex items-start gap-2">
                        <span className="text-gray-400">•</span>
                        <span className="text-gray-700">
                          <span className="font-medium">CC:</span> {session.chief_complaints ? session.chief_complaints.substring(0, 60) + (session.chief_complaints.length > 60 ? '...' : '') : 'Not recorded'}
                        </span>
                      </div>
                      
                      {/* Diagnosis */}
                      <div className="flex items-start gap-2">
                        <span className="text-gray-400">•</span>
                        <span className="text-gray-700">
                          <span className="font-medium">Dx:</span> {session.diagnosis ? session.diagnosis.substring(0, 50) + (session.diagnosis.length > 50 ? '...' : '') : 'Not recorded'}
                        </span>
                      </div>
                      
                      {/* Medications - check both 'medication' (string) and 'medications' (array) */}
                      <div className="space-y-1">
                        <div className="flex items-start gap-2">
                          <span className="text-gray-400">•</span>
                          <span className="font-medium text-gray-700">Rx:</span>
                        </div>
                        {(() => {
                          // Helper to parse medication name and extract clean name
                          const parseMedicationName = (fullName) => {
                            if (!fullName) return { name: '', type: '', generic: '', treatmentFor: '', timing: '' };
                            
                            // Extract clean name (before any [Type: or [Generic: or (After Food))
                            let cleanName = fullName;
                            const typeMatch = fullName.match(/^(.+?)\s*\[Type:/i);
                            if (typeMatch) cleanName = typeMatch[1].trim();
                            else {
                              const genericMatch = fullName.match(/^(.+?)\s*\[Generic:/i);
                              if (genericMatch) cleanName = genericMatch[1].trim();
                              else {
                                const forMatch = fullName.match(/^(.+?)\s*\[For:/i);
                                if (forMatch) cleanName = forMatch[1].trim();
                                else {
                                  const timingMatch = fullName.match(/^(.+?)\s*\((Before|After)\s+Food\)/i);
                                  if (timingMatch) cleanName = timingMatch[1].trim();
                                }
                              }
                            }
                            
                            // Extract type
                            const typeExtract = fullName.match(/\[Type:\s*([^\]]+)\]/i);
                            const type = typeExtract ? typeExtract[1].trim().toLowerCase() : '';
                            
                            // Extract generic
                            const genericExtract = fullName.match(/\[Generic:\s*([^\]]+)\]/i);
                            const generic = genericExtract ? genericExtract[1].trim() : '';
                            
                            // Extract treatment for
                            const treatmentExtract = fullName.match(/\[For:\s*([^\]]+)\]/i);
                            const treatmentFor = treatmentExtract ? treatmentExtract[1].trim() : '';
                            
                            // Extract timing from name string
                            const timingExtract = fullName.match(/\((Before|After)\s+Food\)/i);
                            const timing = timingExtract ? `${timingExtract[1]} Food` : '';
                            
                            return { name: cleanName, type, generic, treatmentFor, timing };
                          };
                          
                          // Prefer 'medications' array if it exists and has items
                          const meds = session.medications || [];
                          
                          // If medications array has items, use it
                          if (meds.length > 0) {
                            return (
                              <div className="ml-4 space-y-1 max-h-32 overflow-y-auto">
                                {meds.map((med, idx) => {
                                  const rawName = typeof med === 'string' ? med : (med.name || med.medication_name || '');
                                  const parsed = parseMedicationName(rawName);
                                  
                                  // Use parsed values or fall back to object properties
                                  const medName = parsed.name || rawName;
                                  const medType = parsed.type || (typeof med === 'object' ? (med.medication_type || '') : '');
                                  const generic = parsed.generic || (typeof med === 'object' ? (med.generic_name || '') : '');
                                  const treatmentFor = parsed.treatmentFor || (typeof med === 'object' ? (med.treatment_for || '') : '');
                                  const timing = parsed.timing || (typeof med === 'object' ? (med.timing || '') : '');
                                  const duration = typeof med === 'object' ? (med.duration || '') : '';
                                  const timeOfDay = typeof med === 'object' && med.time_of_day ? 
                                    (Array.isArray(med.time_of_day) ? med.time_of_day.join(', ') : med.time_of_day) : '';
                                  
                                  if (!medName) return null;
                                  
                                  // Build details line (timing - time of day - duration)
                                  const details = [];
                                  if (timing) details.push(timing);
                                  if (timeOfDay) details.push(timeOfDay);
                                  if (duration) details.push(duration);
                                  
                                  return (
                                    <div key={idx} className="text-xs bg-white rounded p-1.5 border border-gray-200">
                                      <div className="flex items-start gap-1.5">
                                        {medType && <MedicationIcon type={medType} size={14} />}
                                        <div className="flex-1 min-w-0">
                                          <div className="flex flex-wrap items-center gap-1">
                                            <span className="font-medium text-gray-800">{medName}</span>
                                            {generic && <span className="text-gray-500">[{generic}]</span>}
                                          </div>
                                          {treatmentFor && <div className="text-blue-600">[For: {treatmentFor}]</div>}
                                          {details.length > 0 && (
                                            <div className="text-gray-500 text-[10px] mt-0.5">
                                              {details.join(' - ')}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          }
                          
                          // Fall back to 'medication' string field if array is empty
                          // The medication string might contain multiple meds separated by ' | '
                          if (session.medication && typeof session.medication === 'string') {
                            const medStrings = session.medication.split(' | ').filter(s => s.trim());
                            
                            if (medStrings.length === 0) {
                              return <span className="ml-4 text-gray-500 text-xs">None</span>;
                            }
                            
                            return (
                              <div className="ml-4 space-y-1 max-h-32 overflow-y-auto">
                                {medStrings.map((medStr, idx) => {
                                  const parsed = parseMedicationName(medStr.trim());
                                  const medType = parsed.type;
                                  
                                  // Build details line
                                  const details = [];
                                  if (parsed.timing) details.push(parsed.timing);
                                  // Try to extract time of day from the string
                                  const timeMatch = medStr.match(/(?:\)\s*-\s*|\s+-\s+)((?:Morning|Afternoon|Evening|Night|Bedtime)(?:\s*,\s*(?:Morning|Afternoon|Evening|Night|Bedtime))*)/i);
                                  if (timeMatch) details.push(timeMatch[1]);
                                  // Try to extract duration
                                  const durationMatch = medStr.match(/(?:for\s+)?(\d+\s*(?:days?|weeks?|months?))/i);
                                  if (durationMatch) details.push(durationMatch[1]);
                                  
                                  return (
                                    <div key={idx} className="text-xs bg-white rounded p-1.5 border border-gray-200">
                                      <div className="flex items-start gap-1.5">
                                        {medType && <MedicationIcon type={medType} size={14} />}
                                        <div className="flex-1 min-w-0">
                                          <div className="flex flex-wrap items-center gap-1">
                                            <span className="font-medium text-gray-800">{parsed.name || medStr.substring(0, 30)}</span>
                                            {parsed.generic && <span className="text-gray-500">[{parsed.generic}]</span>}
                                          </div>
                                          {parsed.treatmentFor && <div className="text-blue-600">[For: {parsed.treatmentFor}]</div>}
                                          {details.length > 0 && (
                                            <div className="text-gray-500 text-[10px] mt-0.5">
                                              {details.join(' - ')}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          }
                          
                          // No medications found
                          return <span className="ml-4 text-gray-500 text-xs">None</span>;
                        })()}
                      </div>
                      
                      {/* Vitals - compact for chain */}
                      <div className="flex items-start gap-2">
                        <span className="text-gray-400">•</span>
                        <span className="text-gray-700 text-xs">
                          <span className="font-medium">Vitals:</span>{' '}
                          {[
                            session.blood_pressure && `BP ${session.blood_pressure}`,
                            session.temperature && `${session.temperature}°F`,
                            session.pulse && `PR ${session.pulse}`,
                            session.respiratory_rate && `RR ${session.respiratory_rate}`,
                            session.spo2 && `SpO2 ${session.spo2}%`
                          ].filter(Boolean).join(', ') || 'Not recorded'}
                        </span>
                      </div>
                      
                      {/* Tests - show if available */}
                      {session.tests && (
                        <div className="flex items-start gap-2">
                          <span className="text-gray-400">•</span>
                          <span className="text-gray-700 text-xs">
                            <span className="font-medium">Tests:</span>{' '}
                            {session.tests.substring(0, 50)}{session.tests.length > 50 ? '...' : ''}
                          </span>
                        </div>
                      )}
                      
                      {/* Advice/Notes - show if available */}
                      {session.notes && (
                        <div className="flex items-start gap-2">
                          <span className="text-gray-400">•</span>
                          <span className="text-gray-700 text-xs">
                            <span className="font-medium">Advice:</span>{' '}
                            {session.notes.substring(0, 50)}{session.notes.length > 50 ? '...' : ''}
                          </span>
                        </div>
                      )}
                    </div>
                  ));
                })()}
              </div>
            )}
            
            {/* Main Form Panel - with proper scrolling */}
            <div className="flex-1 overflow-y-auto p-4 sm:p-6">
              <DialogHeader>
                <DialogTitle className="flex items-center justify-between pr-8">
                  <div className="flex items-center gap-3">
                    <span>{editingSession ? 'Edit' : followUpFromSession ? 'Create Follow-up' : 'Add'} Medical Session</span>
                    {/* Last Visit Indicator - show for new sessions and follow-ups */}
                    {!editingSession && (lastVisitInfo || followUpFromSession) && (
                      <Badge className="text-xs bg-blue-100 text-blue-700 border border-blue-200">
                        Last Visit: {(() => {
                          const lastDate = followUpFromSession 
                            ? new Date(followUpFromSession.session_date)
                            : lastVisitInfo?.date;
                          if (!lastDate) return 'Unknown';
                          const today = new Date();
                          const diffTime = today - lastDate;
                          const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
                          
                          if (diffDays === 0) return 'Today';
                          if (diffDays === 1) return '1 day ago';
                          if (diffDays < 30) return `${diffDays} days ago`;
                          
                          const months = Math.floor(diffDays / 30);
                          const remainingDays = diffDays % 30;
                          
                          if (months === 1 && remainingDays === 0) return '1 month ago';
                          if (months === 1 && remainingDays > 0) return `1 month ${remainingDays} days ago`;
                          if (months > 1 && remainingDays === 0) return `${months} months ago`;
                          return `${months} months ${remainingDays} days ago`;
                        })()}
                      </Badge>
                    )}
                  </div>
                  {/* Voice Dictate Button - controlled by Central Admin setting */}
                  {!editingSession && aiFeatureSettings.voice_dictation_enabled && hospitalAiFeaturesEnabled && (
                    <GlobalVoiceDictation
                      patientContext={selectedBaby ? `Patient: ${selectedBaby.name}, Age: ${selectedBaby.date_of_birth ? calculateAge(selectedBaby.date_of_birth) : 'Unknown'}, Gender: ${selectedBaby.gender || 'Unknown'}` : ''}
                      disabled={false}
                      doctorId={user?.id}
                      previousSessionMedications={(() => {
                        // Get medications from followUpFromSession - check both array and string formats
                        if (!followUpFromSession) return [];
                        
                        // First try the medications array
                        if (followUpFromSession.medications && followUpFromSession.medications.length > 0) {
                          return followUpFromSession.medications;
                        }
                        
                        // Fall back to parsing the medication string (pipe-separated)
                        if (followUpFromSession.medication && typeof followUpFromSession.medication === 'string') {
                          const medStrings = followUpFromSession.medication.split(' | ').filter(s => s.trim());
                          return medStrings.map((medStr, idx) => {
                            const parsed = parseMedicationFromName(medStr.trim());
                            return {
                              id: idx,
                              name: parsed.name || medStr.trim(),
                              timing: parsed.timing || 'Before Food',
                              duration: parsed.duration || '',
                              time_of_day: parsed.timeOfDay || [],
                              medication_type: parsed.type || '',
                              treatment_for: parsed.treatmentFor || '',
                              generic_name: parsed.generic || ''
                            };
                          });
                        }
                        
                        return [];
                      })()}
                      onReuseMedicationsRequested={(filteredMeds) => {
                        // When doctor says "use same medication" - show reuse dialog with filtered meds
                        if (filteredMeds && filteredMeds.length > 0) {
                          // Parse the medications to extract clean data
                          const parsedMeds = filteredMeds.map((med, idx) => {
                            const rawName = typeof med === 'string' ? med : (med.name || med.medication_name || '');
                            const parsed = parseMedicationFromName(rawName);
                            return {
                              id: idx,
                              name: parsed.name || rawName,
                              timing: parsed.timing || med.timing || 'Before Food',
                              duration: parsed.duration || med.duration || '',
                              time_of_day: parsed.timeOfDay?.length > 0 ? parsed.timeOfDay : (med.time_of_day || []),
                              frequency: med.frequency || '',
                              medication_type: parsed.type || med.medication_type || '',
                              treatment_for: parsed.treatmentFor || med.treatment_for || '',
                              generic_name: parsed.generic || med.generic_name || ''
                            };
                          });
                          setReusableMedications(parsedMeds);
                          setSelectedReuseMedications(parsedMeds.map(m => m.id)); // Pre-select all
                          setShowReuseMedicationsDialog(true);
                        } else {
                          toast.info('No medications found from previous session to reuse');
                        }
                      }}
                      onPrescriptionParsed={(medications) => {
                        setSessionForm(prev => {
                          const existingMeds = prev.medications.filter(m => m.name.trim() !== '');
                          const newMeds = medications.map(med => ({
                            name: med.name || '',
                            timing: med.timing || 'After Food',
                            duration: med.duration || '',
                            time_of_day: med.time_of_day || [],
                            frequency: med.frequency || '',
                            medication_type: med.medication_type || '',
                            treatment_for: med.treatment_for || ''
                          }));
                          return {
                            ...prev,
                            medications: existingMeds.length > 0 
                              ? [...existingMeds, ...newMeds]
                              : newMeds.length > 0 ? newMeds : prev.medications
                          };
                        });
                      }}
                      onChiefComplaintsParsed={(text) => {
                        setSessionForm(prev => ({
                          ...prev,
                          chief_complaints: prev.chief_complaints 
                            ? `${prev.chief_complaints}\n${text}` 
                            : text
                        }));
                      }}
                      onDiagnosisParsed={(text) => {
                        setSessionForm(prev => ({
                          ...prev,
                          diagnosis: prev.diagnosis 
                            ? `${prev.diagnosis}\n${text}` 
                            : text
                        }));
                      }}
                      onTestsParsed={(text) => {
                        setSessionForm(prev => ({
                          ...prev,
                          tests: prev.tests 
                            ? `${prev.tests}, ${text}` 
                            : text
                        }));
                      }}
                      onAssessmentParsed={(text) => {
                        setSessionForm(prev => ({
                          ...prev,
                          assessment: prev.assessment 
                            ? `${prev.assessment}\n${text}` 
                            : text
                        }));
                      }}
                      onAdditionalNotesParsed={(text) => {
                        setSessionForm(prev => ({
                          ...prev,
                          additional_notes: prev.additional_notes 
                            ? `${prev.additional_notes}\n${text}` 
                            : text
                        }));
                      }}
                      onVitalsParsed={(vitals) => {
                        setSessionForm(prev => ({
                          ...prev,
                          ...(vitals.temperature && { temperature: String(vitals.temperature) }),
                          ...(vitals.pulse && { pulse: String(vitals.pulse) }),
                          ...(vitals.spo2 && { spo2: String(vitals.spo2) }),
                          ...(vitals.blood_pressure && { blood_pressure: String(vitals.blood_pressure) }),
                          ...(vitals.respiratory_rate && { respiratory_rate: String(vitals.respiratory_rate) }),
                          ...(vitals.height_cm && { height: String(vitals.height_cm) }),
                          ...(vitals.weight_kg && { weight: String(vitals.weight_kg) })
                        }));
                      }}
                    />
                  )}
                  {/* SOAP Button in Session Form - when editing session with AI SOAP notes */}
                  {editingSession && sessionForm.ai_soap_notes && (
                    <Button 
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => handleViewSOAPNotes(sessionForm.ai_soap_notes)}
                      className="text-purple-600 hover:text-purple-700 hover:bg-purple-50 border-purple-200 bg-purple-50 mr-2"
                      title="View AI SOAP Notes"
                    >
                      <FileText className="w-4 h-4 mr-1" />
                      SOAP Notes
                    </Button>
                  )}
                </DialogTitle>
                <DialogDescription>
                  {editingSession ? 'Update the medical session details below.' : 
                   followUpFromSession ? (
                     <>
                       Create a follow-up session for the previous medical visit.
                       {currentAppointment?.appointment_type === 'followup' && (
                         <span className="block mt-1 text-purple-600 font-medium">
                           📋 This is a follow-up appointment from {currentAppointment.followup_of_date}
                           {currentAppointment.is_free_visit && ' (Free within 7 days)'}
                         </span>
                       )}
                     </>
                   ) : 
                   'Record a new medical session with diagnosis and treatment details.'}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 mt-4">
            {/* Consult Assist Header - Only show if AI features enabled */}
            {aiFeatureSettings.ai_clinical_assistant_enabled && hospitalAiFeaturesEnabled && (
              <div className="flex items-center justify-between bg-gradient-to-r from-purple-50 to-blue-50 p-3 rounded-lg border border-purple-200">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-purple-600" />
                  <span className="font-medium text-purple-900">Consult Assist</span>
                  <Badge className="text-xs bg-purple-100 text-purple-700 border-purple-300">New</Badge>
                </div>
                <div className="flex items-center gap-2">
                  {/* Consult Assist Toggle */}
                  {!editingSession && (
                    <Button
                      type="button"
                      variant={showAIClinicalAssistant ? "default" : "outline"}
                      size="sm"
                      onClick={() => setShowAIClinicalAssistant(!showAIClinicalAssistant)}
                      className={showAIClinicalAssistant ? "bg-purple-600 hover:bg-purple-700" : "border-purple-300 text-purple-700"}
                    >
                      <svg className="w-4 h-4 mr-1" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M2 12h2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        <path d="M6 8v8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        <path d="M10 5v14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        <path d="M14 8v8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        <path d="M18 10v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        <path d="M22 12h-2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                      {showAIClinicalAssistant ? 'Hide Consult Assist' : 'Consult Assist'}
                    </Button>
                  )}
                </div>
              </div>
            )}
            
            {/* AI Clinical Assistant Component */}
            {showAIClinicalAssistant && !editingSession && aiFeatureSettings.ai_clinical_assistant_enabled && hospitalAiFeaturesEnabled && (
              <AIClinicalAssistant
                sessionId={editingSession?.id || 'new'}
                doctorId={user?.id}
                babyId={selectedBaby?.id}
                patientContext={selectedBaby ? `Patient: ${selectedBaby.name}, Age: ${selectedBaby.date_of_birth ? calculateAge(selectedBaby.date_of_birth) : 'Unknown'}, Gender: ${selectedBaby.gender || 'Unknown'}` : ''}
                onClose={() => setShowAIClinicalAssistant(false)}
                onAssessmentSaved={(assessmentId) => {
                  // Store the assessment ID to link with session when saved
                  setSessionForm(prev => ({ ...prev, ai_assessment_id: assessmentId }));
                }}
                onSOAPGenerated={(soapData) => {
                  // Apply SOAP data to form fields - DO NOT fill tests or advice fields
                  // Also save the full SOAP notes to be linked to the session
                  console.log('✅ SOAP Notes Applied:', soapData);
                  setSessionForm(prev => {
                    const updated = {
                      ...prev,
                      chief_complaints: soapData.extracted_fields?.chief_complaints || prev.chief_complaints,
                      assessment: soapData.extracted_fields?.assessment_text || soapData.assessment || prev.assessment,
                      diagnosis: soapData.extracted_fields?.diagnosis || prev.diagnosis,
                      notes: soapData.extracted_fields?.notes || prev.notes,
                      // Save full SOAP notes for session record (viewable by doctor later)
                      ai_soap_notes: soapData
                      // NOT filling: tests, advice - doctor should decide these
                    };
                    console.log('✅ Session form updated with ai_soap_notes:', updated.ai_soap_notes ? 'YES' : 'NO');
                    return updated;
                  });
                }}
                onEntitiesExtracted={(entities) => {
                  // Apply extracted entities - same format as Voice Dictate
                  const complaints = entities.chief_complaints_text || 
                                    entities.chief_complaints?.join(', ') || '';
                  
                  // Extract vitals if available - convert all values to strings for form
                  const vitals = entities.vitals || {};
                  
                  setSessionForm(prev => {
                    // Build updated form with vitals and complaints
                    let updated = {
                      ...prev,
                      chief_complaints: complaints || prev.chief_complaints,
                      // Apply vitals - convert to strings and only update if value exists
                      ...(vitals.temperature && { temperature: String(vitals.temperature) }),
                      ...(vitals.pulse && { pulse: String(vitals.pulse) }),
                      ...(vitals.spo2 && { spo2: String(vitals.spo2) }),
                      ...(vitals.blood_pressure && { blood_pressure: String(vitals.blood_pressure) }),
                      ...(vitals.respiratory_rate && { respiratory_rate: String(vitals.respiratory_rate) }),
                      ...(vitals.height_cm && { height: String(vitals.height_cm) }),
                      ...(vitals.weight_kg && { weight: String(vitals.weight_kg) })
                    };
                    
                    // Apply assessment if present
                    if (entities.assessment_text) {
                      updated.assessment = prev.assessment 
                        ? `${prev.assessment}\n${entities.assessment_text}` 
                        : entities.assessment_text;
                    }
                    
                    // Apply tests if present (tests_text from new format OR tests_mentioned from old)
                    const testsText = entities.tests_text || (entities.tests_mentioned?.length > 0 ? entities.tests_mentioned.join(', ') : '');
                    if (testsText) {
                      updated.tests = prev.tests 
                        ? `${prev.tests}, ${testsText}` 
                        : testsText;
                    }
                    
                    // Apply diagnosis if present
                    if (entities.diagnosis_text) {
                      updated.diagnosis = prev.diagnosis 
                        ? `${prev.diagnosis}, ${entities.diagnosis_text}` 
                        : entities.diagnosis_text;
                    }
                    
                    // Apply medications if present (same as GlobalVoiceDictation)
                    if (entities.medications && entities.medications.length > 0) {
                      const existingMeds = prev.medications.filter(m => m.name && m.name.trim() !== '');
                      const newMeds = entities.medications.map(med => ({
                        name: med.name || '',
                        timing: med.timing || 'After Food',
                        duration: med.duration || '',
                        time_of_day: med.time_of_day || [],
                        frequency: med.frequency || '',
                        medication_type: med.medication_type || '',
                        treatment_for: med.treatment_for || ''
                      }));
                      
                      updated.medications = existingMeds.length > 0 
                        ? [...existingMeds, ...newMeds]
                        : newMeds.length > 0 ? newMeds : prev.medications;
                      
                      toast.success(`${newMeds.length} medication(s) applied to form`);
                    }
                    
                    // Apply additional notes if present
                    if (entities.additional_notes) {
                      updated.additional_notes = prev.additional_notes 
                        ? `${prev.additional_notes}\n${entities.additional_notes}` 
                        : entities.additional_notes;
                    }
                    
                    return updated;
                  });
                }}
                onDiagnosisSuggested={(suggestions) => {
                  // Apply primary diagnosis suggestion
                  if (suggestions.primary_diagnosis?.name) {
                    setSessionForm(prev => ({
                      ...prev,
                      diagnosis: prev.diagnosis 
                        ? `${prev.diagnosis}, ${suggestions.primary_diagnosis.name}`
                        : suggestions.primary_diagnosis.name
                    }));
                  }
                }}
              />
            )}
            
            {/* SOAP Notes are now viewed via the SOAP button in the dialog header - removed inline display */}
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Session Date (DD/MM/YYYY) *</Label>
                <Input
                  type="text"
                  placeholder="DD/MM/YYYY"
                  value={sessionForm.session_date}
                  onChange={handleDateInput(setSessionForm, 'session_date')}
                  onKeyDown={handleDateKeyDown(setSessionForm, 'session_date')}
                  maxLength={10}
                  required
                  disabled
                  readOnly
                  className="text-sm md:text-base bg-gray-100 cursor-not-allowed"
                />
              </div>
              
              <div>
                <Label>Doctor Name *</Label>
                <Input
                  placeholder="Dr. Smith"
                  value={sessionForm.doctor_name}
                  onChange={(e) => setSessionForm(prev => ({ ...prev, doctor_name: e.target.value }))}
                  required
                  disabled
                  readOnly
                  className="text-sm md:text-base bg-gray-100 cursor-not-allowed"
                />
              </div>
            </div>
            
            {/* Follow-up Selection - Show only this doctor's previous sessions */}
            {babyDetails?.sessions && babyDetails.sessions.length > 0 && (
              <div>
                <Label>Follow-up of Previous Session (Optional)</Label>
                <select
                  value={followUpFromSession?.id || sessionForm.follow_up_of || ''}
                  onChange={(e) => {
                    const selectedSessionId = e.target.value;
                    if (selectedSessionId) {
                      // Find the selected session and set it for snapshot display
                      const selectedSession = babyDetails.sessions.find(s => s.id === selectedSessionId);
                      if (selectedSession) {
                        setFollowUpFromSession(selectedSession);
                      }
                    } else {
                      // Clear follow-up selection
                      setFollowUpFromSession(null);
                    }
                    setSessionForm(prev => ({ ...prev, follow_up_of: selectedSessionId }));
                  }}
                  className="w-full h-10 px-3 rounded-md border border-gray-300 bg-white text-sm"
                >
                  <option value="">Not a follow-up</option>
                  {/* Filter sessions by current doctor and sort by date (latest first) */}
                  {babyDetails.sessions
                    .filter(session => session.doctor_id === user?.id || session.doctor_name === sessionForm.doctor_name)
                    .sort((a, b) => new Date(b.session_date) - new Date(a.session_date))
                    .map((session) => (
                      <option key={session.id} value={session.id}>
                        {formatDate(session.session_date)} - {session.doctor_name || 'Doctor'} ({session.diagnosis || session.chief_complaints?.substring(0, 30) || session.chief_complaint?.substring(0, 30) || session.tests?.substring(0, 30) || 'No details'})
                      </option>
                    ))}
                </select>
              </div>
            )}
            
            {/* Background Section */}
            {selectedBaby && babyDetails && (babyDetails.conditions?.length > 0 || babyDetails.medications?.length > 0 || babyDetails.allergies?.length > 0) && (
              <div className="bg-blue-50 border border-blue-100 rounded p-2 max-w-2xl">
                <h4 className="text-xs font-medium text-gray-500 uppercase mb-1">Background</h4>
                <div className="text-sm text-gray-700 space-y-1">
                  {babyDetails.conditions && babyDetails.conditions.length > 0 && (
                    <div>
                      <span className="font-medium">Conditions: </span>
                      <div className="ml-2 space-y-0.5">
                        {babyDetails.conditions.map((c, idx) => (
                          <div key={idx}>
                            • {c.condition_name}
                            {c.medication && <span className="text-gray-600"> ({c.medication})</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {babyDetails.medications && babyDetails.medications.length > 0 && (
                    <div>
                      <span className="font-medium">Medications: </span>
                      <span>{babyDetails.medications.map(m => m.medication_name).join(', ')}</span>
                    </div>
                  )}
                  {babyDetails.allergies && babyDetails.allergies.length > 0 && (
                    <div>
                      <span className="font-medium">Allergies: </span>
                      <span className="text-red-600 font-medium">{babyDetails.allergies.map(a => a.allergen).join(', ')}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Vitals Section */}
            <div>
              <Label>Vitals</Label>
              {/* Row 1: Temp, Pulse, RR, SpO2, BP */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mt-2">
                {/* Temperature */}
                <div>
                  <div className="flex items-center justify-between mb-1 h-5">
                    <Label className="text-xs">Temp (°F)</Label>
                    {getVitalStatus('temp', sessionForm.temperature) && (
                      <Badge className={`text-xs px-1 py-0 ${getVitalStatus('temp', sessionForm.temperature).color}`}>
                        {getVitalStatus('temp', sessionForm.temperature).status}
                      </Badge>
                    )}
                  </div>
                  <Input
                    type="number"
                    step="0.1"
                    placeholder="98.6"
                    value={sessionForm.temperature}
                    onChange={(e) => setSessionForm(prev => ({ ...prev, temperature: e.target.value }))}
                    className="text-sm"
                  />
                </div>
                
                {/* Pulse with age-based range */}
                <div>
                  <div className="flex items-center justify-between mb-1 h-5">
                    <Label className="text-xs whitespace-nowrap">
                      Pulse{selectedBaby?.date_of_birth && <span className="text-[10px] text-gray-400 ml-1">({getPulseRangeByAge(getAgeInMonths(selectedBaby.date_of_birth)).range})</span>}
                    </Label>
                    {getVitalStatus('pulse', sessionForm.pulse, getAgeInMonths(selectedBaby?.date_of_birth)) && (
                      <Badge className={`text-xs px-1 py-0 ${getVitalStatus('pulse', sessionForm.pulse, getAgeInMonths(selectedBaby?.date_of_birth)).color}`}>
                        {getVitalStatus('pulse', sessionForm.pulse, getAgeInMonths(selectedBaby?.date_of_birth)).status}
                      </Badge>
                    )}
                  </div>
                  <Input
                    type="number"
                    placeholder="e.g. 72"
                    value={sessionForm.pulse}
                    onChange={(e) => setSessionForm(prev => ({ ...prev, pulse: e.target.value }))}
                    className="text-sm"
                  />
                </div>
                
                {/* Respiratory Rate with age-based range */}
                <div>
                  <div className="flex items-center justify-between mb-1 h-5">
                    <Label className="text-xs whitespace-nowrap">
                      RR{selectedBaby?.date_of_birth && <span className="text-[10px] text-gray-400 ml-1">({getRRRangeByAge(getAgeInMonths(selectedBaby.date_of_birth)).range})</span>}
                    </Label>
                    {getVitalStatus('respiratory_rate', sessionForm.respiratory_rate, getAgeInMonths(selectedBaby?.date_of_birth)) && (
                      <Badge className={`text-xs px-1 py-0 ${getVitalStatus('respiratory_rate', sessionForm.respiratory_rate, getAgeInMonths(selectedBaby?.date_of_birth)).color}`}>
                        {getVitalStatus('respiratory_rate', sessionForm.respiratory_rate, getAgeInMonths(selectedBaby?.date_of_birth)).status}
                      </Badge>
                    )}
                  </div>
                  <Input
                    type="number"
                    placeholder="e.g. 18"
                    value={sessionForm.respiratory_rate}
                    onChange={(e) => setSessionForm(prev => ({ ...prev, respiratory_rate: e.target.value }))}
                    className="text-sm"
                  />
                </div>
                
                {/* SPO2 */}
                <div>
                  <div className="flex items-center justify-between mb-1 h-5">
                    <Label className="text-xs">SpO2 (%)</Label>
                    {getVitalStatus('spo2', sessionForm.spo2) && (
                      <Badge className={`text-xs px-1 py-0 ${getVitalStatus('spo2', sessionForm.spo2).color}`}>
                        {getVitalStatus('spo2', sessionForm.spo2).status}
                      </Badge>
                    )}
                  </div>
                  <Input
                    type="number"
                    placeholder="e.g. 98"
                    value={sessionForm.spo2}
                    onChange={(e) => setSessionForm(prev => ({ ...prev, spo2: e.target.value }))}
                    className="text-sm"
                  />
                </div>
                
                {/* Blood Pressure */}
                <div>
                  <div className="flex items-center justify-between mb-1 h-5">
                    <Label className="text-xs">BP</Label>
                    {sessionForm.blood_pressure && sessionForm.blood_pressure.includes('/') && (
                      <div className="flex gap-1">
                        {(() => {
                          const [sys, dia] = sessionForm.blood_pressure.split('/');
                          const sysStatus = getVitalStatus('bp_systolic', sys);
                          const diaStatus = getVitalStatus('bp_diastolic', dia);
                          return (
                            <>
                              {sysStatus && (
                                <Badge className={`text-xs px-1 py-0 ${sysStatus.color}`}>
                                  {sysStatus.status}
                                </Badge>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                  <Input
                    type="text"
                    placeholder="120/80"
                    value={sessionForm.blood_pressure}
                    onChange={handleBPInput}
                    onKeyDown={handleBPKeyDown}
                    maxLength={7}
                    className="text-sm"
                  />
                </div>
              </div>
              
              {/* Row 2: Height & Weight on same line */}
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div>
                  <Label className="text-xs mb-1 block">Height (cm)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    placeholder="75.5"
                    value={sessionForm.height}
                    onChange={(e) => setSessionForm(prev => ({ ...prev, height: e.target.value }))}
                    className="text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs mb-1 block">Weight (kg)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    placeholder="9.5"
                    value={sessionForm.weight}
                    onChange={(e) => setSessionForm(prev => ({ ...prev, weight: e.target.value }))}
                    className="text-sm"
                  />
                </div>
              </div>
            </div>
            
            {/* BMI Display */}
            {calculatedBMI && calculatedBMI.bmi && (
              <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <span className="text-sm font-medium text-gray-700">BMI:</span>
                    <span className="text-lg font-bold text-gray-900">{calculatedBMI.bmi}</span>
                    <Badge className={`${getBMIBadgeClasses(calculatedBMI.color)} border`}>
                      {calculatedBMI.status}
                      {calculatedBMI.percentile && ` (${calculatedBMI.percentile})`}
                    </Badge>
                  </div>
                  {calculatedBMI.isPediatric && (
                    <span className="text-xs text-gray-500 italic">Pediatric BMI</span>
                  )}
                </div>
              </div>
            )}
            
            {/* Chief Complaints */}
            <div>
              <Label>Chief Complaints</Label>
              {chiefComplaintTemplates.length > 0 && (
                <div className="mb-2">
                  <SearchableSelect
                    options={chiefComplaintTemplates.map(c => ({ value: c.id, label: c.complaint_name }))}
                    value=""
                    onChange={handleChiefComplaintSelect}
                    placeholder="Select from chief complaint templates (optional)"
                    allowClear={false}
                  />
                </div>
              )}
              <Textarea
                placeholder="Enter chief complaints..."
                value={sessionForm.chief_complaints}
                onChange={(e) => setSessionForm(prev => ({ ...prev, chief_complaints: e.target.value }))}
                rows={2}
                className="text-sm md:text-base"
              />
            </div>
            
            {/* Assessment Field - Moved after Chief Complaints */}
            <div>
              <Label>Assessment</Label>
              <Textarea
                placeholder="Enter clinical assessment..."
                value={sessionForm.assessment}
                onChange={(e) => setSessionForm(prev => ({ ...prev, assessment: e.target.value }))}
                rows={2}
                className="text-sm md:text-base"
              />
            </div>
            
            {/* Tests */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Tests Recommended/Performed</Label>
                {testTemplates.length > 0 && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const testIds = sessionForm.tests 
                        ? sessionForm.tests.split(',').map(t => {
                            const template = testTemplates.find(tt => tt.test_name.trim() === t.trim());
                            return template ? template.id : null;
                          }).filter(Boolean)
                        : [];
                      setTestFieldTarget('tests');
                      setSelectedTests(testIds);
                      setShowTestSelectionDialog(true);
                    }}
                    className="text-xs"
                  >
                    Select Tests
                  </Button>
                )}
              </div>
              <Textarea
                placeholder="Enter test names separated by commas (e.g., CBC, Blood Sugar, X-Ray)"
                value={sessionForm.tests}
                onChange={(e) => setSessionForm(prev => ({ ...prev, tests: e.target.value }))}
                rows={2}
                className="text-sm md:text-base"
              />
            </div>
            
            {/* Differential Diagnosis Section - AI Generated - Only show if master and hospital settings allow */}
            {(sessionForm.chief_complaints || sessionForm.diagnosis) && aiFeatureSettings.differential_diagnosis_enabled && hospitalAiFeaturesEnabled && (
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg p-4 border border-amber-200">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-amber-600" />
                    <Label className="text-amber-900 font-semibold">Differential Diagnosis</Label>
                    <Badge variant="outline" className="text-xs bg-amber-100 text-amber-700 border-amber-300">AI Suggested</Badge>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={generateDifferentialDiagnosis}
                    disabled={isGeneratingDifferential}
                    className="text-xs bg-amber-100 border-amber-300 text-amber-700 hover:bg-amber-200"
                  >
                    {isGeneratingDifferential ? (
                      <>
                        <svg className="w-3 h-3 mr-1 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Generating...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-3 h-3 mr-1" />
                        {differentialDiagnosis ? 'Regenerate' : 'Generate'}
                      </>
                    )}
                  </Button>
                </div>
                
                {differentialDiagnosis ? (
                  <div className="space-y-3">
                    {/* Primary Diagnosis */}
                    {differentialDiagnosis.primary_diagnosis && (
                      <div className="bg-white rounded-lg p-3 border border-amber-200">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-semibold text-amber-800">Primary:</span>
                          <span className="text-sm text-gray-800">{differentialDiagnosis.primary_diagnosis.name}</span>
                          <Badge className={`text-xs ${
                            differentialDiagnosis.primary_diagnosis.confidence === 'high' ? 'bg-green-100 text-green-700' :
                            differentialDiagnosis.primary_diagnosis.confidence === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {differentialDiagnosis.primary_diagnosis.confidence}
                          </Badge>
                        </div>
                        <p className="text-xs text-gray-600">{differentialDiagnosis.primary_diagnosis.reasoning}</p>
                      </div>
                    )}
                    
                    {/* Differential Diagnoses */}
                    {differentialDiagnosis.differential_diagnoses?.length > 0 && (
                      <div>
                        <span className="text-xs font-medium text-gray-500 uppercase">Differentials:</span>
                        <div className="mt-1 space-y-1">
                          {differentialDiagnosis.differential_diagnoses.map((d, i) => (
                            <div key={i} className="flex items-center gap-2 text-sm">
                              <span className="text-gray-400">•</span>
                              <span className="text-gray-700">{d.name}</span>
                              <Badge variant="outline" className="text-xs">{d.confidence}</Badge>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Apply to Diagnosis Button */}
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => {
                        const diagnosisText = differentialDiagnosis.primary_diagnosis?.name || '';
                        if (diagnosisText) {
                          setSessionForm(prev => ({
                            ...prev,
                            diagnosis: prev.diagnosis ? `${prev.diagnosis}\n${diagnosisText}` : diagnosisText
                          }));
                          toast.success('Added to diagnosis');
                        }
                      }}
                      className="bg-amber-600 hover:bg-amber-700 text-white text-xs"
                    >
                      Apply Primary to Diagnosis
                    </Button>
                    
                    <p className="text-xs text-gray-500 italic">
                      * AI suggestions are non-decisional. Doctor makes final diagnosis.
                    </p>
                  </div>
                ) : (
                  <p className="text-xs text-gray-500 italic">
                    Click "Generate" to get AI-suggested differential diagnoses based on chief complaints and vitals.
                  </p>
                )}
              </div>
            )}

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Diagnosis</Label>
                {(editingSession || (uploadedReports.length > 0 && uploadedReports.some(r => r.analysis_file_url))) && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      // If editing session, use the old endpoint
                      if (editingSession) {
                        try {
                          const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/medical-sessions/${editingSession.id}/diagnosis-suggestions`);
                          if (response.data.suggestions) {
                            setSessionForm(prev => ({ ...prev, diagnosis: response.data.suggestions }));
                          }
                        } catch (error) {
                          console.error('Error fetching diagnosis suggestions:', error);
                        }
                      } else {
                        // Use uploaded reports for live session
                        handleAutofillFromUploadedReports();
                      }
                    }}
                    className="text-xs bg-purple-100 border-purple-300 text-purple-700 hover:bg-purple-200"
                  >
                    <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    Autofill from Reports
                  </Button>
                )}
              </div>
              {diagnosisTemplates.length > 0 && (
                <div className="mb-2">
                  <SearchableSelect
                    options={diagnosisTemplates.map(d => ({ value: d.id, label: d.diagnosis_name }))}
                    value=""
                    onChange={handleDiagnosisSelect}
                    placeholder="Select from diagnosis templates (optional)"
                    allowClear={false}
                  />
                </div>
              )}
              <Textarea
                placeholder="Enter the diagnosis (optional)"
                value={sessionForm.diagnosis}
                onChange={(e) => setSessionForm(prev => ({ ...prev, diagnosis: e.target.value }))}
                rows={3}
                className="text-sm md:text-base"
              />
            </div>
            
            {/* Medications */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Medications Prescribed</Label>
                <div className="flex items-center gap-2">
                  {/* Reuse Medications Button - Only show for follow-up sessions */}
                  {followUpFromSession && (
                    <Button 
                      type="button"
                      variant="outline" 
                      size="sm"
                      onClick={handleOpenReuseMedications}
                      className="text-purple-600 border-purple-300 hover:bg-purple-50"
                    >
                      <RefreshCw className="w-4 h-4 mr-1" />
                      Reuse
                    </Button>
                  )}
                  <Button 
                    type="button"
                    variant="outline" 
                    size="sm"
                    onClick={handleAddMedication}
                    className="text-blue-600"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Medication
                  </Button>
                </div>
              </div>
              
              {sessionForm.medications.map((medication, index) => {
                return (
                  <div key={index} className="mb-3 p-3 bg-gray-50 rounded-lg">
                    {/* Single Line: Medication name search + Timing + Duration + Delete */}
                    <div className="flex flex-wrap items-center gap-2">
                      {/* Medication Name Input with Search */}
                      <MedicationSearchInput
                        value={medication.name}
                        onChange={(value) => handleMedicationChange(index, 'name', value)}
                        onTemplateSelect={(sourceId, templateData, isTemplate, isCommon) => 
                          handleMedicationSearchSelect(index, sourceId, templateData, isTemplate, isCommon)
                        }
                        medicationTemplates={medicationTemplates}
                        commonMedications={commonMedications}
                        showCommonMedications={doctorSettings.show_common_medications}
                        medicationType={medication.medication_type}
                        placeholder="Type medication name..."
                        className="flex-1 min-w-[200px]"
                      />
                      
                      {/* Timing Dropdown */}
                      <select
                        value={medication.timing}
                        onChange={(e) => handleMedicationChange(index, 'timing', e.target.value)}
                        className="w-32 h-10 px-3 rounded-md border border-gray-300 bg-white text-sm"
                      >
                        {medicationTimings.map(timing => (
                          <option key={timing} value={timing}>{timing}</option>
                        ))}
                      </select>
                      
                      {/* Duration Input */}
                      <Input
                        placeholder="Duration"
                        value={medication.duration}
                        onChange={(e) => handleMedicationChange(index, 'duration', e.target.value)}
                        className="w-28 h-10 text-sm"
                      />
                      
                      {/* Delete Button */}
                      {sessionForm.medications.length > 1 && (
                        <Button 
                          type="button"
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleRemoveMedication(index)}
                          className="text-red-600"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                    
                    {/* Third Line: Time of Day Selector */}
                    <div className="flex items-center gap-3 mt-2 ml-1 flex-wrap">
                      <span className="text-xs text-gray-600">Time:</span>
                      {['Morning', 'Afternoon', 'Evening', 'Night'].map((timeSlot) => {
                        const isSelected = medication.time_of_day?.includes(timeSlot);
                        return (
                          <button
                            key={timeSlot}
                            type="button"
                            onClick={() => {
                              const currentTimes = medication.time_of_day || [];
                              const newTimes = isSelected
                                ? currentTimes.filter(t => t !== timeSlot)
                                : [...currentTimes, timeSlot];
                              handleMedicationChange(index, 'time_of_day', newTimes);
                            }}
                            className="flex flex-col items-center gap-1 group"
                          >
                            <div className={`w-2.5 h-2.5 rounded-full transition-all ${
                              isSelected 
                                ? 'bg-purple-600 scale-125' 
                                : 'bg-gray-300 group-hover:bg-purple-400'
                            }`} />
                            <span className={`text-[10px] transition-colors ${
                              isSelected 
                                ? 'text-purple-600 font-medium' 
                                : 'text-gray-500 group-hover:text-purple-500'
                            }`}>
                              {timeSlot.substring(0, 1)}
                            </span>
                          </button>
                        );
                      })}
                      
                      {/* Frequency Buttons */}
                      <div className="flex items-center gap-2 ml-4 border-l pl-4">
                        <span className="text-xs text-gray-600">Frequency:</span>
                        {['Once a day', 'Once a week', 'Once a month'].map((freq) => {
                          const isSelected = medication.frequency === freq;
                          return (
                            <button
                              key={freq}
                              type="button"
                              onClick={() => {
                                handleMedicationChange(index, 'frequency', isSelected ? '' : freq);
                              }}
                              className={`px-2 py-1 text-[10px] rounded transition-all ${
                                isSelected
                                  ? 'bg-purple-600 text-white'
                                  : 'bg-gray-200 text-gray-600 hover:bg-purple-100'
                              }`}
                            >
                              {freq}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    
                    {/* Fourth Line: Treatment For Field */}
                    <div className="mt-2">
                      <Input
                        placeholder="Treatment for (e.g., fever, pain relief, hypertension)"
                        value={medication.treatment_for || ''}
                        onChange={(e) => handleMedicationChange(index, 'treatment_for', e.target.value)}
                        className="w-full text-sm text-gray-700"
                      />
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Additional Tests */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Additional Tests</Label>
                {testTemplates.length > 0 && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const testIds = sessionForm.additional_tests 
                        ? sessionForm.additional_tests.split(',').map(t => {
                            const template = testTemplates.find(tt => tt.test_name.trim() === t.trim());
                            return template ? template.id : null;
                          }).filter(Boolean)
                        : [];
                      setTestFieldTarget('additional_tests');
                      setSelectedTests(testIds);
                      setShowTestSelectionDialog(true);
                    }}
                    className="text-xs"
                  >
                    Select Tests
                  </Button>
                )}
              </div>
              <Textarea
                placeholder="Enter additional test names separated by commas (optional)"
                value={sessionForm.additional_tests || ''}
                onChange={(e) => setSessionForm(prev => ({ ...prev, additional_tests: e.target.value }))}
                rows={2}
                className="text-sm md:text-base"
              />
            </div>
            
            <div>
              <Label>Advice</Label>
              {additionalNotesTemplates.length > 0 && (
                <div className="mb-2">
                  <SearchableSelect
                    options={additionalNotesTemplates.map(n => ({ 
                      value: n.id, 
                      label: n.note_title + (n.note_content ? `: ${n.note_content.substring(0, 50)}...` : '') 
                    }))}
                    value=""
                    onChange={handleAdditionalNotesSelect}
                    placeholder="Select from advice templates (optional)"
                    allowClear={false}
                  />
                </div>
              )}
              <Textarea
                placeholder="Any advice or additional information..."
                value={sessionForm.notes}
                onChange={(e) => setSessionForm(prev => ({ ...prev, notes: e.target.value }))}
                rows={3}
                className="text-sm md:text-base"
              />
            </div>

            {/* Next Follow-up Date */}
            <div>
              <Label>Next Follow-up (Optional)</Label>
              <div className="relative">
                <DateInput
                  value={sessionForm.next_followup}
                  onChange={(e) => setSessionForm(prev => ({ ...prev, next_followup: e.target.value }))}
                  onKeyDown={(e) => e.preventDefault()}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-base cursor-pointer"
                  style={{ colorScheme: 'light' }}
                />
                {sessionForm.next_followup && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSessionForm(prev => ({ ...prev, next_followup: '' }));
                    }}
                    className="absolute right-8 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 z-10"
                    title="Clear date"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>

            {/* Upload Reports Section */}
            <div>
              <Label className="flex items-center gap-2">
                <Upload className="w-4 h-4" />
                Upload Reports
              </Label>
              <div className="mt-2 space-y-3">
                
                {/* Uploaded Reports List - Shown ABOVE upload box */}
                {uploadedReports.length > 0 && (
                  <div className="space-y-3">
                    {uploadedReports.map((report) => (
                      <div key={report.id} className="border border-gray-200 rounded-lg p-3 bg-white space-y-2">
                        {/* Analysis File or Analyzing Status - shown first */}
                        {analyzingReports[report.id] ? (
                          <div className="flex items-center gap-2 px-2 py-1 bg-blue-50 border border-blue-200 rounded text-xs">
                            <div className="w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                            <span className="text-blue-700 font-medium">Analyzing report...</span>
                          </div>
                        ) : report.analysis_file_url ? (
                          <div className="flex items-center gap-2 flex-wrap">
                            <button
                              onClick={() => openReportFile(report.analysis_file_url, 'Analysis Report')}
                              className="flex items-center gap-1.5 px-2 py-1 bg-purple-50 hover:bg-purple-100 border border-purple-300 rounded text-xs font-medium transition-colors"
                              title="View AI Analysis"
                            >
                              <FileText className="w-3 h-3 text-purple-600" />
                              <span className="text-purple-700">View Analysis</span>
                              {(report.abnormal_count > 0 || report.borderline_count > 0) ? (
                                <span className="flex gap-1 ml-1">
                                  {report.abnormal_count > 0 && (
                                    <span className="text-[10px] px-1.5 py-0.5 bg-red-100 text-red-700 rounded font-bold">
                                      {report.abnormal_count} Abnormal
                                    </span>
                                  )}
                                  {report.borderline_count > 0 && (
                                    <span className="text-[10px] px-1.5 py-0.5 bg-yellow-100 text-yellow-700 rounded font-bold">
                                      {report.borderline_count} Borderline
                                    </span>
                                  )}
                                </span>
                              ) : (
                                <span className="text-[10px] px-1.5 py-0.5 bg-green-100 text-green-700 rounded font-medium">
                                  All Normal
                                </span>
                              )}
                            </button>
                            
                            {/* Re-analyze button */}
                            <button
                              type="button"
                              onClick={() => handleReanalyzeUploadedReport(report.id)}
                              disabled={reanalyzingReports[report.id]}
                              className="flex items-center gap-1 px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded transition-colors disabled:opacity-50"
                              title="Re-analyze report"
                            >
                              <svg className={`w-3 h-3 ${reanalyzingReports[report.id] ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                              </svg>
                              <span>{reanalyzingReports[report.id] ? 'Analyzing...' : 'Re-analyze'}</span>
                            </button>
                          </div>
                        ) : (
                          /* Show re-analyze button when no analysis URL yet */
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500">Analysis pending</span>
                            <button
                              type="button"
                              onClick={() => handleReanalyzeUploadedReport(report.id)}
                              disabled={reanalyzingReports[report.id]}
                              className="flex items-center gap-1 px-2 py-1 text-xs bg-blue-100 hover:bg-blue-200 text-blue-700 rounded transition-colors disabled:opacity-50"
                              title="Analyze report"
                            >
                              <svg className={`w-3 h-3 ${reanalyzingReports[report.id] ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                              </svg>
                              <span>{reanalyzingReports[report.id] ? 'Analyzing...' : 'Analyze Now'}</span>
                            </button>
                          </div>
                        )}
                        
                        {/* Original Report File */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 flex-1">
                            <FileText className="w-4 h-4 text-blue-600" />
                            <div className="flex-1">
                              <button
                                onClick={() => openReportFile(report.fileUrl, report.originalName)}
                                className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline cursor-pointer text-left"
                                title="Click to open in new tab"
                              >
                                {report.originalName}
                              </button>
                              <div className="text-xs text-gray-500">
                                {(report.fileSize / 1024).toFixed(1)} KB • {formatDateToDDMMYYYY(report.uploadDate)}
                                {report.reportDate && ` • Report Date: ${formatDateToDDMMYYYY(report.reportDate)}`}
                              </div>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeUploadedReport(report.id)}
                            className="text-red-600 hover:text-red-800 p-1"
                            title="Remove report"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* File Upload Box - Now shown BELOW uploaded reports */}
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-gray-400 transition-colors">
                  <input
                    type="file"
                    id="report-upload"
                    multiple
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                    onChange={(e) => handleFileUpload(e.target.files)}
                    className="hidden"
                  />
                  <label 
                    htmlFor="report-upload" 
                    className="cursor-pointer flex flex-col items-center justify-center text-gray-600 hover:text-gray-800"
                  >
                    <Upload className="w-8 h-8 mb-2 text-gray-400" />
                    <span className="text-sm font-medium">Click to upload reports</span>
                    <span className="text-xs text-gray-500 mt-1">
                      PDF, JPG, PNG, DOC, DOCX files supported • Auto-analysis enabled
                    </span>
                  </label>
                  {isUploading && (
                    <div className="mt-2 text-center">
                      <div className="inline-block w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                      <span className="ml-2 text-sm text-blue-600">Uploading & Analyzing...</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {followUpFromSession && (
              <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
                <p className="text-xs text-indigo-700">
                  This is a follow-up session for: <strong>{followUpFromSession.chief_complaint}</strong>
                </p>
              </div>
            )}
            
            <div className="flex gap-2">
              <Button 
                onClick={editingSession ? handleEditSession : handleAddSession}
                className="flex-1 bg-indigo-600 hover:bg-indigo-700"
                disabled={submitting}
              >
                {submitting ? 'Saving...' : (editingSession ? 'Update' : 'Add Session')}
              </Button>
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowAddSession(false);
                  setEditingSession(null);
                  setFollowUpFromSession(null);
                  setUploadedReports([]); // Clear uploaded reports
                  setSessionForm({ 
                    session_date: '', 
                    doctor_name: '', 
                     
                    diagnosis: '', 
                    medications: [{ name: '', timing: 'Before Food', duration: '', time_of_day: [], frequency: '', medication_type: '', treatment_for: '' }],
                    temperature: '',
                    notes: '',
                    follow_up_of: '',
                    pulse: '',
                    respiratory_rate: '',
                    spo2: '',
                    blood_pressure: '',
                    height: '',
                    weight: '',
                    tests: ''
                  });
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Quick Lookup Dialog */}
      <Dialog open={showQuickLookup} onOpenChange={setShowQuickLookup}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Quick Lookup</DialogTitle>
            <DialogDescription>
              Search by ID or name and date of birth.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={(e) => {
            e.preventDefault();
            handleQuickLookup();
          }}>
            <div className="space-y-4">
              <div>
                <Label>ID</Label>
                <Input
                  value={lookupQuery.id}
                  onChange={(e) => setLookupQuery({...lookupQuery, id: e.target.value.toUpperCase()})}
                  placeholder="e.g., 78D97795"
                />
              </div>
              
              <div className="text-center text-gray-500">OR</div>
              
              <div>
                <Label>Name</Label>
                <Input
                  value={lookupQuery.name}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Capitalize first letter of each word
                    const capitalized = value
                      .split(' ')
                      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                      .join(' ');
                    setLookupQuery({...lookupQuery, name: capitalized});
                  }}
                  placeholder="Enter name"
                />
              </div>
              
              <div>
                <Label>Date of Birth (DD/MM/YYYY)</Label>
                <Input
                  type="text"
                  placeholder="DD/MM/YYYY"
                  value={lookupQuery.dob}
                  onChange={handleDateInput(setLookupQuery, 'dob')}
                  onKeyDown={handleDateKeyDown(setLookupQuery, 'dob')}
                  maxLength={10}
                />
              </div>
              
              <Button type="submit" className="w-full bg-purple-600 hover:bg-purple-700">
                <Search className="w-4 h-4 mr-2" />
                Search
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Quick Appointment Dialog */}
      <Dialog open={showQuickAppointment} onOpenChange={(open) => {
        setShowQuickAppointment(open);
        if (!open) {
          setQuickAppointmentForm({
            baby_id: '',
            baby_name: '',
            baby_dob: '',
            baby_gender: 'Male',
            baby_phone: '',
            baby_country_code: '+91',
            notes: '',
            appointment_type: 'new',
            category: 'Regular',
            category_ref: '',
            payment_method: 'Cash'
          });
          setFoundBabyQuick(null);
          setUseExistingBabyQuick(true);
        }
      }}>
        <DialogContent className="sm:max-w-md z-50 max-h-[90vh] overflow-y-auto" data-testid="quick-appointment-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-green-600" />
              Quick Appointment
            </DialogTitle>
            <DialogDescription>
              Quickly book an appointment for today. The appointment will appear first in pending list.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Existing vs New Toggle */}
            <div className="flex items-center space-x-4">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  checked={useExistingBabyQuick}
                  onChange={() => setUseExistingBabyQuick(true)}
                  className="w-4 h-4"
                />
                <span className="text-sm">Existing</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  checked={!useExistingBabyQuick}
                  onChange={() => setUseExistingBabyQuick(false)}
                  className="w-4 h-4"
                />
                <span className="text-sm">New</span>
              </label>
            </div>

            {useExistingBabyQuick ? (
              <>
                <div>
                  <Label htmlFor="quick_baby_id">ID</Label>
                  <Input
                    id="quick_baby_id"
                    value={quickAppointmentForm.baby_id}
                    onChange={(e) => {
                      setQuickAppointmentForm({...quickAppointmentForm, baby_id: e.target.value.toUpperCase()});
                      setFoundBabyQuick(null);
                    }}
                    placeholder="Enter ID"
                    data-testid="quick-baby-id-input"
                  />
                </div>
                
                <div className="text-center text-gray-500 text-sm">OR</div>
                
                <div>
                  <Label htmlFor="quick_baby_name">Name</Label>
                  <Input
                    id="quick_baby_name"
                    value={quickAppointmentForm.baby_name}
                    onChange={(e) => {
                      const value = e.target.value;
                      const capitalized = value.charAt(0).toUpperCase() + value.slice(1);
                      setQuickAppointmentForm({...quickAppointmentForm, baby_name: capitalized});
                      setFoundBabyQuick(null);
                    }}
                    placeholder="Enter Name"
                  />
                </div>
                
                <div>
                  <Label htmlFor="quick_baby_dob">Date of Birth (DD/MM/YYYY)</Label>
                  <Input
                    id="quick_baby_dob"
                    value={quickAppointmentForm.baby_dob}
                    onChange={(e) => handleQuickAppointmentDobInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Backspace' || e.key === 'Delete') {
                        const value = quickAppointmentForm.baby_dob;
                        if (value.endsWith('/')) {
                          e.preventDefault();
                          setQuickAppointmentForm({...quickAppointmentForm, baby_dob: value.slice(0, -1)});
                        }
                      }
                    }}
                    placeholder="DD/MM/YYYY"
                    maxLength={10}
                  />
                </div>
                
                <Button
                  type="button"
                  onClick={handleQuickAppointmentSearch}
                  variant="outline"
                  className="w-full"
                >
                  <Search className="w-4 h-4 mr-2" />
                  Search
                </Button>
                
                {foundBabyQuick && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <p className="text-sm font-semibold text-green-700">✓ Found</p>
                    <p className="text-sm text-green-800">{foundBabyQuick.name}</p>
                    <p className="text-xs text-green-600">ID: <span className="uppercase font-mono">{foundBabyQuick.short_id || foundBabyQuick.id.substring(0, 8).toUpperCase()}</span></p>
                    <p className="text-xs text-green-600">DOB: {foundBabyQuick.date_of_birth}</p>
                  </div>
                )}
                
                {/* Appointment Type Selection - Only for existing patients */}
                {foundBabyQuick && (
                  <div>
                    <Label>Appointment Type</Label>
                    <div className="flex gap-6 mt-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="quick_appointment_type"
                          value="new"
                          checked={quickAppointmentForm.appointment_type === 'new'}
                          onChange={(e) => setQuickAppointmentForm({...quickAppointmentForm, appointment_type: e.target.value})}
                          className="w-4 h-4 text-blue-600"
                        />
                        <span className="text-sm font-medium text-gray-700">New Visit</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="quick_appointment_type"
                          value="followup"
                          checked={quickAppointmentForm.appointment_type === 'followup'}
                          onChange={(e) => setQuickAppointmentForm({...quickAppointmentForm, appointment_type: e.target.value})}
                          className="w-4 h-4 text-blue-600"
                        />
                        <span className="text-sm font-medium text-gray-700">Follow-up</span>
                      </label>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <>
                <div>
                  <Label htmlFor="new_quick_baby_name">Name *</Label>
                  <Input
                    id="new_quick_baby_name"
                    value={quickAppointmentForm.baby_name}
                    onChange={(e) => {
                      const value = e.target.value;
                      const capitalized = value.charAt(0).toUpperCase() + value.slice(1);
                      setQuickAppointmentForm({...quickAppointmentForm, baby_name: capitalized});
                    }}
                    placeholder="e.g., Aarav"
                  />
                </div>
                <div>
                  <Label htmlFor="new_quick_baby_dob">Date of Birth (DD/MM/YYYY) *</Label>
                  <Input
                    id="new_quick_baby_dob"
                    value={quickAppointmentForm.baby_dob}
                    onChange={(e) => handleQuickAppointmentDobInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Backspace' || e.key === 'Delete') {
                        const value = quickAppointmentForm.baby_dob;
                        if (value.endsWith('/')) {
                          e.preventDefault();
                          setQuickAppointmentForm({...quickAppointmentForm, baby_dob: value.slice(0, -1)});
                        }
                      }
                    }}
                    placeholder="DD/MM/YYYY"
                    maxLength={10}
                  />
                </div>
                <div>
                  <Label>Gender *</Label>
                  <div className="grid grid-cols-2 gap-3 mt-1">
                    <button
                      type="button"
                      onClick={() => setQuickAppointmentForm({...quickAppointmentForm, baby_gender: 'Male'})}
                      className={`h-12 rounded-lg border-2 font-semibold transition-all ${
                        quickAppointmentForm.baby_gender === 'Male'
                          ? 'bg-blue-500 text-white border-blue-600 shadow-lg'
                          : 'bg-white text-blue-600 border-blue-300 hover:bg-blue-50'
                      }`}
                    >
                      Male
                    </button>
                    <button
                      type="button"
                      onClick={() => setQuickAppointmentForm({...quickAppointmentForm, baby_gender: 'Female'})}
                      className={`h-12 rounded-lg border-2 font-semibold transition-all ${
                        quickAppointmentForm.baby_gender === 'Female'
                          ? 'bg-pink-500 text-white border-pink-600 shadow-lg'
                          : 'bg-white text-pink-600 border-pink-300 hover:bg-pink-50'
                      }`}
                    >
                      Female
                    </button>
                  </div>
                </div>
              </>
            )}

            <div>
              <Label htmlFor="quick_baby_phone">Phone Number *</Label>
              <Input
                id="quick_baby_phone"
                value={quickAppointmentForm.baby_phone}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                  setQuickAppointmentForm({...quickAppointmentForm, baby_phone: value});
                }}
                placeholder="Enter 10-digit mobile number"
                maxLength={10}
                data-testid="quick-phone-input"
              />
              <p className="text-xs text-gray-500 mt-1">Phone number for this appointment</p>
            </div>

            {/* Category Selection */}
            <div>
              <Label>Category *</Label>
              <div className="flex gap-6 mt-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="quick_category"
                    value="Regular"
                    checked={quickAppointmentForm.category === 'Regular'}
                    onChange={(e) => setQuickAppointmentForm({...quickAppointmentForm, category: e.target.value, category_ref: ''})}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm font-medium text-gray-700">Regular</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="quick_category"
                    value="Known"
                    checked={quickAppointmentForm.category === 'Known'}
                    onChange={(e) => setQuickAppointmentForm({...quickAppointmentForm, category: e.target.value})}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm font-medium text-gray-700">Known</span>
                </label>
              </div>
            </div>

            {/* Ref field - shown only for Known */}
            {quickAppointmentForm.category === 'Known' && (
              <div>
                <Label htmlFor="quick_category_ref">Ref (Reference) *</Label>
                <Input
                  id="quick_category_ref"
                  value={quickAppointmentForm.category_ref}
                  onChange={(e) => setQuickAppointmentForm({...quickAppointmentForm, category_ref: e.target.value})}
                  placeholder="Enter reference/clarification..."
                />
                <p className="text-xs text-gray-500 mt-1">Consultation fee: ₹0 (No charge)</p>
              </div>
            )}

            {/* Payment Method - shown only for Regular category */}
            {quickAppointmentForm.category === 'Regular' && (
              <div>
                <Label>Payment Method *</Label>
                <div className="flex gap-6 mt-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="quick_payment_method"
                      value="Cash"
                      checked={quickAppointmentForm.payment_method === 'Cash'}
                      onChange={(e) => setQuickAppointmentForm({...quickAppointmentForm, payment_method: e.target.value})}
                      className="w-4 h-4 text-blue-600"
                    />
                    <span className="text-sm font-medium text-gray-700">Cash</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="quick_payment_method"
                      value="Online"
                      checked={quickAppointmentForm.payment_method === 'Online'}
                      onChange={(e) => setQuickAppointmentForm({...quickAppointmentForm, payment_method: e.target.value})}
                      className="w-4 h-4 text-blue-600"
                    />
                    <span className="text-sm font-medium text-gray-700">Online</span>
                  </label>
                </div>
              </div>
            )}

            {/* Info about Quick Appointment */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-xs text-blue-700">
                <strong>Note:</strong> Quick appointments appear first in your pending list with time shown as "Quick - {new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })}"
              </p>
            </div>

            <div className="flex space-x-2">
              <Button 
                onClick={handleCreateQuickAppointment} 
                className="flex-1 bg-green-600 hover:bg-green-700"
                disabled={quickAppointmentSubmitting}
                data-testid="create-quick-appointment-btn"
              >
                {quickAppointmentSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Creating...
                  </>
                ) : (
                  <>
                    <Zap className="w-4 h-4 mr-2" />
                    Create Appointment
                  </>
                )}
              </Button>
              <Button variant="outline" onClick={() => setShowQuickAppointment(false)} className="flex-1">
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Previous Session Dialog - View Follow-up Chain */}
      <Dialog open={showPreviousSessionDialog} onOpenChange={setShowPreviousSessionDialog}>
        <DialogContent className="sm:max-w-3xl z-50 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Previous Session Details</DialogTitle>
            <DialogDescription>
              View the details of the previous medical session in the follow-up chain.
            </DialogDescription>
          </DialogHeader>
          {previousSessionData && (
            <div className="space-y-4">
              {/* Header with Date and Doctor */}
              <div className="flex items-center justify-between border-b pb-3">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                    <Stethoscope className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{formatDate(previousSessionData.session_date)}</p>
                    <p className="text-sm text-gray-600">{previousSessionData.doctor_name || 'Doctor'}</p>
                  </div>
                </div>
              </div>

              {/* Vitals Section */}
              {(previousSessionData.temperature || previousSessionData.pulse || previousSessionData.spo2 || previousSessionData.blood_pressure || previousSessionData.height || previousSessionData.weight) && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <h4 className="text-xs font-medium text-gray-500 uppercase mb-2">Vitals</h4>
                  <div className="text-sm text-gray-800 flex flex-wrap items-center gap-2">
                    {previousSessionData.temperature && (
                      <span className="bg-white px-2 py-1 rounded border">Temp: {previousSessionData.temperature}°F</span>
                    )}
                    {previousSessionData.pulse && (
                      <span className="bg-white px-2 py-1 rounded border">Pulse: {previousSessionData.pulse} bpm</span>
                    )}
                    {previousSessionData.spo2 && (
                      <span className="bg-white px-2 py-1 rounded border">SpO2: {previousSessionData.spo2}%</span>
                    )}
                    {previousSessionData.blood_pressure && (
                      <span className="bg-white px-2 py-1 rounded border">BP: {previousSessionData.blood_pressure}</span>
                    )}
                    {previousSessionData.height && (
                      <span className="bg-white px-2 py-1 rounded border">Height: {previousSessionData.height} cm</span>
                    )}
                    {previousSessionData.weight && (
                      <span className="bg-white px-2 py-1 rounded border">Weight: {previousSessionData.weight} kg</span>
                    )}
                  </div>
                </div>
              )}

              {/* Chief Complaints */}
              {(previousSessionData.chief_complaints || previousSessionData.chief_complaint || previousSessionData.symptoms) && (
                <div>
                  <h4 className="text-xs font-medium text-gray-500 uppercase mb-1">Chief Complaints</h4>
                  <p className="text-sm text-gray-800 leading-relaxed">{previousSessionData.chief_complaints || previousSessionData.chief_complaint || previousSessionData.symptoms}</p>
                </div>
              )}

              {/* Assessment */}
              {previousSessionData.assessment && (
                <div>
                  <h4 className="text-xs font-medium text-gray-500 uppercase mb-1">Assessment</h4>
                  <p className="text-sm text-gray-800 leading-relaxed">{previousSessionData.assessment}</p>
                </div>
              )}

              {/* Tests Recommended */}
              {previousSessionData.tests && (
                <div>
                  <h4 className="text-xs font-medium text-gray-500 uppercase mb-1">Tests Recommended</h4>
                  <p className="text-sm text-gray-800 leading-relaxed">{previousSessionData.tests}</p>
                </div>
              )}

              {/* Diagnosis */}
              {previousSessionData.diagnosis && (
                <div>
                  <h4 className="text-xs font-medium text-gray-500 uppercase mb-1">Diagnosis</h4>
                  <div className="text-sm text-gray-800 leading-relaxed space-y-1">
                    {previousSessionData.diagnosis.split('\n').filter(line => line.trim()).map((line, idx) => (
                      <div key={idx} className="text-blue-700">{line}</div>
                    ))}
                  </div>
                </div>
              )}

              {/* Medication Prescribed - Complete Details */}
              {previousSessionData.medication && (
                <div>
                  <h4 className="text-xs font-medium text-gray-500 uppercase mb-1 flex items-center gap-1">
                    <FileText className="w-3 h-3" />
                    <span>Medication Prescribed</span>
                  </h4>
                  <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
                    <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-line">{previousSessionData.medication}</p>
                  </div>
                </div>
              )}

              {/* Medications Array - if stored as array */}
              {previousSessionData.medications && previousSessionData.medications.length > 0 && (
                <div>
                  <h4 className="text-xs font-medium text-gray-500 uppercase mb-2 flex items-center gap-1">
                    <Pill className="w-3 h-3" />
                    <span>Medications ({previousSessionData.medications.length})</span>
                  </h4>
                  <div className="space-y-2">
                    {previousSessionData.medications.map((med, idx) => {
                      const medName = typeof med === 'string' ? med : (med.name || med.medication_name || '');
                      const duration = typeof med === 'object' ? med.duration : '';
                      const timing = typeof med === 'object' ? med.timing : '';
                      const frequency = typeof med === 'object' ? med.frequency : '';
                      const timeOfDay = typeof med === 'object' && med.time_of_day ? 
                        (Array.isArray(med.time_of_day) ? med.time_of_day.join(', ') : med.time_of_day) : '';
                      const treatmentFor = typeof med === 'object' ? med.treatment_for : '';
                      
                      if (!medName) return null;
                      
                      return (
                        <div key={idx} className="bg-blue-50 border border-blue-100 rounded-lg p-3">
                          <div className="font-medium text-gray-800 mb-1">{medName}</div>
                          <div className="text-xs text-gray-600 flex flex-wrap gap-x-3 gap-y-1">
                            {duration && <span className="flex items-center gap-1">📅 Duration: {duration}</span>}
                            {timing && <span className="flex items-center gap-1">🍽️ {timing}</span>}
                            {frequency && <span className="flex items-center gap-1">🔄 {frequency}</span>}
                            {timeOfDay && <span className="flex items-center gap-1">⏰ {timeOfDay}</span>}
                            {treatmentFor && <span className="flex items-center gap-1">💊 For: {treatmentFor}</span>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Additional Tests */}
              {previousSessionData.additional_tests && (
                <div>
                  <h4 className="text-xs font-medium text-gray-500 uppercase mb-1">Additional Tests</h4>
                  <p className="text-sm text-gray-800 leading-relaxed">{previousSessionData.additional_tests}</p>
                </div>
              )}

              {/* Advice / Notes */}
              {previousSessionData.notes && (
                <div>
                  <h4 className="text-xs font-medium text-gray-500 uppercase mb-1">Advice</h4>
                  <p className="text-sm text-gray-600 leading-relaxed italic bg-yellow-50 p-2 rounded border border-yellow-100">{previousSessionData.notes}</p>
                </div>
              )}

              {/* Next Follow-up */}
              {previousSessionData.next_followup && (
                <div>
                  <h4 className="text-xs font-medium text-gray-500 uppercase mb-1">Next Follow-up</h4>
                  <p className="text-sm text-gray-800 font-medium">
                    {(() => {
                      const date = new Date(previousSessionData.next_followup);
                      const day = String(date.getDate()).padStart(2, '0');
                      const month = String(date.getMonth() + 1).padStart(2, '0');
                      const year = date.getFullYear();
                      return `${day}/${month}/${year}`;
                    })()}
                  </p>
                </div>
              )}

              {/* Attached Documents */}
              {previousSessionData.documents && previousSessionData.documents.length > 0 && (
                <div>
                  <h4 className="text-xs font-medium text-gray-500 uppercase mb-2 flex items-center gap-1">
                    <FileText className="w-3 h-3" />
                    <span>Attached Documents ({previousSessionData.documents.length})</span>
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {previousSessionData.documents.map((doc, index) => {
                      const docUrl = typeof doc === 'object' ? doc.url : doc;
                      const getFileIcon = (url) => {
                        const ext = url.split('.').pop().toLowerCase();
                        if (ext === 'pdf') return '📄';
                        if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return '🖼️';
                        return '📎';
                      };
                      const getFilename = (url) => url.split('/').pop();
                      return (
                        <button
                          key={index}
                          onClick={() => openReportFile(docUrl, getFilename(docUrl))}
                          className="flex items-center space-x-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors border cursor-pointer"
                        >
                          <span className="text-lg">{getFileIcon(docUrl)}</span>
                          <span className="text-xs text-gray-700 max-w-[150px] truncate">{getFilename(docUrl)}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="flex justify-end pt-4 border-t">
                <Button 
                  variant="outline" 
                  onClick={() => setShowPreviousSessionDialog(false)}
                >
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reuse Medications Dialog */}
      <Dialog open={showReuseMedicationsDialog} onOpenChange={setShowReuseMedicationsDialog}>
        <DialogContent className="sm:max-w-lg z-[60]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-purple-600" />
              Reuse Medications from Previous Session
            </DialogTitle>
            <DialogDescription>
              Select medications from the previous session ({followUpFromSession ? formatDate(followUpFromSession.session_date) : ''}) to add to the current prescription.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3 max-h-[400px] overflow-y-auto">
            {reusableMedications.length === 0 ? (
              <p className="text-center text-gray-500 py-4">No medications found in the previous session</p>
            ) : (
              <>
                {/* Select All / Deselect All */}
                <div className="flex items-center justify-between pb-2 border-b">
                  <span className="text-sm text-gray-600">{selectedReuseMedications.length} of {reusableMedications.length} selected</span>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setSelectedReuseMedications(reusableMedications.map(m => m.id))}
                    >
                      Select All
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setSelectedReuseMedications([])}
                    >
                      Deselect All
                    </Button>
                  </div>
                </div>
                
                {/* Medication List */}
                {reusableMedications.map((med) => {
                  // Build the details line
                  const details = [];
                  if (med.timing) details.push(med.timing);
                  if (med.time_of_day && med.time_of_day.length > 0) {
                    details.push(Array.isArray(med.time_of_day) ? med.time_of_day.join(', ') : med.time_of_day);
                  }
                  if (med.duration) details.push(med.duration);
                  
                  return (
                    <div
                      key={med.id}
                      onClick={() => {
                        setSelectedReuseMedications(prev => 
                          prev.includes(med.id) 
                            ? prev.filter(id => id !== med.id)
                            : [...prev, med.id]
                        );
                      }}
                      className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                        selectedReuseMedications.includes(med.id)
                          ? 'bg-purple-50 border-purple-300'
                          : 'bg-white hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-5 h-5 rounded border flex items-center justify-center mt-0.5 ${
                          selectedReuseMedications.includes(med.id)
                            ? 'bg-purple-600 border-purple-600'
                            : 'border-gray-300'
                        }`}>
                          {selectedReuseMedications.includes(med.id) && (
                            <Check className="w-3 h-3 text-white" />
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            {med.medication_type && <MedicationIcon type={med.medication_type} size={18} />}
                            <span className="font-medium text-gray-900">{med.name}</span>
                            {med.generic_name && (
                              <span className="text-gray-500 text-sm">[{med.generic_name}]</span>
                            )}
                          </div>
                          {med.treatment_for && (
                            <p className="text-xs text-blue-600 mt-0.5">[For: {med.treatment_for}]</p>
                          )}
                          {details.length > 0 && (
                            <p className="text-xs text-gray-600 mt-1">{details.join(' - ')}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </div>
          
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button 
              variant="outline" 
              onClick={() => setShowReuseMedicationsDialog(false)}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleAddReusedMedications}
              disabled={selectedReuseMedications.length === 0}
              className="bg-purple-600 hover:bg-purple-700"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add {selectedReuseMedications.length} Medication{selectedReuseMedications.length !== 1 ? 's' : ''}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Temperature Reading Dialog */}
      <Dialog open={showTempDialog} onOpenChange={setShowTempDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Temperature Reading</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddTemperature} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="temperature">Temperature *</Label>
                <Input
                  id="temperature"
                  type="number"
                  step="0.1"
                  placeholder="98.7"
                  value={tempFormData.temperature}
                  onChange={(e) => setTempFormData(prev => ({ ...prev, temperature: e.target.value }))}
                  required
                  className="text-sm md:text-base"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="unit">Unit *</Label>
                <Select onValueChange={(value) => setTempFormData(prev => ({ ...prev, unit: value }))} value={tempFormData.unit}>
                  <SelectTrigger className="text-sm md:text-base">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="C">Celsius (°C)</SelectItem>
                    <SelectItem value="F">Fahrenheit (°F)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="method">Method *</Label>
              <Select onValueChange={(value) => setTempFormData(prev => ({ ...prev, method: value }))} value={tempFormData.method}>
                <SelectTrigger className="text-sm md:text-base">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Oral">Oral</SelectItem>
                  <SelectItem value="Rectal">Rectal</SelectItem>
                  <SelectItem value="Axillary">Axillary (Armpit)</SelectItem>
                  <SelectItem value="Forehead">Forehead</SelectItem>
                  <SelectItem value="Ear">Ear (Tympanic)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="temp_date">Date *</Label>
                <Input
                  id="temp_date"
                  type="text"
                  placeholder="DD/MM/YYYY"
                  value={tempFormData.date_recorded}
                  onChange={handleTempDateInput}
                  onKeyDown={handleTempDateKeyDown}
                  maxLength={10}
                  required
                  className="text-sm md:text-base"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="temp_time">Time *</Label>
                <Input
                  id="temp_time"
                  type="time"
                  value={tempFormData.time_recorded}
                  onChange={(e) => setTempFormData(prev => ({ ...prev, time_recorded: e.target.value }))}
                  required
                  className="text-sm md:text-base"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="temp_notes">Notes (Optional)</Label>
              <Textarea
                id="temp_notes"
                placeholder="Any advice or notes..."
                value={tempFormData.notes}
                onChange={(e) => setTempFormData(prev => ({ ...prev, notes: e.target.value }))}
                rows={3}
                className="text-sm md:text-base"
              />
            </div>
            
            <div className="flex space-x-2 pt-4">
              <Button 
                type="submit" 
                className="flex-1"
              >
                Save Reading
              </Button>
              <Button 
                type="button"
                variant="outline" 
                onClick={() => setShowTempDialog(false)}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Blood Pressure Reading Dialog */}
      <Dialog open={showBpDialog} onOpenChange={setShowBpDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Blood Pressure Reading</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddBloodPressure} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="systolic">Systolic *</Label>
                <Input
                  id="systolic"
                  type="number"
                  placeholder="120"
                  value={bpFormData.systolic}
                  onChange={(e) => setBpFormData(prev => ({ ...prev, systolic: e.target.value }))}
                  required
                  className="text-sm md:text-base"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="diastolic">Diastolic *</Label>
                <Input
                  id="diastolic"
                  type="number"
                  placeholder="80"
                  value={bpFormData.diastolic}
                  onChange={(e) => setBpFormData(prev => ({ ...prev, diastolic: e.target.value }))}
                  required
                  className="text-sm md:text-base"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="pulse">Pulse (Optional)</Label>
              <Input
                id="pulse"
                type="number"
                placeholder="72"
                value={bpFormData.pulse}
                onChange={(e) => setBpFormData(prev => ({ ...prev, pulse: e.target.value }))}
                className="text-sm md:text-base"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="bp_method">Method *</Label>
              <Select onValueChange={(value) => setBpFormData(prev => ({ ...prev, method: value }))} value={bpFormData.method}>
                <SelectTrigger className="text-sm md:text-base">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Manual">Manual</SelectItem>
                  <SelectItem value="Automatic">Automatic</SelectItem>
                  <SelectItem value="Digital">Digital</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="bp_date">Date *</Label>
                <Input
                  id="bp_date"
                  type="text"
                  placeholder="DD/MM/YYYY"
                  value={bpFormData.date_recorded}
                  onChange={handleBpDateInput}
                  onKeyDown={handleBpDateKeyDown}
                  maxLength={10}
                  required
                  className="text-sm md:text-base"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="bp_time">Time *</Label>
                <Input
                  id="bp_time"
                  type="time"
                  value={bpFormData.time_recorded}
                  onChange={(e) => setBpFormData(prev => ({ ...prev, time_recorded: e.target.value }))}
                  required
                  className="text-sm md:text-base"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="bp_notes">Notes (Optional)</Label>
              <Textarea
                id="bp_notes"
                placeholder="Any advice or notes..."
                value={bpFormData.notes}
                onChange={(e) => setBpFormData(prev => ({ ...prev, notes: e.target.value }))}
                rows={3}
                className="text-sm md:text-base"
              />
            </div>
            
            <div className="flex space-x-2 pt-4">
              <Button 
                type="submit" 
                className="flex-1"
              >
                Save Reading
              </Button>
              <Button 
                type="button"
                variant="outline" 
                onClick={() => setShowBpDialog(false)}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Test Selection Dialog */}
      <Dialog open={showTestSelectionDialog} onOpenChange={setShowTestSelectionDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto z-[110]">
          <DialogHeader>
            <DialogTitle>Select Tests</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800">
                Select tests from your templates. Selected tests will be added to the Tests field separated by commas.
              </p>
            </div>

            {testTemplates.length > 0 ? (
              <>
                {/* Search Input */}
                <div className="relative">
                  <Input
                    type="text"
                    placeholder="Search tests..."
                    value={testSearchQuery}
                    onChange={(e) => setTestSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                  <svg 
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>

                {/* Test List - Grouped and Collapsible (All Tests Combined) */}
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {(() => {
                    // Filter tests based on search
                    const filteredTests = testTemplates.filter(test => 
                      test.test_name.toLowerCase().includes(testSearchQuery.toLowerCase()) ||
                      (test.description && test.description.toLowerCase().includes(testSearchQuery.toLowerCase()))
                    );

                    if (testSearchQuery && filteredTests.length === 0) {
                      return (
                        <div className="text-center py-8 text-gray-500">
                          <p>No tests found matching "{testSearchQuery}"</p>
                        </div>
                      );
                    }

                    // Group ALL tests by test type (extracted from description or default)
                    const groupTestsByType = (tests) => {
                      return tests.reduce((acc, test) => {
                        const desc = test.description || '';
                        const testType = desc.split(' - ')[0] || 'My Custom Tests';
                        if (!acc[testType]) acc[testType] = [];
                        acc[testType].push(test);
                        return acc;
                      }, {});
                    };

                    const groupedTests = groupTestsByType(filteredTests);
                    
                    // Always expanded by default (show all tests and categories)
                    const isExpanded = (key) => true;
                    const isCategoryExpanded = (key) => true;

                    return Object.entries(groupedTests).map(([testType, tests]) => {
                      // Check if this is a Blood Test type (has categories)
                      const isBloodTest = testType === 'Blood Test';
                      
                      // For Blood Tests, group by category
                      const groupedByCategory = isBloodTest 
                        ? tests.reduce((acc, test) => {
                            const category = (test.description || '').split(' - ')[1] || 'Uncategorized';
                            if (!acc[category]) acc[category] = [];
                            acc[category].push(test);
                            return acc;
                          }, {})
                        : null;

                      return (
                        <div key={testType} className="border border-gray-200 rounded-lg">
                          {/* Test Type Header */}
                          <button
                            onClick={() => setExpandedTestTypes(prev => ({ ...prev, [testType]: !prev[testType] }))}
                            className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 transition-colors rounded-t"
                          >
                            <div className="flex items-center space-x-2">
                              {isExpanded(testType) ? (
                                <ChevronDown className="w-4 h-4 text-gray-600" />
                              ) : (
                                <ChevronRight className="w-4 h-4 text-gray-600" />
                              )}
                              <span className="font-semibold text-gray-900 text-sm">{testType}</span>
                              <span className="text-xs text-gray-600 bg-white px-2 py-0.5 rounded">{tests.length} tests</span>
                            </div>
                          </button>

                          {/* Test Type Content */}
                          {isExpanded(testType) && (
                            <div className="p-2">
                              {groupedByCategory ? (
                                // Blood Tests grouped by category with category checkboxes
                                Object.entries(groupedByCategory).map(([category, categoryTests]) => {
                                  const allCategorySelected = categoryTests.every(t => selectedTests.includes(t.id));
                                  const someCategorySelected = categoryTests.some(t => selectedTests.includes(t.id)) && !allCategorySelected;
                                  
                                  return (
                                    <div key={category} className="mb-2">
                                      {/* Category Header with Checkbox */}
                                      <div className="flex items-center gap-2">
                                        <input
                                          type="checkbox"
                                          checked={allCategorySelected}
                                          ref={el => {
                                            if (el) el.indeterminate = someCategorySelected;
                                          }}
                                          onChange={() => handleToggleCategory(categoryTests)}
                                          className="w-4 h-4 text-blue-600 rounded"
                                        />
                                        <button
                                          onClick={() => setExpandedCategories(prev => ({ ...prev, [category]: !prev[category] }))}
                                          className="flex-1 flex items-center justify-between p-2 bg-blue-50 hover:bg-blue-100 transition-colors rounded"
                                        >
                                          <div className="flex items-center space-x-2">
                                            {isCategoryExpanded(category) ? (
                                              <ChevronDown className="w-3 h-3 text-blue-600" />
                                            ) : (
                                              <ChevronRight className="w-3 h-3 text-blue-600" />
                                            )}
                                            <span className="text-sm font-medium text-blue-900">{category}</span>
                                            <span className="text-xs text-blue-600 bg-white px-2 py-0.5 rounded">{categoryTests.length}</span>
                                          </div>
                                        </button>
                                      </div>

                                      {/* Category Tests */}
                                      {isCategoryExpanded(category) && (
                                        <div className="mt-1 space-y-1 ml-6">
                                          {categoryTests.map(test => (
                                            <label 
                                              key={test.id}
                                              className="flex items-start p-2 border border-gray-200 rounded hover:bg-gray-50 cursor-pointer transition-colors bg-white"
                                            >
                                              <input
                                                type="checkbox"
                                                checked={selectedTests.includes(test.id)}
                                                onChange={() => handleToggleTest(test.id)}
                                                className="w-4 h-4 text-blue-600 rounded mt-0.5"
                                              />
                                              <div className="ml-2 flex-1">
                                                <p className="text-sm font-medium text-gray-900">{test.test_name}</p>
                                              </div>
                                            </label>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })
                              ) : (
                                // Non-Blood Tests (no categories)
                                <div className="space-y-1">
                                  {tests.map(test => (
                                    <label 
                                      key={test.id}
                                      className="flex items-start p-2 border border-gray-200 rounded hover:bg-gray-50 cursor-pointer transition-colors"
                                    >
                                      <input
                                        type="checkbox"
                                        checked={selectedTests.includes(test.id)}
                                        onChange={() => handleToggleTest(test.id)}
                                        className="w-4 h-4 text-blue-600 rounded mt-0.5"
                                      />
                                      <div className="ml-2 flex-1">
                                        <p className="text-sm font-medium text-gray-900">{test.test_name}</p>
                                        {test.description && (
                                          <p className="text-xs text-gray-600 mt-1">{test.description}</p>
                                        )}
                                      </div>
                                    </label>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    });
                  })()}
                </div>
              </>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <svg className="w-12 h-12 text-gray-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                <p>No test templates available</p>
                <p className="text-sm mt-1">Add test templates in Configuration tab first</p>
              </div>
            )}

            {selectedTests.length > 0 && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <p className="text-sm text-green-800">
                  <strong>{selectedTests.length} test(s) selected:</strong>{' '}
                  {testTemplates
                    .filter(t => selectedTests.includes(t.id))
                    .map(t => t.test_name)
                    .join(', ')}
                </p>
              </div>
            )}

            <div className="flex space-x-2 pt-4 border-t">
              <Button 
                onClick={handleApplySelectedTests}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
              >
                Apply Selected Tests
              </Button>
              <Button 
                variant="outline" 
                onClick={() => setShowTestSelectionDialog(false)}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Level 2 Complaints Dialog */}
      <Dialog open={showSubComplaints} onOpenChange={setShowSubComplaints}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg">Select Level 2 Complaint</DialogTitle>
            <DialogDescription>
              {selectedChiefComplaint && (
                <span className="text-sm">
                  Main Complaint: <span className="font-semibold text-blue-600">{selectedChiefComplaint.complaint_name}</span>
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 mt-4">
            {selectedChiefComplaint && selectedChiefComplaint.sub_complaints && selectedChiefComplaint.sub_complaints.map((subComplaint, index) => {
              const subName = typeof subComplaint === 'string' ? subComplaint : subComplaint.name;
              const subMedCount = typeof subComplaint === 'object' ? (subComplaint.associated_medications || []).length : 0;
              const subSubCount = typeof subComplaint === 'object' ? (subComplaint.sub_sub_complaints || []).length : 0;
              
              return (
                <button
                  key={index}
                  onClick={() => handleSubComplaintSelect(subComplaint)}
                  className="w-full text-left p-3 border border-gray-200 rounded-lg hover:bg-blue-50 hover:border-blue-300 transition-colors"
                >
                  <div className="flex items-start gap-2">
                    <span className="text-blue-600 mt-1">•</span>
                    <div className="flex-1">
                      <span className="text-sm block">{subName}</span>
                      <div className="flex gap-3 mt-1">
                        {subMedCount > 0 && (
                          <span className="text-xs text-gray-500">
                            {subMedCount} medication(s)
                          </span>
                        )}
                        {subSubCount > 0 && (
                          <span className="text-xs text-purple-600 font-medium">
                            {subSubCount} Level 3 option(s) →
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
          <div className="mt-4 pt-4 border-t">
            <button
              onClick={() => {
                // Add main complaint without sub-complaint
                if (selectedChiefComplaint) {
                  const currentComplaints = sessionForm.chief_complaints || '';
                  const newComplaint = selectedChiefComplaint.complaint_name;
                  const updatedComplaints = currentComplaints 
                    ? `${currentComplaints}, ${newComplaint}`
                    : newComplaint;
                  setSessionForm(prev => ({ ...prev, chief_complaints: updatedComplaints }));
                  toast.success('Chief complaint added');
                }
                setShowSubComplaints(false);
                setSelectedChiefComplaint(null);
              }}
              className="w-full text-sm text-gray-600 hover:text-gray-800 underline"
            >
              Or add main complaint only (skip Level 2)
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Level 3 Complaints Dialog */}
      <Dialog open={showSubSubComplaints} onOpenChange={setShowSubSubComplaints}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg">Select Level 3 Complaint</DialogTitle>
            <DialogDescription>
              {selectedChiefComplaint && selectedSubComplaint && (
                <div className="text-sm space-y-1">
                  <div>
                    <span>Main Complaint: </span>
                    <span className="font-semibold text-blue-600">{selectedChiefComplaint.complaint_name}</span>
                  </div>
                  <div>
                    <span>Level 2: </span>
                    <span className="font-semibold text-purple-600">
                      {typeof selectedSubComplaint === 'string' ? selectedSubComplaint : selectedSubComplaint.name}
                    </span>
                  </div>
                </div>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 mt-4">
            {selectedSubComplaint && (() => {
              const subSubComplaints = typeof selectedSubComplaint === 'object' ? selectedSubComplaint.sub_sub_complaints || [] : [];
              
              return subSubComplaints.map((subSubComplaint, index) => {
                const subSubName = typeof subSubComplaint === 'string' ? subSubComplaint : subSubComplaint.name;
                const subSubMedCount = typeof subSubComplaint === 'object' ? (subSubComplaint.associated_medications || []).length : 0;
                
                return (
                  <button
                    key={index}
                    onClick={() => handleSubSubComplaintSelect(subSubComplaint)}
                    className="w-full text-left p-3 border border-gray-200 rounded-lg hover:bg-purple-50 hover:border-purple-300 transition-colors"
                  >
                    <div className="flex items-start gap-2">
                      <span className="text-purple-600 mt-1">•</span>
                      <div className="flex-1">
                        <span className="text-sm block">{subSubName}</span>
                        {subSubMedCount > 0 && (
                          <span className="text-xs text-gray-500 mt-1 block">
                            {subSubMedCount} medication(s) will be added
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                );
              });
            })()}
          </div>
          <div className="mt-4 pt-4 border-t">
            <button
              onClick={() => {
                // Add chief + sub complaint without sub-sub complaint
                if (selectedChiefComplaint && selectedSubComplaint) {
                  const currentComplaints = sessionForm.chief_complaints || '';
                  const subComplaintName = typeof selectedSubComplaint === 'string' ? selectedSubComplaint : selectedSubComplaint.name;
                  const fullComplaint = `${selectedChiefComplaint.complaint_name}: ${subComplaintName}`;
                  const updatedComplaints = currentComplaints 
                    ? `${currentComplaints}, ${fullComplaint}`
                    : fullComplaint;
                  setSessionForm(prev => ({ ...prev, chief_complaints: updatedComplaints }));
                  toast.success('Chief complaint with sub-complaint added');
                }
                setShowSubSubComplaints(false);
                setShowSubComplaints(false);
                setSelectedSubComplaint(null);
                setSelectedChiefComplaint(null);
              }}
              className="w-full text-sm text-gray-600 hover:text-gray-800 underline"
            >
              Or add without Level 3
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Level 4 Complaints Dialog */}
      <Dialog open={showLevel4Complaints} onOpenChange={setShowLevel4Complaints}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg">Select Level 4 Complaint</DialogTitle>
            <DialogDescription>
              {selectedChiefComplaint && selectedSubComplaint && selectedSubSubComplaint && (
                <div className="text-sm space-y-1">
                  <div><span>Chief: </span><span className="font-semibold text-blue-600">{selectedChiefComplaint.complaint_name}</span></div>
                  <div><span>Level 2: </span><span className="font-semibold text-purple-600">{typeof selectedSubComplaint === 'string' ? selectedSubComplaint : selectedSubComplaint.name}</span></div>
                  <div><span>Level 3: </span><span className="font-semibold text-purple-600">{typeof selectedSubSubComplaint === 'string' ? selectedSubSubComplaint : selectedSubSubComplaint.name}</span></div>
                </div>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 mt-4">
            {selectedSubSubComplaint && (() => {
              const level4Complaints = typeof selectedSubSubComplaint === 'object' ? selectedSubSubComplaint.level_4_complaints || [] : [];
              
              return level4Complaints.map((level4Complaint, index) => {
                const level4Name = typeof level4Complaint === 'string' ? level4Complaint : level4Complaint.name;
                const level4MedCount = typeof level4Complaint === 'object' ? (level4Complaint.associated_medications || []).length : 0;
                const level5Count = typeof level4Complaint === 'object' ? (level4Complaint.level_5_complaints || []).length : 0;
                
                return (
                  <button
                    key={index}
                    onClick={() => handleLevel4ComplaintSelect(level4Complaint)}
                    className="w-full text-left p-3 border border-gray-200 rounded-lg hover:bg-orange-50 hover:border-orange-300 transition-colors"
                  >
                    <div className="flex items-start gap-2">
                      <span className="text-orange-600 mt-1">•</span>
                      <div className="flex-1">
                        <span className="text-sm block">{level4Name}</span>
                        <div className="flex gap-3 mt-1">
                          {level4MedCount > 0 && <span className="text-xs text-gray-500">{level4MedCount} medication(s)</span>}
                          {level5Count > 0 && <span className="text-xs text-teal-600 font-medium">{level5Count} Level 5 option(s) →</span>}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              });
            })()}
          </div>
          <div className="mt-4 pt-4 border-t">
            <button
              onClick={() => {
                if (selectedChiefComplaint && selectedSubComplaint && selectedSubSubComplaint) {
                  const subComplaintName = typeof selectedSubComplaint === 'string' ? selectedSubComplaint : selectedSubComplaint.name;
                  const subSubComplaintName = typeof selectedSubSubComplaint === 'string' ? selectedSubSubComplaint : selectedSubSubComplaint.name;
                  const fullComplaint = `${selectedChiefComplaint.complaint_name}: ${subComplaintName} - ${subSubComplaintName}`;
                  const currentComplaints = sessionForm.chief_complaints || '';
                  const updatedComplaints = currentComplaints ? `${currentComplaints}, ${fullComplaint}` : fullComplaint;
                  setSessionForm(prev => ({ ...prev, chief_complaints: updatedComplaints }));
                  toast.success('Chief complaint added');
                }
                setShowLevel4Complaints(false);
                setShowSubSubComplaints(false);
                setShowSubComplaints(false);
                setSelectedSubSubComplaint(null);
                setSelectedSubComplaint(null);
                setSelectedChiefComplaint(null);
              }}
              className="w-full text-sm text-gray-600 hover:text-gray-800 underline"
            >
              Or add without Level 4
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Level 5 Complaints Dialog */}
      <Dialog open={showLevel5Complaints} onOpenChange={setShowLevel5Complaints}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg">Select Level 5 Complaint</DialogTitle>
            <DialogDescription>
              {selectedChiefComplaint && selectedSubComplaint && selectedSubSubComplaint && selectedLevel4Complaint && (
                <div className="text-sm space-y-1">
                  <div><span>Chief: </span><span className="font-semibold">{selectedChiefComplaint.complaint_name}</span></div>
                  <div><span>Level 4: </span><span className="font-semibold text-orange-600">{typeof selectedLevel4Complaint === 'string' ? selectedLevel4Complaint : selectedLevel4Complaint.name}</span></div>
                </div>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 mt-4">
            {selectedLevel4Complaint && (() => {
              const level5Complaints = typeof selectedLevel4Complaint === 'object' ? selectedLevel4Complaint.level_5_complaints || [] : [];
              
              return level5Complaints.map((level5Complaint, index) => {
                const level5Name = typeof level5Complaint === 'string' ? level5Complaint : level5Complaint.name;
                const level5MedCount = typeof level5Complaint === 'object' ? (level5Complaint.associated_medications || []).length : 0;
                const level6Count = typeof level5Complaint === 'object' ? (level5Complaint.level_6_complaints || []).length : 0;
                
                return (
                  <button
                    key={index}
                    onClick={() => handleLevel5ComplaintSelect(level5Complaint)}
                    className="w-full text-left p-3 border border-gray-200 rounded-lg hover:bg-teal-50 hover:border-teal-300 transition-colors"
                  >
                    <div className="flex items-start gap-2">
                      <span className="text-teal-600 mt-1">•</span>
                      <div className="flex-1">
                        <span className="text-sm block">{level5Name}</span>
                        <div className="flex gap-3 mt-1">
                          {level5MedCount > 0 && <span className="text-xs text-gray-500">{level5MedCount} medication(s)</span>}
                          {level6Count > 0 && <span className="text-xs text-pink-600 font-medium">{level6Count} Level 6 option(s) →</span>}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              });
            })()}
          </div>
          <div className="mt-4 pt-4 border-t">
            <button
              onClick={() => {
                if (selectedChiefComplaint && selectedSubComplaint && selectedSubSubComplaint && selectedLevel4Complaint) {
                  const subComplaintName = typeof selectedSubComplaint === 'string' ? selectedSubComplaint : selectedSubComplaint.name;
                  const subSubComplaintName = typeof selectedSubSubComplaint === 'string' ? selectedSubSubComplaint : selectedSubSubComplaint.name;
                  const level4ComplaintName = typeof selectedLevel4Complaint === 'string' ? selectedLevel4Complaint : selectedLevel4Complaint.name;
                  const fullComplaint = `${selectedChiefComplaint.complaint_name}: ${subComplaintName} - ${subSubComplaintName} - ${level4ComplaintName}`;
                  const currentComplaints = sessionForm.chief_complaints || '';
                  const updatedComplaints = currentComplaints ? `${currentComplaints}, ${fullComplaint}` : fullComplaint;
                  setSessionForm(prev => ({ ...prev, chief_complaints: updatedComplaints }));
                  toast.success('Chief complaint added');
                }
                setShowLevel5Complaints(false);
                setShowLevel4Complaints(false);
                setShowSubSubComplaints(false);
                setShowSubComplaints(false);
                setSelectedLevel4Complaint(null);
                setSelectedSubSubComplaint(null);
                setSelectedSubComplaint(null);
                setSelectedChiefComplaint(null);
              }}
              className="w-full text-sm text-gray-600 hover:text-gray-800 underline"
            >
              Or add without Level 5
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Level 6 Complaints Dialog */}
      <Dialog open={showLevel6Complaints} onOpenChange={setShowLevel6Complaints}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg">Select Level 6 Complaint</DialogTitle>
            <DialogDescription>
              {selectedChiefComplaint && selectedLevel4Complaint && selectedLevel5Complaint && (
                <div className="text-sm space-y-1">
                  <div><span>Chief: </span><span className="font-semibold">{selectedChiefComplaint.complaint_name}</span></div>
                  <div><span>Level 5: </span><span className="font-semibold text-teal-600">{typeof selectedLevel5Complaint === 'string' ? selectedLevel5Complaint : selectedLevel5Complaint.name}</span></div>
                </div>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 mt-4">
            {selectedLevel5Complaint && (() => {
              const level6Complaints = typeof selectedLevel5Complaint === 'object' ? selectedLevel5Complaint.level_6_complaints || [] : [];
              
              return level6Complaints.map((level6Complaint, index) => {
                const level6Name = typeof level6Complaint === 'string' ? level6Complaint : level6Complaint.name;
                const level6MedCount = typeof level6Complaint === 'object' ? (level6Complaint.associated_medications || []).length : 0;
                
                return (
                  <button
                    key={index}
                    onClick={() => handleLevel6ComplaintSelect(level6Complaint)}
                    className="w-full text-left p-3 border border-gray-200 rounded-lg hover:bg-pink-50 hover:border-pink-300 transition-colors"
                  >
                    <div className="flex items-start gap-2">
                      <span className="text-pink-600 mt-1">•</span>
                      <div className="flex-1">
                        <span className="text-sm block">{level6Name}</span>
                        {level6MedCount > 0 && (
                          <span className="text-xs text-gray-500 mt-1 block">{level6MedCount} medication(s) will be added</span>
                        )}
                      </div>
                    </div>
                  </button>
                );
              });
            })()}
          </div>
          <div className="mt-4 pt-4 border-t">
            <button
              onClick={() => {
                if (selectedChiefComplaint && selectedSubComplaint && selectedSubSubComplaint && selectedLevel4Complaint && selectedLevel5Complaint) {
                  const subComplaintName = typeof selectedSubComplaint === 'string' ? selectedSubComplaint : selectedSubComplaint.name;
                  const subSubComplaintName = typeof selectedSubSubComplaint === 'string' ? selectedSubSubComplaint : selectedSubSubComplaint.name;
                  const level4ComplaintName = typeof selectedLevel4Complaint === 'string' ? selectedLevel4Complaint : selectedLevel4Complaint.name;
                  const level5ComplaintName = typeof selectedLevel5Complaint === 'string' ? selectedLevel5Complaint : selectedLevel5Complaint.name;
                  const fullComplaint = `${selectedChiefComplaint.complaint_name}: ${subComplaintName} - ${subSubComplaintName} - ${level4ComplaintName} - ${level5ComplaintName}`;
                  const currentComplaints = sessionForm.chief_complaints || '';
                  const updatedComplaints = currentComplaints ? `${currentComplaints}, ${fullComplaint}` : fullComplaint;
                  setSessionForm(prev => ({ ...prev, chief_complaints: updatedComplaints }));
                  toast.success('Chief complaint added');
                }
                setShowLevel6Complaints(false);
                setShowLevel5Complaints(false);
                setShowLevel4Complaints(false);
                setShowSubSubComplaints(false);
                setShowSubComplaints(false);
                setSelectedLevel5Complaint(null);
                setSelectedLevel4Complaint(null);
                setSelectedSubSubComplaint(null);
                setSelectedSubComplaint(null);
                setSelectedChiefComplaint(null);
              }}
              className="w-full text-sm text-gray-600 hover:text-gray-800 underline"
            >
              Or add without Level 6
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Level 2 Diagnosis Selection Dialog */}
      <Dialog open={showLevel2Diagnosis} onOpenChange={setShowLevel2Diagnosis}>
        <DialogContent className="max-w-md max-h-[600px] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Select Level 2 Diagnosis</DialogTitle>
            <DialogDescription>
              {selectedDiagnosis && (
                <div>
                  Main Diagnosis: <span className="font-semibold text-blue-600">{selectedDiagnosis.diagnosis_name}</span>
                </div>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-2">
            {selectedDiagnosis && selectedDiagnosis.level_2_diagnosis && selectedDiagnosis.level_2_diagnosis.map((level2, index) => {
              const level2Name = typeof level2 === 'string' ? level2 : level2.name;
              const level3List = typeof level2 === 'object' ? level2.level_3_diagnosis || [] : [];
              
              return (
                <button
                  key={index}
                  onClick={() => handleAddLevel2Diagnosis(level2)}
                  className="w-full text-left p-3 border rounded hover:bg-orange-50 hover:border-orange-300 transition-colors"
                >
                  <div className="font-medium">{level2Name}</div>
                  {level3List.length > 0 && (
                    <div className="text-xs text-gray-500 mt-1">
                      {level3List.length} Level 3 option{level3List.length !== 1 ? 's' : ''} available
                    </div>
                  )}
                </button>
              );
            })}
          </div>
          
          <div className="pt-4 border-t">
            <button
              onClick={() => {
                if (selectedDiagnosis) {
                  // Add without Level 2
                  addDiagnosisToSession(selectedDiagnosis.diagnosis_name);
                  
                  // Auto-fill associated notes
                  if (selectedDiagnosis.associated_notes && selectedDiagnosis.associated_notes.length > 0) {
                    autoFillDiagnosisNotes(selectedDiagnosis.associated_notes);
                  }
                  
                  setShowLevel2Diagnosis(false);
                  setSelectedDiagnosis(null);
                }
              }}
              className="w-full text-sm text-gray-600 hover:text-gray-800 underline"
            >
              Or add without Level 2
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Level 3 Diagnosis Selection Dialog */}
      <Dialog open={showLevel3Diagnosis} onOpenChange={setShowLevel3Diagnosis}>
        <DialogContent className="max-w-md max-h-[600px] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Select Level 3 Diagnosis</DialogTitle>
            <DialogDescription>
              {selectedDiagnosis && selectedLevel2Diagnosis && (
                <div className="text-sm space-y-1">
                  <div>
                    <span>Main Diagnosis: </span>
                    <span className="font-semibold text-blue-600">{selectedDiagnosis.diagnosis_name}</span>
                  </div>
                  <div>
                    <span>Level 2: </span>
                    <span className="font-semibold text-purple-600">
                      {typeof selectedLevel2Diagnosis === 'string' ? selectedLevel2Diagnosis : selectedLevel2Diagnosis.name}
                    </span>
                  </div>
                </div>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-2">
            {selectedLevel2Diagnosis && (() => {
              const level3List = typeof selectedLevel2Diagnosis === 'object' ? selectedLevel2Diagnosis.level_3_diagnosis || [] : [];
              
              return level3List.map((level3, index) => {
                const level3Name = typeof level3 === 'string' ? level3 : level3.name;
                
                return (
                  <button
                    key={index}
                    onClick={() => handleAddLevel3Diagnosis(level3)}
                    className="w-full text-left p-3 border rounded hover:bg-purple-50 hover:border-purple-300 transition-colors"
                  >
                    <div className="font-medium">{level3Name}</div>
                  </button>
                );
              });
            })()}
          </div>
          
          <div className="pt-4 border-t">
            <button
              onClick={() => {
                if (selectedDiagnosis && selectedLevel2Diagnosis) {
                  // Add without Level 3
                  const level2Name = typeof selectedLevel2Diagnosis === 'string' ? selectedLevel2Diagnosis : selectedLevel2Diagnosis.name;
                  const fullDiagnosis = `${selectedDiagnosis.diagnosis_name}: ${level2Name}`;
                  addDiagnosisToSession(fullDiagnosis);
                  
                  // Auto-fill ONLY Level 2 advice
                  const level2Notes = typeof selectedLevel2Diagnosis === 'object' ? selectedLevel2Diagnosis.associated_notes : [];
                  if (level2Notes && level2Notes.length > 0) {
                    autoFillDiagnosisNotes(level2Notes);
                  }
                  
                  setShowLevel3Diagnosis(false);
                  setShowLevel2Diagnosis(false);
                  setSelectedDiagnosis(null);
                  setSelectedLevel2Diagnosis(null);
                }
              }}
              className="w-full text-sm text-gray-600 hover:text-gray-800 underline"
            >
              Or add without Level 3
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* SOAP Notes Dialog - Editable with Natural Format */}
      <Dialog open={showSOAPDialog} onOpenChange={setShowSOAPDialog}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-purple-600" />
                <span>SOAP Notes</span>
              </div>
              {viewingSOAPNotes && (
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => {
                    // Copy SOAP notes to clipboard
                    const text = `SUBJECTIVE\n${viewingSOAPNotes.subjective || ''}\n\nOBJECTIVE\n${viewingSOAPNotes.objective || ''}\n\nASSESSMENT\n${viewingSOAPNotes.assessment || ''}\n\nPLAN\n${viewingSOAPNotes.plan || ''}`;
                    navigator.clipboard.writeText(text);
                    toast.success('SOAP notes copied to clipboard');
                  }}
                  className="text-xs mr-8"
                >
                  <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  Copy
                </Button>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {viewingSOAPNotes ? (
            <div className="space-y-4 mt-2 font-mono text-sm">
              {/* SUBJECTIVE */}
              <div className="border-l-4 border-purple-500 pl-4">
                <h4 className="font-bold text-purple-700 text-base mb-2">SUBJECTIVE</h4>
                <Textarea
                  value={viewingSOAPNotes.subjective || ''}
                  onChange={(e) => setViewingSOAPNotes(prev => ({...prev, subjective: e.target.value}))}
                  className="min-h-[80px] text-gray-800 bg-purple-50/50 border-purple-200 focus:border-purple-400"
                  placeholder="• Chief complaint – duration&#10;• Associated symptoms"
                />
              </div>
              
              {/* OBJECTIVE */}
              <div className="border-l-4 border-blue-500 pl-4">
                <h4 className="font-bold text-blue-700 text-base mb-2">OBJECTIVE</h4>
                <Textarea
                  value={viewingSOAPNotes.objective || ''}
                  onChange={(e) => setViewingSOAPNotes(prev => ({...prev, objective: e.target.value}))}
                  className="min-h-[80px] text-gray-800 bg-blue-50/50 border-blue-200 focus:border-blue-400"
                  placeholder="• Vitals&#10;• Physical findings"
                />
              </div>
              
              {/* ASSESSMENT */}
              <div className="border-l-4 border-green-500 pl-4">
                <h4 className="font-bold text-green-700 text-base mb-2">ASSESSMENT</h4>
                <Textarea
                  value={viewingSOAPNotes.assessment || ''}
                  onChange={(e) => setViewingSOAPNotes(prev => ({...prev, assessment: e.target.value}))}
                  className="min-h-[60px] text-gray-800 bg-green-50/50 border-green-200 focus:border-green-400"
                  placeholder="• Diagnosis/impression"
                />
              </div>
              
              {/* PLAN */}
              <div className="border-l-4 border-amber-500 pl-4">
                <h4 className="font-bold text-amber-700 text-base mb-2">PLAN</h4>
                <Textarea
                  value={viewingSOAPNotes.plan || ''}
                  onChange={(e) => setViewingSOAPNotes(prev => ({...prev, plan: e.target.value}))}
                  className="min-h-[80px] text-gray-800 bg-amber-50/50 border-amber-200 focus:border-amber-400"
                  placeholder="• Medications&#10;• Investigations&#10;• Follow-up"
                />
              </div>
              
              <div className="flex items-center justify-between pt-2 border-t">
                <p className="text-xs text-gray-500 italic">
                  Edit inline • Changes auto-saved when you close
                </p>
                <Button 
                  size="sm"
                  onClick={async () => {
                    // Save updated SOAP notes to session
                    if (editingSession) {
                      try {
                        await axios.put(`${API}/api/medical-sessions/${editingSession.id}`, {
                          ...sessionForm,
                          ai_soap_notes: viewingSOAPNotes
                        });
                        setSessionForm(prev => ({...prev, ai_soap_notes: viewingSOAPNotes}));
                        toast.success('SOAP notes saved');
                        setShowSOAPDialog(false);
                      } catch (error) {
                        toast.error('Failed to save SOAP notes');
                      }
                    } else {
                      setSessionForm(prev => ({...prev, ai_soap_notes: viewingSOAPNotes}));
                      toast.success('SOAP notes updated');
                      setShowSOAPDialog(false);
                    }
                  }}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  <Check className="w-4 h-4 mr-1" />
                  Save Changes
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No SOAP notes available</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default DoctorDashboard;
